﻿import React, { Component, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { gsap } from "gsap";
import * as THREE from "three";
import "./styles.css";

const runtimeConfig = globalThis.__PUBOBOT_CONFIG__ || {};
const siteBaseUrl = (runtimeConfig.publicSiteUrl || window.location.origin || "https://aoebd.tanvirxen.com").replace(/\/+$/, "");
const publicUrl = (pathname = "/") => `${siteBaseUrl}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
const supabase = runtimeConfig.supabaseUrl && runtimeConfig.supabaseAnonKey
  ? createClient(runtimeConfig.supabaseUrl, runtimeConfig.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null;

const nullableString = z.preprocess(
  (value) => (value === null || value === undefined ? null : String(value)),
  z.string().nullable(),
);
const blankableString = z.preprocess(
  (value) => (value === null || value === undefined ? "" : String(value)),
  z.string(),
);
const nullableNumber = z.preprocess(
  (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  },
  z.number().nullable(),
);

const AnalyticsStatSchema = z.object({
  matches: nullableNumber.optional(),
  wins: nullableNumber.optional(),
  losses: nullableNumber.optional(),
  draws: nullableNumber.optional(),
  win_rate: nullableNumber.optional(),
});

const AnalyticsEntitySchema = z.object({
  key: nullableString.optional(),
  name: nullableString.optional(),
  profile_id: nullableString.optional(),
  matches: nullableNumber.optional(),
  wins: nullableNumber.optional(),
  losses: nullableNumber.optional(),
  draws: nullableNumber.optional(),
  win_rate: nullableNumber.optional(),
});

const PlayerAnalyticsSchema = z.object({
  profile_id: nullableString.optional(),
  player_name: nullableString.optional(),
  last_synced_at: nullableNumber.optional(),
  current_1v1_rating: nullableNumber.optional(),
  current_1v1_name: nullableString.optional(),
  highest_1v1_rating: nullableNumber.optional(),
  highest_1v1_name: nullableString.optional(),
  current_team_rating: nullableNumber.optional(),
  current_team_name: nullableString.optional(),
  highest_team_rating: nullableNumber.optional(),
  highest_team_name: nullableString.optional(),
  overall: AnalyticsStatSchema.nullable().optional(),
  same_team: AnalyticsStatSchema.nullable().optional(),
  avg_eapm: nullableNumber.optional(),
  peak_eapm: nullableNumber.optional(),
  avg_eapm_early: nullableNumber.optional(),
  avg_eapm_mid: nullableNumber.optional(),
  avg_eapm_late: nullableNumber.optional(),
  eapm_trend: nullableNumber.optional(),
  peak_eapm_minute: nullableNumber.optional(),
  peak_eapm_bucket: nullableNumber.optional(),
  economy_score: nullableNumber.optional(),
  food_collected: nullableNumber.optional(),
  wood_collected: nullableNumber.optional(),
  stone_collected: nullableNumber.optional(),
  gold_collected: nullableNumber.optional(),
  tribute_sent: nullableNumber.optional(),
  tribute_received: nullableNumber.optional(),
  trade_gold: nullableNumber.optional(),
  relic_gold: nullableNumber.optional(),
  total_resources_collected: nullableNumber.optional(),
  resource_bank_avg: nullableNumber.optional(),
  resource_bank_peak: nullableNumber.optional(),
  resource_bank_min: nullableNumber.optional(),
  gather_rate_per_min: nullableNumber.optional(),
  eco_score_per_min: nullableNumber.optional(),
  tc_idle_estimate_s: nullableNumber.optional(),
  tc_idle_ratio: nullableNumber.optional(),
  villager_efficiency: nullableNumber.optional(),
  opening_strategy: nullableString.optional(),
  strategy_tags: z.any().optional(),
  best_civ: AnalyticsEntitySchema.nullable().optional(),
  worst_civ: AnalyticsEntitySchema.nullable().optional(),
  best_map: AnalyticsEntitySchema.nullable().optional(),
  worst_map: AnalyticsEntitySchema.nullable().optional(),
  best_teammate: AnalyticsEntitySchema.nullable().optional(),
  worst_teammate: AnalyticsEntitySchema.nullable().optional(),
  best_opponent: AnalyticsEntitySchema.nullable().optional(),
  worst_opponent: AnalyticsEntitySchema.nullable().optional(),
});

const StatRowSchema = z.object({
  key: nullableString.optional(),
  profile_id: nullableString.optional(),
  name: nullableString.optional(),
  matches: nullableNumber.optional(),
  wins: nullableNumber.optional(),
  losses: nullableNumber.optional(),
  draws: nullableNumber.optional(),
  win_rate: nullableNumber.optional(),
  last_match_at: nullableNumber.optional(),
});

const MatchPlayerSchema = z.object({
  name: nullableString.optional(),
  rating: nullableNumber.optional(),
  rating_diff: nullableNumber.optional(),
  won: z.boolean().nullable().optional(),
  civ_name: nullableString.optional(),
  civ_image_url: nullableString.optional(),
});

const RecentMatchSchema = z.object({
  source: nullableString.optional(),
  match_id: z.union([z.string(), z.number()]).transform((v) => String(v)),
  at: nullableNumber.optional(),
  duration_seconds: nullableNumber.optional(),
  map_name: nullableString.optional(),
  map_image_url: nullableString.optional(),
  mode_label: nullableString.optional(),
  won: z.boolean().nullable().optional(),
  teams: z.array(z.array(MatchPlayerSchema)).optional().default([]),
});

const PlayerRatingHistorySchema = z.object({
  id: z.union([z.string(), z.number()]).transform((v) => String(v)),
  at: nullableNumber.optional(),
  rating_before: nullableNumber.optional(),
  rating_change: nullableNumber.optional(),
  rating_after: nullableNumber.optional(),
  match_id: nullableNumber.optional(),
  reason: nullableString.optional(),
});

const PlayerSchema = z.object({
  user_id: nullableString,
  channel_id: nullableString.optional(),
  nick: blankableString,
  game_id: blankableString,
  rating: nullableNumber.optional(),
  deviation: nullableNumber.optional(),
  wins: nullableNumber.optional(),
  losses: nullableNumber.optional(),
  draws: nullableNumber.optional(),
  streak: nullableNumber.optional(),
  is_hidden: z.boolean().optional().default(false),
  last_ranked_match_at: nullableNumber.optional(),
  highest_server_team_rating_override: nullableNumber.optional(),
  analytics: PlayerAnalyticsSchema.nullable().optional(),
  civs: z.array(StatRowSchema).optional().default([]),
  maps: z.array(StatRowSchema).optional().default([]),
  teammates: z.array(StatRowSchema).optional().default([]),
  opponents: z.array(StatRowSchema).optional().default([]),
  recent_matches: z.array(RecentMatchSchema).optional().default([]),
  rating_history: z.array(PlayerRatingHistorySchema).optional().default([]),
  rank_str: nullableString.optional(),
  is_linked: z.boolean().optional().default(false),
  discord_avatar_url: nullableString.optional(),
  discord_display_name: nullableString.optional(),
  highest_server_team_rating: nullableNumber.optional(),
  server_team_matches: nullableNumber.optional(),
}).passthrough();

const BootstrapSchema = z.object({
  user: z.object({
    id: z.union([z.string(), z.number()]).transform((v) => String(v)),
    username: z.string(),
    discriminator: z.string().optional().default("0"),
    avatar: z.string().nullable().optional(),
    email: z.string().email().optional(),
  }),
  root_url: z.string().optional().default(""),
  schemas: z.object({
    queue_channel: z.any(),
    pickup_queue: z.any(),
    tournament_channel: z.any().optional(),
  }),
  guilds: z.array(z.object({
    id: z.union([z.string(), z.number()]).transform((v) => String(v)),
    name: z.string(),
    icon_url: z.string().optional().default(""),
    editable: z.boolean().optional().default(true),
    text_channels: z.array(z.object({
      id: z.union([z.string(), z.number()]).transform((v) => String(v)),
      name: z.string(),
      category: z.string().nullable().optional(),
    })).optional().default([]),
    roles: z.array(z.object({
      id: z.union([z.string(), z.number()]).transform((v) => String(v)),
      name: z.string(),
    })).optional().default([]),
  })),
  queue_channels: z.array(z.any()),
  tournament_channels: z.array(z.any()).optional().default([]),
  selected_channel_id: z.union([z.string(), z.number()]).nullable().optional(),
  selected_queue_id: z.union([z.string(), z.number()]).nullable().optional(),
});

const Api = {
  accessToken: null,
  onUnauthorized: null,
  setAccessToken(token) {
    this.accessToken = token || null;
  },
  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  },
  async get(path) {
    return this.request(path, { method: "GET" });
  },
  async send(path, method, body) {
    return this.request(path, { method, body });
  },
  async request(path, { method = "GET", body } = {}) {
    const headers = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const res = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401) {
      await this.onUnauthorized?.();
      throw new Error("Not authenticated");
    }
    if (!res.ok) throw new Error(await res.text());
    if (res.status === 204) return null;
    return res.json();
  },
};

// --- Minimal URL router ------------------------------------------------
// No router dependency: webui/server.js already serves dist/index.html for any
// unmatched path (SPA fallback), so real pushState-based routes work with zero
// backend changes. pushState doesn't fire "popstate" on its own, so navigate()
// notifies subscribers directly through the same store popstate itself updates.
const routeStore = {
  listeners: new Set(),
  get: () => window.location.pathname + window.location.search,
  subscribe(cb) {
    routeStore.listeners.add(cb);
    return () => routeStore.listeners.delete(cb);
  },
  emit() {
    routeStore.listeners.forEach((cb) => cb());
  },
};
window.addEventListener("popstate", () => routeStore.emit());

function navigate(path, { replace = false } = {}) {
  if (path === routeStore.get()) return;
  if (replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  routeStore.emit();
}

function parseRoute(raw) {
  const [pathname, search] = raw.split("?");
  const query = new URLSearchParams(search || "");
  const parts = pathname.split("/").filter(Boolean);
  // /leaderboard[/player/:userId] - public, unauthenticated, single fixed channel
  // (WEBUI_PUBLIC_CHANNEL_ID server-side) meant to be linked publicly.
  if (parts[0] === "leaderboard") {
    if (parts[1] === "player" && parts[2]) {
      return { name: "public-player", userId: parts[2], query };
    }
    return { name: "public-leaderboard", query };
  }
  if (parts[0] === "match" && parts[1] && parts[2]) {
    return { name: "public-match", source: parts[1], matchId: parts[2], query };
  }
  if (parts[0] === "tournaments") {
    if (parts[1] === "teams" && parts[2]) {
      return { name: "public-tournament-team", slug: "", teamId: parts[2], query };
    }
    if (parts[2] === "teams" && parts[3]) {
      return { name: "public-tournament-team", slug: decodeURIComponent(parts[1]), teamId: parts[3], query };
    }
    if (parts[1]) return { name: "public-tournament", slug: decodeURIComponent(parts.slice(1).join("/")), query };
    return { name: "public-tournaments", query };
  }
  if (parts[0] === "login") {
    return { name: "login", query };
  }
  if (parts[0] === "auth" && parts[1] === "callback") {
    return { name: "auth-callback", query };
  }
  if (parts[0] === "member") {
    if (parts[1] === "login") return { name: "member-login", query };
    if (parts[1] === "tournaments" && parts[2]) return { name: "member-tournament", tournamentId: parts[2], query };
    if (parts[1] === "tournaments") return { name: "member-tournaments", query };
    if (parts[1] === "predictions" && parts[2]) return { name: "member-predictions", tournamentId: parts[2], query };
    if (parts[1] === "predictions") return { name: "member-predictions", tournamentId: null, query };
    if (parts[1] === "map-vote") return { name: "member-map-vote", queueId: parts[2] || null, query };
    if (parts[1] === "profile") return { name: "member-profile", query };
    if (parts[1] === "players") return { name: "member-players", query };
    return { name: "member-home", query };
  }
  if (parts[0] === "admin" && parts[1] === "map-votes") {
    return { name: "admin-map-votes", query };
  }
  // /join/:lobbyid - hands off to the aoe2de:// custom protocol handler. Exists only because
  // Discord's link buttons reject non-http(s) URL schemes outright (error code 50035), so
  // bot/match/lobby.py and the /lobby command point their "Join"/"Spectate" buttons at this
  // https page instead of aoe2de:// directly.
  if (parts[0] === "join" && parts[1]) {
    return { name: "join-lobby", lobbyid: parts[1] };
  }
  // /c/:channelId[/players/:userId | /leaderboard | /matches | /q/:queueId]
  if (parts[0] === "c" && parts[1]) {
    const channelId = parts[1];
    if (parts[2] === "players" && parts[3]) {
      return { name: "player", channelId, userId: parts[3], query };
    }
    if (parts[2] === "leaderboard") {
      return { name: "leaderboard", channelId, query };
    }
    if (parts[2] === "matches") {
      return { name: "matches", channelId, query };
    }
    if (parts[2] === "commands") {
      return { name: "commands", channelId, query };
    }
    if (parts[2] === "q" && parts[3]) {
      return { name: "config", channelId, queueId: parts[3], query };
    }
    return { name: "config", channelId, queueId: null, query };
  }
  if (parts[0] === "t" && parts[1]) {
    return { name: "tournament-config", channelId: parts[1], query };
  }
  return { name: "landing", query };
}

function useRoute() {
  const raw = useSyncExternalStore(routeStore.subscribe, routeStore.get);
  return useMemo(() => parseRoute(raw), [raw]);
}

function withQuery(path, params) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ).toString();
  return qs ? `${path}?${qs}` : path;
}

const sectionsByKind = {
  queue_channel: ["General", "Auto-remove", "Rating", "Leaderboard"],
  pickup_queue: ["General", "Teams", "Appearance", "Maps"],
  tournament_channel: ["General", "Schedule", "Eligibility", "Roles"],
};

const sectionIcons = {
  General: "\u2699",
  Schedule: "\uD83D\uDCC5",
  Teams: "\uD83D\uDC65",
  Appearance: "\uD83C\uDFA8",
  Maps: "\uD83D\uDDFA",
  "Auto-remove": "\uD83E\uDDF9",
  Rating: "\uD83D\uDCC8",
  Leaderboard: "\uD83C\uDFC6",
};

const COMMAND_DOC_SECTIONS = [
  {
    title: "Queue channel slash commands",
    scope: "Enabled queue channel",
    commands: [
      { name: "/add", params: ["queues?"], does: "Add yourself to one or more queues.", allowed: "Any enabled queue channel." },
      { name: "/remove", params: ["queues?"], does: "Remove yourself from one or more queues.", allowed: "Any enabled queue channel." },
      { name: "/who", params: ["queues?"], does: "List players currently added to queues.", allowed: "Any enabled queue channel." },
      { name: "/promote", params: ["queue?"], does: "Promote a queue in Discord.", allowed: "Any enabled queue channel." },
      { name: "/subscribe", params: ["queues"], does: "Subscribe to queue promotion notifications.", allowed: "Any enabled queue channel." },
      { name: "/unsubscribe", params: ["queues"], does: "Unsubscribe from queue promotion notifications.", allowed: "Any enabled queue channel." },
      { name: "/server", params: ["queue"], does: "Show the server for a queue.", allowed: "Any enabled queue channel." },
      { name: "/maps", params: ["queue"], does: "List the maps configured for a queue.", allowed: "Any enabled queue channel." },
      { name: "/map", params: ["queue"], does: "Show one random map from the queue map pool.", allowed: "Any enabled queue channel." },
      { name: "/cooldown", params: ["queue"], does: "Show maps currently on cooldown for a queue.", allowed: "Any enabled queue channel." },
      { name: "/matches", params: [], does: "Show active matches on the current queue channel.", allowed: "Any enabled queue channel." },
      { name: "/teams", params: [], does: "Show teams on the current active match.", allowed: "Any enabled queue channel." },
      { name: "/ready", params: [], does: "Confirm participation during check-in.", allowed: "Any enabled queue channel." },
      { name: "/notready", params: [], does: "Abort participation during check-in.", allowed: "Any enabled queue channel." },
      { name: "/subme", params: [], does: "Request a substitute.", allowed: "Any enabled queue channel." },
      { name: "/subfor", params: ["player"], does: "Volunteer as a substitute for a player.", allowed: "Any enabled queue channel." },
      { name: "/capme", params: [], does: "Leave the captain role.", allowed: "Any enabled queue channel." },
      { name: "/capfor", params: ["team"], does: "Become captain for a team.", allowed: "Any enabled queue channel." },
      { name: "/pick", params: ["player"], does: "Pick a player during a match.", allowed: "Any enabled queue channel." },
      { name: "/report", params: ["result"], does: "Report a match result as loss, draw, or abort.", allowed: "Any enabled queue channel." },
      { name: "/lastgame", params: ["queue?", "player?", "match_id?"], does: "Show the last game for a queue, player, or match id.", allowed: "Any enabled queue channel." },
      { name: "/top", params: ["period?"], does: "Show the top players for a time period.", allowed: "Any enabled queue channel." },
      { name: "/predictionboard", params: ["page?", "period?"], does: "Show the prediction win/loss board.", allowed: "Any enabled queue channel." },
      { name: "/teamboard", params: [], does: "Show the best 4-player team combinations by win rate.", allowed: "Any enabled queue channel." },
      { name: "/lobby", params: ["lobby_id"], does: "Look up an AoE2:DE lobby and generate a join button.", allowed: "Any enabled queue channel." },
      { name: "/replay upload", params: ["file", "summary?"], does: "Analyse a recorded game and post its breakdown.", allowed: "Any enabled queue channel." },
      { name: "/aoe stat", params: ["player?"], does: "A player's record from analysed games: positions, APM, Elo, luck.", allowed: "Any enabled queue channel." },
      { name: "/aoe civs", params: ["map?"], does: "Civ win rates from analysed games, overall or on one map.", allowed: "Any enabled queue channel." },
      { name: "/aoe seats", params: ["player?", "map?"], does: "Flank and pocket records with win rates.", allowed: "Any enabled queue channel." },
      { name: "/aoe draft", params: ["mode", "map?", "redraw?"], does: "Civ draft for your started match.", allowed: "Any enabled queue channel." },
      { name: "/aoe link", params: ["name", "player?"], does: "Link an in-game name to your Discord account.", allowed: "Any enabled queue channel." },
      { name: "/aoe map_type", params: ["map", "type?"], does: "Show a map's type; moderators can set it.", allowed: "Any enabled queue channel." },
      { name: "Explain analysis", params: [], does: "Right-click a breakdown post → Apps: why each player got their scores.", allowed: "Breakdown posts and their threads." },
      { name: "/rank", params: ["player?", "board?"], does: "Show a player rating profile.", allowed: "Any enabled queue channel." },
      { name: "/leaderboard", params: ["page?", "board?"], does: "Show the rating leaderboard.", allowed: "Any enabled queue channel." },
      { name: "/auto_ready", params: ["duration?"], does: "Auto-confirm the next check-in for a duration.", allowed: "Any enabled queue channel." },
      { name: "/expire", params: ["duration?"], does: "Set or show your current expire timer.", allowed: "Any enabled queue channel." },
      { name: "/expire_default", params: ["duration?", "afk?", "clear?"], does: "Set or show your default expire timer.", allowed: "Any enabled queue channel." },
      { name: "/allow_offline", params: [], does: "Toggle offline-status immunity.", allowed: "Any enabled queue channel." },
      { name: "/switch_dms", params: [], does: "Toggle whether the bot DMs players on queue start.", allowed: "Any enabled queue channel." },
      { name: "/cointoss", params: ["side?"], does: "Toss a coin.", allowed: "Any enabled queue channel." },
      { name: "/help", params: ["queue?"], does: "Show help for the channel or a queue.", allowed: "Any enabled queue channel." },
      { name: "/commands", params: [], does: "Open the command list page.", allowed: "Any guild channel allowed by the slash command registration." },
      { name: "/nick", params: ["nick"], does: "Change your nickname with the rating prefix.", allowed: "Any enabled queue channel." },
    ],
  },
  {
    title: "Queue admin slash commands",
    scope: "Admin only",
    commands: [
      { name: "/channel enable", params: [], does: "Enable the bot on the current text channel.", allowed: "Any text channel in the server." },
      { name: "/channel enable_tournament", params: ["registration_channel", "announcement_channel?"], does: "Enable tournament registration and announcement channels.", allowed: "Any text channel in the server." },
      { name: "/channel disable", params: [], does: "Disable the bot on the current enabled queue or tournament channel.", allowed: "Current enabled channel." },
      { name: "/channel delete", params: [], does: "Delete stats/configs and disable the bot on the current queue channel.", allowed: "Current enabled queue channel." },
      { name: "/channel show", params: [], does: "Show the channel configuration.", allowed: "Current enabled queue channel." },
      { name: "/channel set", params: ["variable", "value"], does: "Change a channel configuration value.", allowed: "Current enabled queue channel." },
      { name: "/queue create_pickup", params: ["name", "size?"], does: "Create a new pickup queue.", allowed: "Current enabled queue channel." },
      { name: "/queue list", params: [], does: "List all queues on the channel.", allowed: "Current enabled queue channel." },
      { name: "/queue show", params: ["queue"], does: "Show one queue configuration.", allowed: "Current enabled queue channel." },
      { name: "/queue set", params: ["queue", "variable", "value"], does: "Change a queue setting.", allowed: "Current enabled queue channel." },
      { name: "/queue delete", params: ["queue"], does: "Delete a queue.", allowed: "Current enabled queue channel." },
      { name: "/queue add_player", params: ["player", "queue"], does: "Add a player to a queue.", allowed: "Current enabled queue channel." },
      { name: "/queue remove_player", params: ["player", "queues?"], does: "Remove a player from one or more queues.", allowed: "Current enabled queue channel." },
      { name: "/queue clear", params: ["queue?"], does: "Remove players from a queue.", allowed: "Current enabled queue channel." },
      { name: "/queue start", params: ["queue"], does: "Start a queue.", allowed: "Current enabled queue channel." },
      { name: "/queue split", params: ["queue", "group_size?", "sort_by_rating?"], does: "Split a queue into multiple matches.", allowed: "Current enabled queue channel." },
      { name: "/match report", params: ["match_id", "winner_team?", "draw?", "abort?"], does: "Report a pickup rating match result as a moderator.", allowed: "Current enabled queue channel." },
      { name: "/match create", params: ["queue", "winners", "losers", "draw?"], does: "Manually report a rating match.", allowed: "Current enabled queue channel." },
      { name: "/match sub_player", params: ["player1", "player2"], does: "Substitute a player in a match.", allowed: "Current enabled queue channel." },
      { name: "/match test_story", params: ["match_id"], does: "Regenerate the AI match story for a match.", allowed: "Current enabled queue channel." },
      { name: "/match put", params: ["match_id", "player", "team"], does: "Place a player into a team.", allowed: "Current enabled queue channel." },
      { name: "/noadds list", params: [], does: "Show the no-adds list.", allowed: "Current enabled queue channel." },
      { name: "/noadds add", params: ["player", "duration?", "reason?"], does: "Ban a player from queue participation.", allowed: "Current enabled queue channel." },
      { name: "/noadds remove", params: ["player"], does: "Remove a player from the no-adds list.", allowed: "Current enabled queue channel." },
      { name: "/phrases add", params: ["player", "phrase"], does: "Add a player phrase.", allowed: "Current enabled queue channel." },
      { name: "/phrases clear", params: ["player"], does: "Clear player phrases.", allowed: "Current enabled queue channel." },
      { name: "/rating seed", params: ["player", "rating", "deviation?"], does: "Set a player rating and deviation.", allowed: "Current enabled queue channel." },
      { name: "/rating penality", params: ["player", "points", "reason?"], does: "Subtract points from a player rating.", allowed: "Current enabled queue channel." },
      { name: "/rating hide_player", params: ["player"], does: "Hide a player from the leaderboard.", allowed: "Current enabled queue channel." },
      { name: "/rating unhide_player", params: ["player"], does: "Unhide a player from the leaderboard.", allowed: "Current enabled queue channel." },
      { name: "/rating reset", params: [], does: "Reset rating data on the channel.", allowed: "Current enabled queue channel." },
      { name: "/rating snap", params: [], does: "Snap player ratings to rank values.", allowed: "Current enabled queue channel." },
      { name: "/stats show", params: ["player?"], does: "Show channel or player stats.", allowed: "Current enabled queue channel." },
      { name: "/stats reset", params: [], does: "Reset all stats data on the channel.", allowed: "Current enabled queue channel." },
      { name: "/stats reset_player", params: ["player"], does: "Reset player stats.", allowed: "Current enabled queue channel." },
      { name: "/stats stats_replace_player", params: ["player1", "player2"], does: "Replace one player with another in stats.", allowed: "Current enabled queue channel." },
      { name: "/stats undo_match", params: ["match_id"], does: "Undo a finished match.", allowed: "Current enabled queue channel." },
    ],
  },
  {
    title: "Tournament commands",
    scope: "Tournament channels only",
    commands: [
      { name: "/test team", params: ["player1", "player2"], does: "Check whether a 2-player team is eligible.", allowed: "Tournament commands channel." },
      { name: "/test registration", params: ["player1", "player2"], does: "Check whether a registration team is eligible.", allowed: "Tournament commands channel." },
      { name: "/test player", params: ["player"], does: "Check whether a player is eligible.", allowed: "Tournament commands channel." },
      { name: "/suggest player", params: ["player", "all?"], does: "Suggest teammates for a player. `all` includes players below the 12-match activity filter.", allowed: "Tournament commands channel." },
      { name: "/register team", params: ["player1", "player2", "team_name"], does: "Register a 2-player team for the active tournament.", allowed: "Tournament registration channel only." },
      { name: "/tournament start", params: ["name?"], does: "Start the active tournament and generate group matches.", allowed: "Tournament commands channel." },
      { name: "/tournament report", params: ["match_id", "team_a_points", "team_b_points"], does: "Report a tournament score. Enter F for the forfeiting team; its opponent receives a 3-F win.", allowed: "Authorized tournament reporters in the tournament chat only." },
      { name: "/tournament undo_report", params: ["match_id"], does: "Undo a wrong tournament result, restore the pointboard, and reverse prediction points.", allowed: "Authorized tournament reporters in the tournament chat only." },
      { name: "/tournament not_registered", params: [], does: "List eligible players who are not yet registered.", allowed: "Tournament commands channel." },
      { name: "/tournament suggest_team", params: [], does: "Suggest strong team combinations from eligible unregistered players.", allowed: "Tournament commands channel." },
      { name: "/tournaments", params: ["tournament?"], does: "List tournaments or show registered teams for one tournament.", allowed: "Tournament commands channel." },
    ],
  },
  {
    title: "Queue text aliases",
    scope: "Message commands in enabled queue channels",
    commands: [
      { name: "add / j", params: ["queues?"], does: "Text alias for /add.", allowed: "Enabled queue channel." },
      { name: "remove / l", params: ["queues?"], does: "Text alias for /remove.", allowed: "Enabled queue channel." },
      { name: "who", params: [], does: "List queued players.", allowed: "Enabled queue channel." },
      { name: "queues", params: [], does: "List configured queues.", allowed: "Enabled queue channel." },
      { name: "matches", params: [], does: "Show active matches.", allowed: "Enabled queue channel." },
      { name: "teams", params: [], does: "Show teams for the current match.", allowed: "Enabled queue channel." },
      { name: "ready / r", params: [], does: "Mark yourself ready.", allowed: "Enabled queue channel." },
      { name: "notready / nr", params: [], does: "Mark yourself not ready.", allowed: "Enabled queue channel." },
      { name: "subme", params: [], does: "Request a substitute.", allowed: "Enabled queue channel." },
      { name: "subfor", params: ["@player"], does: "Offer to substitute for a player.", allowed: "Enabled queue channel." },
      { name: "capme", params: [], does: "Leave captaincy.", allowed: "Enabled queue channel." },
      { name: "capfor", params: ["team"], does: "Become captain for a team.", allowed: "Enabled queue channel." },
      { name: "pick / p", params: ["player"], does: "Pick a player.", allowed: "Enabled queue channel." },
      { name: "report_loss / rl", params: [], does: "Report a loss.", allowed: "Enabled queue channel." },
      { name: "report_draw / rd", params: [], does: "Report a draw.", allowed: "Enabled queue channel." },
      { name: "report_cancel / rc", params: [], does: "Cancel the current match result.", allowed: "Enabled queue channel." },
      { name: "allow_offline / ao", params: [], does: "Toggle offline immunity.", allowed: "Enabled queue channel." },
      { name: "expire", params: ["duration?"], does: "Set or show your expire timer.", allowed: "Enabled queue channel." },
      { name: "auto_ready / ar", params: ["duration?"], does: "Auto-ready for a period.", allowed: "Enabled queue channel." },
      { name: "rank", params: ["player?", "board?"], does: "Show a rating profile.", allowed: "Enabled queue channel." },
      { name: "leaderboard / lb", params: ["page?", "board?"], does: "Show the leaderboard.", allowed: "Enabled queue channel." },
      { name: "predictionboard / pb", params: ["page?", "period?"], does: "Show prediction standings.", allowed: "Enabled queue channel." },
      { name: "teamboard / tb", params: [], does: "Show team combination standings.", allowed: "Enabled queue channel." },
      { name: "lastgame / lg", params: ["queue? | player? | match_id?"], does: "Show the latest game for a queue, player, or match.", allowed: "Enabled queue channel." },
      { name: "cancel_match", params: ["match_id"], does: "Cancel a finished match as an admin.", allowed: "Enabled queue channel." },
      { name: "test_story", params: ["match_id"], does: "Regenerate a match story.", allowed: "Enabled queue channel." },
      { name: "lobby", params: ["lobby_id"], does: "Inspect an open AoE2:DE lobby.", allowed: "Enabled queue channel." },
      { name: "promote", params: ["queue?"], does: "Promote a queue.", allowed: "Enabled queue channel." },
      { name: "set_channel_cfg", params: ["json"], does: "Set raw channel config JSON.", allowed: "Enabled queue channel." },
      { name: "set_queue_cfg", params: ["queue", "json"], does: "Set raw queue config JSON.", allowed: "Enabled queue channel." },
      { name: "stats_reset_player", params: ["@player"], does: "Reset one player's stats.", allowed: "Enabled queue channel." },
      { name: "stats_replace_player", params: ["@player1", "@player2"], does: "Replace one player with another in stats.", allowed: "Enabled queue channel." },
      { name: "rating_hide_player", params: ["@player"], does: "Hide a player from the leaderboard.", allowed: "Enabled queue channel." },
      { name: "rating_unhide_player", params: ["@player"], does: "Unhide a player from the leaderboard.", allowed: "Enabled queue channel." },
    ],
  },
];

function isBlank(value) {
  return value === null || value === undefined || value === "";
}

function CommandListPanel() {
  return (
    <div className="panel command-list-panel">
      <div className="panel-header">
        <div>
          <h1>Command list</h1>
          <p>
            A reference for the bot commands available in this server. Each command shows its
            params, purpose, and where it is allowed to run.
          </p>
        </div>
      </div>
      <div className="command-list-note">
        Channel rules in the bot are enforced in code, so this page is meant as the source of truth
        for admins and moderators when they need to remember a command format quickly.
      </div>
      <div className="command-section-list">
        {COMMAND_DOC_SECTIONS.map((section) => (
          <section key={section.title} className="command-section">
            <div className="command-section-head">
              <div>
                <h2>{section.title}</h2>
                <p>{section.scope}</p>
              </div>
              <span className="field-suffix">{section.commands.length} commands</span>
            </div>
            <div className="command-grid">
              {section.commands.map((command) => (
                <article key={`${section.title}:${command.name}`} className="command-card">
                  <div className="command-card-head">
                    <div className="command-name">{command.name}</div>
                    <div className="command-badges">
                      <span className="command-chip">{command.allowed}</span>
                    </div>
                  </div>
                  <div className="command-meta">
                    <div>
                      <span className="command-meta-label">Params</span>
                      <div className="command-param-list">
                        {command.params.length ? command.params.map((param) => (
                          <span key={param} className="command-param-pill">{param}</span>
                        )) : <span className="command-empty">No params</span>}
                      </div>
                    </div>
                    <div>
                      <span className="command-meta-label">Does</span>
                      <div className="command-description">{command.does}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function toDuration(seconds) {
  const total = Number(seconds || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function fromDuration(value) {
  if (isBlank(value)) return "";
  if (typeof value === "number") return toDuration(value);
  return String(value);
}

function toDraftValue(field, value, resources = {}) {
  if (field.kind === "VariableTable") {
    return Array.isArray(value)
      ? value.map((row) => {
          const copy = {};
          for (const col of field.columns || []) {
            copy[col.name] = toDraftValue(col, row?.[col.name] ?? "", resources);
          }
          return copy;
        })
      : [];
  }
  if (field.kind === "BoolVar") {
    return Number(value) ? "1" : "0";
  }
  if (field.kind === "DurationVar") {
    return fromDuration(value);
  }
  if (field.kind === "DateVar") {
    return isBlank(value) ? "" : String(value);
  }
  if (field.kind === "RoleVar") {
    const roles = Array.isArray(resources.roles) ? resources.roles : [];
    const normalized = isBlank(value) ? "" : String(value);
    return normalized && !roles.some((role) => String(role.id) === normalized) ? "" : normalized;
  }
  if (field.kind === "TextChanVar") {
    const channels = Array.isArray(resources.text_channels) ? resources.text_channels : [];
    const normalized = isBlank(value) ? "" : String(value);
    return normalized && !channels.some((channel) => String(channel.id) === normalized) ? "" : normalized;
  }
  return isBlank(value) ? "" : String(value);
}

function defaultDraft(field) {
  if (field.kind === "VariableTable") return [];
  if (field.kind === "BoolVar") return field.default ? "1" : "0";
  if (field.kind === "DurationVar") return fromDuration(field.default ?? "");
  if (field.kind === "DateVar") return isBlank(field.default) ? "" : String(field.default);
  return isBlank(field.default) ? "" : String(field.default);
}

function formatWinRate(value) {
  if (value === null || value === undefined || value === "") return "\u2014";
  const rate = Number(value);
  if (!Number.isFinite(rate)) return "\u2014";
  const percent = rate <= 1 ? rate * 100 : rate;
  return `${percent.toFixed(percent >= 10 ? 0 : 1)}%`;
}

function formatCount(value) {
  if (value === null || value === undefined || value === "") return "\u2014";
  return String(value);
}

function formatElo(value) {
  if (value === null || value === undefined || value === "") return "\u2014";
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number)) : "\u2014";
}

function compareText(a, b, dir = "asc") {
  const av = String(a ?? "").toLowerCase();
  const bv = String(b ?? "").toLowerCase();
  const result = av.localeCompare(bv);
  return dir === "asc" ? result : -result;
}

function compareNumber(a, b, dir = "desc") {
  const av = Number(a);
  const bv = Number(b);
  const an = Number.isFinite(av) ? av : Number.NEGATIVE_INFINITY;
  const bn = Number.isFinite(bv) ? bv : Number.NEGATIVE_INFINITY;
  const result = an === bn ? 0 : an > bn ? 1 : -1;
  return dir === "asc" ? result : -result;
}

function getLeaderboardSortOptions(board) {
  if (board === "predictionboard") {
    return [
      { value: "rank", label: "Sort: rank" },
      { value: "nick", label: "Sort: nickname" },
      { value: "wins", label: "Sort: wins" },
      { value: "losses", label: "Sort: losses" },
      { value: "streak", label: "Sort: streak" },
      { value: "points", label: "Sort: score" },
      { value: "win_rate", label: "Sort: win rate" },
    ];
  }
  if (board === "teamcombos") {
    return [
      { value: "matches", label: "Sort: matches" },
      { value: "team", label: "Sort: team name" },
      { value: "wins", label: "Sort: wins" },
      { value: "losses", label: "Sort: losses" },
      { value: "draws", label: "Sort: draws" },
      { value: "win_rate", label: "Sort: win rate" },
    ];
  }
  const options = [
    { value: "rank", label: "Sort: rank" },
    { value: "nick", label: "Sort: nickname" },
    { value: "rating", label: "Sort: rating" },
    { value: "wins", label: "Sort: wins" },
    { value: "losses", label: "Sort: losses" },
    { value: "draws", label: "Sort: draws" },
    { value: "win_rate", label: "Sort: win rate" },
  ];
  if (board === "team") {
    options.push(
      { value: "max_server", label: "Sort: max server Elo" },
      { value: "avg_server", label: "Sort: avg server Elo" },
    );
  }
  return options;
}

function getDefaultLeaderboardSort(board) {
  if (board === "predictionboard") return "rank";
  if (board === "teamcombos") return "matches";
  return "rating";
}

function sortLeaderboardRows(rows, board, sortKey) {
  const sort = String(sortKey || getDefaultLeaderboardSort(board)).toLowerCase();
  return [...rows].sort((a, b) => {
    if (board === "predictionboard") {
      switch (sort) {
        case "rank": return compareNumber(a.rank, b.rank, "asc");
        case "nick": return compareText(a.nick, b.nick, "asc");
        case "wins": return compareNumber(a.wins, b.wins, "desc");
        case "losses": return compareNumber(a.losses, b.losses, "asc");
        case "streak": return compareNumber(a.streak, b.streak, "desc");
        case "points": return compareNumber(a.points, b.points, "desc");
        case "win_rate": return compareNumber(a.win_rate, b.win_rate, "desc");
        default: return compareNumber(a.rank, b.rank, "asc");
      }
    }
    if (board === "teamcombos") {
      switch (sort) {
        case "team": return compareText(a.member_names, b.member_names, "asc");
        case "matches": return compareNumber(a.matches, b.matches, "desc");
        case "wins": return compareNumber(a.wins, b.wins, "desc");
        case "losses": return compareNumber(a.losses, b.losses, "asc");
        case "draws": return compareNumber(a.draws, b.draws, "desc");
        case "win_rate": return compareNumber(a.win_rate, b.win_rate, "desc");
        default: return compareNumber(a.matches, b.matches, "desc");
      }
    }
    switch (sort) {
      case "rank": return compareNumber(a.rank, b.rank, "asc");
      case "nick": return compareText(a.nick, b.nick, "asc");
      case "wins": return compareNumber(a.wins, b.wins, "desc");
      case "losses": return compareNumber(a.losses, b.losses, "asc");
      case "draws": return compareNumber(a.draws, b.draws, "desc");
      case "win_rate": {
        const aw = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : null;
        const bw = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : null;
        return compareNumber(aw, bw, "desc");
      }
      case "max_server":
        return compareNumber(a.highest_server_team_rating, b.highest_server_team_rating, "desc");
      case "avg_server": {
        const aAvg = a.rating != null && a.highest_server_team_rating != null ? (Number(a.rating) + Number(a.highest_server_team_rating)) / 2 : null;
        const bAvg = b.rating != null && b.highest_server_team_rating != null ? (Number(b.rating) + Number(b.highest_server_team_rating)) / 2 : null;
        return compareNumber(aAvg, bAvg, "desc");
      }
      case "rating":
      default:
        return compareNumber(a.rating, b.rating, "desc");
    }
  });
}

function formatSyncedAt(value) {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(Number(value) * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function formatDateOnly(value) {
  if (value === null || value === undefined || value === "") return null;
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const date = new Date(`${text}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    }
  }
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  }
  return text;
}

function decodeEscapedText(value) {
  if (value === null || value === undefined) return value;
  const text = String(value);
  if (!text.includes("\\u")) return text;
  return text.replace(/\\u([0-9a-fA-F]{4})/g, (_match, hex) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function analyticsLabel(entity) {
  return decodeEscapedText(entity?.name || entity?.key || entity?.profile_id || "\u2014");
}

function formatAgo(unixSeconds) {
  if (!unixSeconds) return null;
  const diff = Math.max(0, Date.now() / 1000 - Number(unixSeconds));
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hours ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)} days ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function formatAgoPrecise(unixSeconds) {
  if (!unixSeconds) return null;
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - Number(unixSeconds)));
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (hours || parts.length) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  if (minutes || parts.length) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  parts.push(`${seconds} second${seconds === 1 ? "" : "s"}`);
  return `${parts.join(", ")} ago`;
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function formatSignedNumber(value, digits = 1) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (Number.isInteger(number)) return `${number >= 0 ? "+" : ""}${number}`;
  const fixed = number.toFixed(digits).replace(/\.0+$/, "");
  return `${number >= 0 ? "+" : ""}${fixed}`;
}

function formatMetricValue(value, digits = 1) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (Number.isInteger(number)) return formatCount(number);
  return number.toFixed(digits).replace(/\.0+$/, "");
}

function formatRatio(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return formatWinRate(number);
}

function formatStrategyTags(tags) {
  if (!tags) return null;
  if (typeof tags === "string") return tags;
  if (Array.isArray(tags)) return tags.filter(Boolean).map((tag) => String(tag)).join(" · ");
  if (typeof tags === "object") {
    const entries = Object.entries(tags)
      .filter(([, value]) => value !== null && value !== undefined && value !== false)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`);
    return entries.length ? entries.join(" · ") : null;
  }
  return String(tags);
}

function MatchCard({ match, onClick }) {
  const ago = formatAgo(match.at);
  const duration = formatDuration(match.duration_seconds);
  const mapName = decodeEscapedText(match.map_name) || "Unknown map";
  const modeLabel = decodeEscapedText(match.mode_label) || "";
  const sourceLabel = match.source === "aoe2c" ? "Replay" : match.source === "internal" ? "Server" : match.source || "Match";
  const isClickable = typeof onClick === "function";
  const Wrapper = isClickable ? "button" : "div";
  return (
    <Wrapper
      {...(isClickable ? { type: "button", onClick } : {})}
      className={`match-card${isClickable ? " match-card-clickable" : ""}`}
    >
      <div className="match-card-map">
        {match.map_image_url ? <img src={match.map_image_url} alt={mapName} /> : <div className="match-card-map-placeholder">🗺</div>}
        <div>
          <div className="match-card-map-name">{mapName}</div>
          <div className="match-card-map-mode">{modeLabel}</div>
          <div className="match-card-map-time">{sourceLabel}{ago ? ` \u00B7 ${ago}` : ""}{duration ? ` \u00B7 ${duration}` : ""}</div>
        </div>
      </div>
      <div className="match-card-teams">
        {match.teams.map((team, teamIdx) => (
          <div key={teamIdx} className="match-card-team">
            {team.map((p, pIdx) => (
              <div key={pIdx} className={`match-card-player ${p.won === true ? "won" : p.won === false ? "lost" : ""}`}>
                {p.civ_image_url ? <img className="match-card-civ" src={p.civ_image_url} alt={decodeEscapedText(p.civ_name) || ""} /> : null}
                <span className="match-card-player-name">{decodeEscapedText(p.name) || "?"}</span>
                {p.rating != null ? <span className="match-card-rating">{p.rating}</span> : null}
                {p.rating_diff != null ? (
                  <span className={`match-card-rating-diff ${p.rating_diff >= 0 ? "up" : "down"}`}>
                    {p.rating_diff >= 0 ? "\u2191" : "\u2193"}{Math.abs(p.rating_diff)}
                  </span>
                ) : null}
                {p.civ_name ? <span className="match-card-civ-name">{decodeEscapedText(p.civ_name)}</span> : null}
                <span className="match-card-result">{p.won === true ? "👑" : p.won === false ? "💀" : ""}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Wrapper>
  );
}

function WinRateBar({ value }) {
  const percent = Math.max(0, Math.min(100, (value <= 1 ? value * 100 : value)));
  const tier = percent >= 60 ? "high" : percent >= 45 ? "mid" : "low";
  return (
    <div className="winrate-bar" title={`${percent.toFixed(1)}%`}>
      <div className={`winrate-bar-fill winrate-bar-${tier}`} style={{ width: `${percent}%` }} />
      <span className="winrate-bar-label">{formatWinRate(value)}</span>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="lb-pagination">
      <button className="button secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>Prev</button>
      <span className="lb-pagination-label">Page {page} of {pages}</span>
      <button className="button secondary" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</button>
    </div>
  );
}

function AnalyticsCard({ label, value, note }) {
  return (
    <div className="analytics-card">
      <div className="analytics-card-label">{label}</div>
      <div className="analytics-card-value">{value}</div>
      {note ? <div className="analytics-card-note">{note}</div> : null}
    </div>
  );
}

function groupFields(schema) {
  const ordered = schema.sections?.length ? [...schema.sections] : ["General"];
  const out = Object.fromEntries(ordered.map((section) => [section, []]));
  for (const field of schema.variables || []) {
    const section = field.section || ordered[0] || "General";
    if (!out[section]) out[section] = [];
    out[section].push(field);
  }
  return { ordered, out };
}

function schemaForTarget(data, target) {
  if (!data) return null;
  if (target?.kind === "tournament") return data.schemas.tournament_channel;
  return target?.kind === "queue" ? data.schemas.pickup_queue : data.schemas.queue_channel;
}

function queueConfigForChannel(channel) {
  return (channel?.queues || []).map((queue) => ({ ...queue, kind: "queue" }));
}

function useBootstrap(enabled = true) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const refresh = async () => {
    if (!enabled) return;
    setState({ loading: true, data: null, error: null });
    try {
      const raw = await Api.get("/api/bootstrap");
      const parsed = BootstrapSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(`Bootstrap validation failed: ${parsed.error.issues.map((i) => i.path.join(".") + " " + i.message).join("; ")}`);
      }
      setState({ loading: false, data: parsed.data, error: null });
    } catch (error) {
      setState({ loading: false, data: null, error: error.message });
    }
  };
  useEffect(() => {
    if (enabled) refresh();
    else setState({ loading: false, data: null, error: null });
  }, [enabled]);
  return [state, refresh];
}

function useChannelOptions(channelId, kind = "queue") {
  const [state, setState] = useState({ loading: false, data: { roles: [], text_channels: [], members: [] }, error: null });
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const path = kind === "tournament" ? `/api/tournament-channels/${channelId}/options` : `/api/channels/${channelId}/options`;
    Api.get(path)
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, data, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ loading: false, data: { roles: [], text_channels: [] }, error: error.message });
      });
    return () => { cancelled = true; };
  }, [channelId, kind]);
  return state;
}

function useAuth() {
  const [state, setState] = useState({ loading: true, session: null, user: null, error: null });

  useEffect(() => {
    let cancelled = false;

    // Supabase's own redirect (success or decline) doesn't always land on the
    // /auth/callback path we asked for via redirectTo - if that path isn't in the
    // project's Redirect URLs allow list, GoTrue falls back to the Site URL root
    // instead, tokens/error and all. So we key off the "a login is in flight" flag
    // set by signInWithDiscord() rather than the current pathname.
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const oauthError = searchParams.get("error") || hashParams.get("error");
    if (oauthError) {
      const destination = window.sessionStorage.getItem("pubobot-auth-next") || "/member";
      window.sessionStorage.removeItem("pubobot-auth-next");
      window.sessionStorage.setItem(
        "pubobot-auth-error",
        oauthError === "access_denied"
          ? "Discord sign-in was cancelled."
          : (searchParams.get("error_description") || hashParams.get("error_description") || "Discord sign-in failed."),
      );
      window.history.replaceState({}, "", destination);
      routeStore.emit();
    }

    if (!supabase) {
      setState({
        loading: false,
        session: null,
        user: null,
        error: "Supabase auth is not configured. Set SUPABASE_URL and SUPABASE_PUBLISHABLE_KEY in config.cfg.",
      });
      Api.setAccessToken(null);
      return () => {};
    }

    Api.setUnauthorizedHandler(async () => {
      try {
        await supabase.auth.signOut();
      } finally {
        Api.setAccessToken(null);
        if (!cancelled) setState({ loading: false, session: null, user: null, error: null });
      }
    });

    const syncSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) throw error;
        const session = data.session || null;
        Api.setAccessToken(session?.access_token || null);
        setState({
          loading: false,
          session,
          user: session?.user || null,
          error: null,
        });
        if (session && window.sessionStorage.getItem("pubobot-auth-next")) {
          const destination = window.sessionStorage.getItem("pubobot-auth-next");
          window.sessionStorage.removeItem("pubobot-auth-next");
          window.history.replaceState({}, "", destination);
          routeStore.emit();
        }
      } catch (error) {
        if (cancelled) return;
        Api.setAccessToken(null);
        setState({ loading: false, session: null, user: null, error: error.message });
      }
    };

    syncSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      Api.setAccessToken(session?.access_token || null);
      setState({
        loading: false,
        session,
        user: session?.user || null,
        error: null,
      });
      if (session && window.sessionStorage.getItem("pubobot-auth-next")) {
        const destination = window.sessionStorage.getItem("pubobot-auth-next");
        window.sessionStorage.removeItem("pubobot-auth-next");
        window.history.replaceState({}, "", destination);
        routeStore.emit();
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const refresh = async () => {
    if (!supabase) {
      setState({
        loading: false,
        session: null,
        user: null,
        error: "Supabase auth is not configured.",
      });
      return null;
    }
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    const session = data.session || null;
    Api.setAccessToken(session?.access_token || null);
    setState({
      loading: false,
      session,
      user: session?.user || null,
      error: null,
    });
    return session?.user || null;
  };

  const signInWithPassword = async ({ email, password }) => {
    if (!supabase) throw new Error("Supabase auth is not configured.");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signInWithDiscord = async ({ redirectTo, next = "/member" }) => {
    if (!supabase) throw new Error("Supabase auth is not configured.");
    window.sessionStorage.setItem("pubobot-auth-next", next);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: { redirectTo },
    });
    if (error) throw error;
  };

  const logout = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    Api.setAccessToken(null);
    setState({ loading: false, session: null, user: null, error: null });
  };

  return {
    ...state,
    refresh,
    logout,
    signInWithPassword,
    signInWithDiscord,
    ready: Boolean(supabase),
  };
}

function App() {
  const auth = useAuth();
  const [{ loading, data, error }, refresh] = useBootstrap(Boolean(auth.user));
  const route = useRoute();
  const [selectedGuildId, setSelectedGuildId] = useState(null);
  const [selectedSection, setSelectedSection] = useState("General");
  const [sidebarQuery, setSidebarQuery] = useState("");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null);
  const [selectedPlayerRefresh, setSelectedPlayerRefresh] = useState(0);
  const [tournamentTeams, setTournamentTeams] = useState({ loading: false, data: null, error: null });
  const [tournamentTeamsRefresh, setTournamentTeamsRefresh] = useState(0);

  const channels = data?.queue_channels || [];
  const tournamentChannels = data?.tournament_channels || [];
  const allConfigChannels = [...channels, ...tournamentChannels];
  const guilds = data?.guilds || [];
  const view = route.name; // "root" | "config" | "leaderboard" | "matches" | "player"

  // The only redirect App does on its own: land on the first available channel's config
  // page when there's no channel in the URL yet. Every other navigation is explicit (nav
  // clicks, sidebar clicks, row clicks) so back/forward and deep links stay predictable.
  useEffect(() => {
    if (!auth.user || !data) return;
    if ((route.name === "root" || route.name === "login") && allConfigChannels[0]) {
      const first = allConfigChannels[0];
      navigate(first.type === "tournament" ? `/t/${first.id}` : `/c/${first.id}`, { replace: true });
    }
  }, [auth.user, data, route.name, allConfigChannels]);

  const selectedChannel = useMemo(
    () => channels.find((ch) => String(ch.id) === String(route.channelId)) || null,
    [channels, route.channelId],
  );
  const selectedTournamentChannel = useMemo(
    () => tournamentChannels.find((ch) => String(ch.id) === String(route.channelId)) || null,
    [tournamentChannels, route.channelId],
  );
  const selectedQueue = useMemo(
    () => selectedChannel?.queues?.find((q) => String(q.id) === String(route.queueId)) || null,
    [selectedChannel, route.queueId],
  );

  useEffect(() => {
    const selected = selectedTournamentChannel || selectedChannel;
    if (selected && !selectedGuildId) setSelectedGuildId(selected.guild_id);
  }, [selectedChannel, selectedTournamentChannel, selectedGuildId]);

  useEffect(() => {
    if (!selectedTournamentChannel) {
      setTournamentTeams({ loading: false, data: null, error: null });
      return;
    }
    let cancelled = false;
    setTournamentTeams({ loading: true, data: null, error: null });
    Api.get(`/api/tournament-channels/${selectedTournamentChannel.id}/teams`)
      .then((data) => { if (!cancelled) setTournamentTeams({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setTournamentTeams({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [selectedTournamentChannel?.id, tournamentTeamsRefresh]);
  const target = selectedTournamentChannel
    ? { kind: "tournament", item: selectedTournamentChannel }
    : selectedQueue ? { kind: "queue", item: selectedQueue } : selectedChannel ? { kind: "channel", item: selectedChannel } : null;
  const schema = schemaForTarget(data, target);
  const options = useChannelOptions(
    target?.kind === "tournament" ? target.item.id : selectedChannel?.id,
    target?.kind,
  );

  const fieldResources = useMemo(() => ({
    roles: options.data?.roles || [],
    text_channels: options.data?.text_channels || [],
  }), [options.data]);

  const grouped = useMemo(() => {
    if (!schema) return { ordered: [], out: {} };
    return groupFields(schema);
  }, [schema]);

  useEffect(() => {
    if (!auth.user || !schema || !target?.item) return;
    const next = {};
    for (const field of schema.variables || []) {
      next[field.name] = toDraftValue(field, target.item.cfg?.[field.name], fieldResources);
    }
    setDraft(next);
    setSelectedSection((current) => (grouped.ordered.includes(current) ? current : (grouped.ordered[0] || "General")));
    // Also re-runs whenever `target.item.cfg` itself changes (a new object on every bootstrap
    // refetch - refresh() fires after several unrelated actions elsewhere on the page, e.g.
    // creating/deleting a queue or editing a player, without target.item.id ever changing).
    // Without this, the draft could keep submitting a value a field had at the moment this page
    // was first opened - even after the server-side value changed underneath it (e.g. a role
    // cleared via a Discord command elsewhere) - and Save would keep failing on stale data the
    // user never touched and had no way to see, until they did a full page reload.
  }, [auth.user, schema, target?.item?.id, target?.item?.cfg, grouped.ordered, fieldResources]);

  const filteredChannels = useMemo(() => {
    const q = sidebarQuery.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter((channel) => {
      return String(channel.channel_name || "").toLowerCase().includes(q) ||
        String(channel.guild_name || "").toLowerCase().includes(q) ||
        (channel.queues || []).some((queue) => String(queue.name || "").toLowerCase().includes(q));
    });
  }, [channels, sidebarQuery]);
  const filteredTournamentChannels = useMemo(() => {
    const q = sidebarQuery.trim().toLowerCase();
    if (!q) return tournamentChannels;
    return tournamentChannels.filter((channel) => (
      String(channel.channel_name || "").toLowerCase().includes(q) ||
      String(channel.announcement_channel_name || "").toLowerCase().includes(q) ||
      String(channel.guild_name || "").toLowerCase().includes(q)
    ));
  }, [tournamentChannels, sidebarQuery]);

  const save = async () => {
    if (!schema || !target?.item) return;
    const payload = {};
    for (const field of schema.variables || []) {
      const value = draft[field.name];
      if (field.kind === "VariableTable") {
        payload[field.name] = Array.isArray(value) ? value.map((row) => ({ ...row })) : [];
      } else if (field.kind === "BoolVar") {
        payload[field.name] = value === "1" || value === 1 || value === true ? 1 : 0;
      } else if (field.kind === "IntVar" || field.kind === "SliderVar") {
        if (isBlank(value)) {
          if (field.notnull) throw new Error(`${field.display || field.name} is required.`);
          payload[field.name] = null;
        } else if (!Number.isFinite(Number(value))) {
          throw new Error(`${field.display || field.name} must be a number.`);
        } else {
          payload[field.name] = Number(value);
        }
      } else if (field.kind === "DurationVar") {
        if (isBlank(value)) {
          if (field.notnull) throw new Error(`${field.display || field.name} is required.`);
          payload[field.name] = null;
        } else if (!/^\d{2}:\d{2}:\d{2}$/.test(String(value))) {
          // Shape-check only, matching core.utils.parse_duration's ^\d\d:\d\d:\d\d$ format - the
          // backend owns actual parsing (it also accepts "3h2m1s" style, which this field's UI
          // doesn't produce but validate() still allows). Don't recompute seconds here: this used
          // to send a raw integer, which parse_duration rejects since it never accepts a bare
          // number - that mismatch is exactly what caused "Invalid duration format." errors.
          throw new Error(`${field.display || field.name} must be in HH:MM:SS format.`);
        } else {
          payload[field.name] = String(value);
        }
      } else if (field.kind === "TextChanVar") {
        if (isBlank(value)) {
          payload[field.name] = null;
        } else {
          payload[field.name] = String(value);
        }
      } else {
        payload[field.name] = isBlank(value) ? null : value;
        if (field.notnull && isBlank(value)) throw new Error(`${field.display || field.name} is required.`);
      }
    }

    setSaving(true);
    setStatus("");
    try {
      const path = target.kind === "tournament"
        ? `/api/tournament-channels/${target.item.id}/config`
        : selectedQueue
        ? `/api/channels/${selectedChannel.id}/queues/${selectedQueue.id}/config`
        : `/api/channels/${selectedChannel.id}/config`;
      await Api.send(path, "POST", payload);
      await refresh();
      setStatus("Saved successfully.");
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSaving(false);
    }
  };

  const createQueue = async ({ name, size }) => {
    if (!selectedChannel) return;
    await Api.send(`/api/channels/${selectedChannel.id}/queues`, "POST", { name, size: Number(size) });
    await refresh();
  };

  const createTournamentTeam = async ({ teamName, player1Id, player2Id }) => {
    if (!selectedTournamentChannel) return;
    await Api.send(`/api/tournament-channels/${selectedTournamentChannel.id}/teams`, "POST", {
      team_name: teamName,
      player1_id: Number(player1Id),
      player2_id: Number(player2Id),
    });
    setTournamentTeamsRefresh((value) => value + 1);
    await refresh();
  };

  const deleteTournamentTeam = async (teamId) => {
    if (!selectedTournamentChannel) return;
    await Api.send(`/api/tournament-channels/${selectedTournamentChannel.id}/teams/${teamId}`, "DELETE");
    setTournamentTeamsRefresh((value) => value + 1);
    await refresh();
  };

  const reportTournamentMatch = async (matchId, result) => {
    if (!selectedTournamentChannel) return;
    const data = await Api.send(
      `/api/tournament-channels/${selectedTournamentChannel.id}/matches/${matchId}/report`,
      "POST",
      result,
    );
    setTournamentTeams({ loading: false, data, error: null });
    return data;
  };

  const enableChannel = async ({ channelId, type, announcementChannelId }) => {
    if (type === "tournament") {
      await Api.send(`/api/tournament-channels/${channelId}/enable`, "POST", { announcement_channel_id: announcementChannelId });
    } else {
      await Api.send(`/api/channels/${channelId}/enable`, "POST");
    }
    await refresh();
  };

  const disableChannel = async () => {
    if (!target?.item) return;
    const path = target.kind === "tournament"
      ? `/api/tournament-channels/${target.item.id}/disable`
      : `/api/channels/${selectedChannel.id}/disable`;
    await Api.send(path, "POST");
    await refresh();
  };

  const sectionFields = grouped.out[selectedSection] || [];
  const userLabel =
    auth.user?.email ||
    auth.user?.user_metadata?.full_name ||
    auth.user?.user_metadata?.name ||
    auth.user?.user_metadata?.preferred_username ||
    auth.user?.id ||
    "Signed in";

  if (auth.loading) {
    return (
      <div className="screen-state login">
        <div className="login-card">
          <h1>Checking session...</h1>
          <p>Loading your bot management workspace.</p>
        </div>
      </div>
    );
  }

  if (!auth.user) {
    return (
      <LoginScreen
        auth={auth}
      />
    );
  }

  if (loading) {
    return (
      <div className="screen-state login">
        <div className="login-card">
          <h1>Loading AOE BD admin...</h1>
          <p>Fetching servers, channels, and queue data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="screen-state login">
        <div className="login-card">
          <h1>Failed to load</h1>
          <p>{error}</p>
          <div className="modal-actions">
            <button className="button" onClick={refresh}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand" onClick={() => navigate("/")}>
          <img className="brand-mark" src="/android-chrome-192x192.png" alt="" />
          <div>
            <div className="brand-title">AOE BD</div>
            <div className="brand-subtitle">Age Of Empires 2 DE Bangladesh Community</div>
          </div>
        </div>
        <nav className="top-nav">
          <button
            className={`top-nav-item ${view === "leaderboard" ? "active" : ""}`}
            onClick={() => selectedChannel && navigate(view === "leaderboard" ? `/c/${selectedChannel.id}` : `/c/${selectedChannel.id}/leaderboard`)}
          >🏆 Leaderboards</button>
          <button
            className={`top-nav-item ${view === "matches" ? "active" : ""}`}
            onClick={() => selectedChannel && navigate(view === "matches" ? `/c/${selectedChannel.id}` : `/c/${selectedChannel.id}/matches`)}
          >⚔️ Active Matches</button>
          <button
            className={`top-nav-item ${view === "commands" ? "active" : ""}`}
            onClick={() => selectedChannel && navigate(view === "commands" ? `/c/${selectedChannel.id}` : `/c/${selectedChannel.id}/commands`)}
          >📚 Command list</button>
          <button
            className={`top-nav-item ${view === "admin-map-votes" ? "active" : ""}`}
            onClick={() => navigate("/admin/map-votes")}
          >🗳️ Map votes</button>
        </nav>
        <div className="top-actions">
          <div className="user-pill">{userLabel}</div>
          <button className="button secondary" onClick={auth.logout}>Logout</button>
        </div>
      </header>

      <div className="layout">
        <aside className="guild-rail">
          {guilds.map((guild) => (
            <button
              key={guild.id}
              className={`guild-pill ${String(selectedGuildId) === String(guild.id) ? "active" : ""}`}
              onClick={() => {
                setSelectedGuildId(guild.id);
                const first = allConfigChannels.find((channel) => String(channel.guild_id) === String(guild.id));
                if (first) navigate(first.type === "tournament" ? `/t/${first.id}` : `/c/${first.id}`);
              }}
              title={guild.name}
            >
              {guild.icon_url ? <img src={guild.icon_url} alt={guild.name} /> : guild.name.slice(0, 1)}
            </button>
          ))}
          <button className="guild-pill add" onClick={() => setModal({ type: "enableChannel" })}>+</button>
        </aside>

        <aside className="sidebar">
          <div className="sidebar-head"># find-games</div>
          <div className="sidebar-search">
            <input value={sidebarQuery} onChange={(e) => setSidebarQuery(e.target.value)} placeholder="Search channels and queues" />
          </div>
          <div className="sidebar-list">
            {filteredChannels.map((channel) => (
              <div key={channel.id} className="channel-block">
                <button
                  className={`channel-row ${String(selectedChannel?.id) === String(channel.id) && !selectedQueue ? "active" : ""}`}
                  onClick={() => navigate(`/c/${channel.id}`)}
                >
                  <span className="badge">[QC]</span>
                  <div>
                    <div>{channel.channel_name || `Channel ${channel.id}`}</div>
                    <small>{channel.guild_name || channel.guild_id}</small>
                  </div>
                </button>
                {String(selectedChannel?.id) === String(channel.id) ? (
                  <div className="queue-list">
                    {queueConfigForChannel(channel).map((queue) => (
                      <button
                        key={queue.id}
                        className={`queue-row ${String(selectedQueue?.id) === String(queue.id) ? "active" : ""}`}
                        onClick={() => navigate(`/c/${channel.id}/q/${queue.id}`)}
                      >
                        <span className="badge">[PQ]</span>
                        <div>
                          <div>{queue.name}</div>
                          <small>{queue.status}</small>
                        </div>
                      </button>
                    ))}
                    {!channel.queues?.length ? <div className="empty-note">No queues yet.</div> : null}
                  </div>
                ) : null}
              </div>
            ))}
            {filteredTournamentChannels.map((channel) => (
              <div key={`t-${channel.id}`} className="channel-block">
                <button
                  className={`channel-row ${String(selectedTournamentChannel?.id) === String(channel.id) ? "active" : ""}`}
                  onClick={() => navigate(`/t/${channel.id}`)}
                >
                  <span className="badge">[TC]</span>
                  <div>
                    <div>{channel.channel_name || `Tournament ${channel.id}`}</div>
                    <small>{channel.guild_name || channel.guild_id}</small>
                  </div>
                </button>
                {String(selectedTournamentChannel?.id) === String(channel.id) ? (
                  <div className="queue-list">
                    <div className="empty-note">Announcements: #{channel.announcement_channel_name || channel.announcement_channel_id || "not set"}</div>
                    <button className="queue-row" onClick={() => navigate("/tournaments")}>
                      <span className="badge">[WEB]</span>
                      <div>
                        <div>Public tournaments</div>
                        <small>/tournaments</small>
                      </div>
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </aside>

        <main className="content">
          {view === "admin-map-votes" ? (
            <AdminMapVotesPanel />
          ) : !target?.item ? (
            <div className="panel empty-panel">Select a channel to begin.</div>
          ) : view === "leaderboard" ? (
            <LeaderboardPanel
              channelId={selectedChannel.id}
              query={route.query}
              onQueryChange={(patch) => navigate(withQuery(`/c/${selectedChannel.id}/leaderboard`, { ...Object.fromEntries(route.query), ...patch }), { replace: true })}
              onSelectPlayer={(player) => navigate(withQuery(`/c/${selectedChannel.id}/players/${player.user_id}`, { board: player.board }))}
              refreshToken={selectedPlayerRefresh}
            />
          ) : view === "matches" ? (
            <ActiveMatchesPanel channelId={selectedChannel.id} />
          ) : view === "commands" ? (
            <CommandListPanel />
          ) : view === "player" ? (
            <PlayerProfilePage
              channelId={selectedChannel.id}
              userId={route.userId}
              board={route.query.get("board") || "team"}
              members={options.data?.members || []}
              guildName={selectedChannel.guild_name || selectedChannel.guild_id}
              onBack={() => navigate(withQuery(`/c/${selectedChannel.id}/leaderboard`, { board: route.query.get("board") || "team" }))}
              onSaved={() => {
                setSelectedPlayerRefresh((value) => value + 1);
                refresh();
              }}
            />
          ) : (
            <div className="panel">
              <div className="panel-header">
                <div>
                  <h1>{selectedQueue ? selectedQueue.name : (target.kind === "tournament" ? (selectedTournamentChannel.channel_name || "Tournament settings") : (selectedChannel.channel_name || "Channel settings"))}</h1>
                  <p>{selectedQueue ? "Pickup queue configuration" : target.kind === "tournament" ? "Tournament registration channel configuration" : "Queue channel configuration"}</p>
                </div>
                <div className="panel-actions">
                  {selectedChannel && !selectedQueue && target.kind !== "tournament" ? <button className="button secondary" onClick={() => setModal({ type: "createQueue" })}>Create queue</button> : null}
                  {selectedQueue ? <button className="button danger" onClick={async () => {
                    await Api.send(`/api/channels/${selectedChannel.id}/queues/${selectedQueue.id}`, "DELETE");
                    navigate(`/c/${selectedChannel.id}`);
                    await refresh();
                  }}>Delete queue</button> : null}
                  {target?.item ? <button className="button danger" onClick={disableChannel}>Disable channel</button> : null}
                </div>
              </div>

              <div className="tabs">
                {(grouped.ordered || []).map((name) => (
                  <button key={name} className={`tab ${selectedSection === name ? "active" : ""}`} onClick={() => setSelectedSection(name)}>
                    {sectionIcons[name] || "\u25AA"} {name}
                  </button>
                ))}
              </div>

              <div className="editor">
                <SectionEditor
                  schema={schema}
                  sectionName={selectedSection}
                  fields={sectionFields}
                  draft={draft}
                  setDraft={setDraft}
                  resources={fieldResources}
                />
                <div className="save-row">
                  <button className="button" disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</button>
                  <div className="status-line">{status || `Changes apply to this ${selectedQueue ? "queue" : target.kind === "tournament" ? "tournament channel" : "channel"} only.`}</div>
                </div>
              </div>

              {target.kind === "tournament" ? (
                <div className="panel tournament-team-admin">
                  <div className="panel-header">
                    <div>
                      <h2>Tournament teams</h2>
                      <p>Create or delete teams for the active tournament registration.</p>
                    </div>
                    <div className="panel-actions">
                      <button className="button secondary" onClick={() => setModal({ type: "createTournamentTeam" })}>Create team</button>
                    </div>
                  </div>
                  {tournamentTeams.loading ? <div className="empty-panel">Loading teams...</div> : null}
                  {tournamentTeams.error ? <div className="empty-panel">{tournamentTeams.error}</div> : null}
                  {!tournamentTeams.loading && !tournamentTeams.error && !tournamentTeams.data?.tournament ? (
                    <div className="empty-panel">No active tournament yet. Create a team to start the registration set.</div>
                  ) : null}
                  <div className="tournament-team-grid">
                    {(tournamentTeams.data?.tournament?.teams || []).map((team) => (
                      <div key={team.id} className="team-mini-card admin-team-card">
                        <strong>{team.name}</strong>
                        <span>{team.players?.map((p) => p.name).join(" + ") || "Players pending"}</span>
                        <small>Registration combined server Elo {team.combined_elo ?? "?"}</small>
                        <small>Current combined server Elo {team.current_combined_server_elo ?? "?"}</small>
                        <button
                          className="button danger"
                          onClick={async () => {
                            if (!window.confirm(`Delete team "${team.name}"?`)) return;
                            await deleteTournamentTeam(team.id);
                          }}
                        >
                          Delete team
                        </button>
                      </div>
                    ))}
                    {tournamentTeams.data?.tournament && !(tournamentTeams.data?.tournament?.teams || []).length ? (
                      <div className="empty-panel">No teams registered yet.</div>
                    ) : null}
                  </div>
                  <div className="tournament-result-admin">
                    <div className="panel-header compact">
                      <div>
                        <h2>Pointboard results</h2>
                        <p>Choose the winner and enter each team&apos;s points. Saving updates the public standings immediately.</p>
                      </div>
                    </div>
                    {(tournamentTeams.data?.tournament?.matches || []).length ? (
                      <div className="tournament-result-grid">
                        {tournamentTeams.data.tournament.matches.map((match) => (
                          <TournamentMatchResultEditor key={match.id} match={match} onSave={reportTournamentMatch} />
                        ))}
                      </div>
                    ) : (
                      <div className="empty-panel">Match result controls will appear after the tournament is started.</div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </main>
      </div>

      {modal ? (
        <ActionModal
          modal={modal}
          onClose={() => setModal(null)}
          guilds={guilds}
          channels={channels}
          members={options.data?.members || []}
          onEnableChannel={enableChannel}
          onCreateQueue={createQueue}
          onCreateTournamentTeam={createTournamentTeam}
        />
      ) : null}
    </div>
  );
}

function LoginScreen({ auth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPassword = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      setInfo("Enter your email and password.");
      return;
    }
    setBusy(true);
    setInfo("");
    try {
      await auth.signInWithPassword({ email, password });
    } catch (error) {
      setInfo(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen-state login">
      <div className="login-card">
        <h1>AOE BD Admin</h1>
        <p>Sign in with your email and password to manage queues and player data.</p>
        {!auth.ready ? <div className="auth-alert">Auth is not configured. Set `SUPABASE_URL` and `SUPABASE_PUBLISHABLE_KEY` in `config.cfg`.</div> : null}
        {auth.error ? <div className="auth-alert">{auth.error}</div> : null}
        {info ? <div className="auth-note">{info}</div> : null}
        <form onSubmit={submitPassword}>
          <div style={{ display: "grid", gap: "10px" }}>
            <input className="input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            <input className="input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            <div className="modal-actions">
              <button className="button" type="submit" disabled={busy || !auth.ready}>
                {busy ? "Signing in..." : "Sign in"}
              </button>
            </div>
          </div>
        </form>
        <div className="auth-tabs">
          <button type="button" className="button secondary" style={{ width: "100%" }} onClick={() => navigate("/leaderboard")}>
            🏆 View public leaderboard
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminMapVoteCard({ item, onRefresh }) {
  const [candidates, setCandidates] = useState((item.candidates || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const act = async (path, method = "POST", body) => {
    setBusy(true);
    setMessage("");
    try {
      await Api.send(path, method, body);
      await onRefresh();
      setMessage("Saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  const label = new Date(Number(item.year), Number(item.month) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <section className="panel member-card admin-vote-card">
      <div className="member-section-head">
        <div><h2>{item.queue?.name} · {label}</h2><p>{item.state} · {item.turnout || 0} submitted ballots</p></div>
        <span className={`member-state state-${item.state}`}>{item.state}</span>
      </div>
      <div className="admin-vote-grid">
        <label>
          <span>Candidate maps (one per line)</span>
          <textarea value={candidates} onChange={(event) => setCandidates(event.target.value)} disabled={item.state !== "upcoming"} />
        </label>
        <div>
          <h3>Current results</h3>
          <div className="member-result-list">
            {(item.results || []).map((row) => <div key={row.map}><strong>{row.map}</strong><span>{row.preferred} votes · {row.bans} bans</span></div>)}
          </div>
        </div>
      </div>
      {item.final_maps?.length ? <p><strong>Final pool:</strong> {item.final_maps.join(", ")}</p> : null}
      {item.banned_maps?.length ? <p><strong>Community bans:</strong> {item.banned_maps.join(", ")}</p> : null}
      <div className="member-actions">
        <button className="button secondary" disabled={busy || item.state !== "upcoming"} onClick={() => act(`/api/admin/map-votes/${item.id}`, "PUT", { candidates: candidates.split(/\r?\n/).map((x) => x.trim()).filter(Boolean) })}>Save candidates</button>
        <button className="button" disabled={busy || !["closed", "finalized"].includes(item.state)} onClick={() => act(`/api/admin/map-votes/${item.id}/finalize`)}>Finalize results</button>
        <button className="button" disabled={busy || !["finalized", "applied"].includes(item.state)} onClick={() => act(`/api/admin/map-votes/${item.id}/apply`)}>Apply to queue</button>
      </div>
      {message ? <div className="auth-note">{message}</div> : null}
    </section>
  );
}

function AdminMapVotesPanel() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const refresh = async () => {
    setState((old) => ({ ...old, loading: true, error: null }));
    try {
      const data = await Api.get("/api/admin/map-votes");
      setState({ loading: false, data, error: null });
    } catch (error) {
      setState({ loading: false, data: null, error: error.message });
    }
  };
  useEffect(() => { refresh(); }, []);
  return (
    <div className="member-stack">
      <div className="panel-header"><div><h1>Monthly map votes</h1><p>Review turnout, configure candidates, finalize community bans, and apply the next lobby pool.</p></div></div>
      {state.loading ? <div className="panel empty-panel">Loading map votes...</div> : null}
      {state.error ? <div className="auth-alert">{state.error}</div> : null}
      {(state.data?.periods || []).map((item) => <AdminMapVoteCard key={item.id} item={item} onRefresh={refresh} />)}
    </div>
  );
}

function SectionEditor({ schema, sectionName, fields, draft, setDraft, resources }) {
  if (!schema || !fields.length) return <div className="empty-panel">No fields in this section.</div>;
  return (
    <div className="section">
      <h2>{sectionName}</h2>
      {fields.map((field) => (
        <FieldRow
          key={field.name}
          field={field}
          value={draft[field.name]}
          onChange={(value) => setDraft((prev) => ({ ...prev, [field.name]: value }))}
          resources={resources}
          onReset={() => setDraft((prev) => ({ ...prev, [field.name]: defaultDraft(field) }))}
          setDraft={setDraft}
        />
      ))}
    </div>
  );
}

function FieldRow({ field, value, onChange, resources, onReset, setDraft }) {
  return (
    <div className="field-row">
      <div className="field-label">
        <span>{field.display || field.name}</span>
        {field.description ? <span className="hint" title={field.description}>i</span> : null}
        <button className="tiny-button" onClick={onReset}>Reset</button>
      </div>
      <FieldControl field={field} value={value} onChange={onChange} resources={resources} setDraft={setDraft} />
      {field.description ? <div className="field-description">{field.description}</div> : null}
    </div>
  );
}

function FieldControl({ field, value, onChange, resources, setDraft }) {
  const kind = field.kind;
  if (kind === "TextVar") return <textarea className="input textarea" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (kind === "BoolVar") {
    const active = String(value) === "1" || value === 1 || value === true || String(value).toLowerCase() === "on";
    return (
      <div className="segmented">
        <button className={!active ? "active" : ""} onClick={() => onChange("0")}>X</button>
        <button className="neutral" onClick={() => onChange("0")}>/</button>
        <button className={active ? "active on" : "on"} onClick={() => onChange("1")}>V</button>
      </div>
    );
  }
  if (kind === "OptionVar") {
    return (
      <select className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (kind === "RoleVar") {
    return (
      <select className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {(resources.roles || []).map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
      </select>
    );
  }
  if (kind === "TextChanVar") {
    return (
      <select className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {(resources.text_channels || []).map((channel) => <option key={channel.id} value={channel.id}>#{channel.name}</option>)}
      </select>
    );
  }
  if (kind === "MemberVar") {
    return <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Member name or id" />;
  }
  if (kind === "SliderVar") {
    return <input className="input" type="number" value={value ?? ""} min={field.min_val ?? 0} max={field.max_val ?? 100} onChange={(e) => onChange(e.target.value)} />;
  }
  if (kind === "DurationVar") {
    const placeholder = isBlank(field.default) ? "HH:MM:SS" : fromDuration(field.default);
    return <input className="input" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
  }
  if (kind === "DateVar") {
    return <input className="input" type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (kind === "IntVar") {
    return <input className="input" type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (kind === "VariableTable") {
    const rows = Array.isArray(value) ? value : [];
    return (
      <div className="table-editor">
        <div className="table-head">
          {(field.columns || []).map((column) => <div key={column.name}>{column.display || column.name}</div>)}
          <div />
        </div>
        {rows.map((row, rowIndex) => (
          <div className="table-row" key={rowIndex}>
            {(field.columns || []).map((column) => (
              <div key={column.name}>
                <FieldControl
                  field={column}
                  value={row?.[column.name] ?? ""}
                  onChange={(next) => {
                    setDraft((prev) => {
                      const nextRows = [...(prev[field.name] || [])];
                      nextRows[rowIndex] = { ...nextRows[rowIndex], [column.name]: next };
                      return { ...prev, [field.name]: nextRows };
                    });
                  }}
                  resources={resources}
                  setDraft={setDraft}
                />
              </div>
            ))}
            <button
              className="remove"
              onClick={() => {
                setDraft((prev) => {
                  const nextRows = [...(prev[field.name] || [])];
                  nextRows.splice(rowIndex, 1);
                  return { ...prev, [field.name]: nextRows };
                });
              }}
            >
              \u00D7
            </button>
          </div>
        ))}
        <button
          className="button secondary"
          onClick={() => {
            const row = {};
            for (const column of field.columns || []) row[column.name] = defaultDraft(column);
            setDraft((prev) => ({ ...prev, [field.name]: [...(prev[field.name] || []), row] }));
          }}
        >
          add more
        </button>
      </div>
    );
  }
  return <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function LeaderboardPanel({ channelId, query: routeQuery, onQueryChange, onSelectPlayer, refreshToken }) {
  const board = routeQuery.get("board") || "team";
  const searchQuery = routeQuery.get("q") || "";
  const missingGameId = routeQuery.get("missing") === "1";
  const unlinkedOnly = routeQuery.get("unlinked") === "1";
  const page = Number(routeQuery.get("page") || 1);
  const sort = routeQuery.get("sort") || "matches";
  const setBoard = (b) => onQueryChange({ board: b, page: "" });
  const setSearchQuery = (q) => onQueryChange({ q });
  const setMissingGameId = (v) => onQueryChange({ missing: v ? "1" : "" });
  const setUnlinkedOnly = (v) => onQueryChange({ unlinked: v ? "1" : "" });
  const setSort = (s) => onQueryChange({ sort: s, page: "" });
  const setPage = (p) => onQueryChange({ page: p });
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [autoLink, setAutoLink] = useState({ running: false, result: null, error: null });
  const [localRefresh, setLocalRefresh] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    const sortParam = board === "teamcombos" ? `&sort=${sort}` : "";
    Api.get(`/api/channels/${channelId}/leaderboard?board=${board}&page=${page}${sortParam}`)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, data: null, error: error.message });
      });
    return () => { cancelled = true; };
  }, [channelId, board, page, sort, refreshToken, localRefresh]);

  const runAutoLink = async () => {
    setAutoLink({ running: true, result: null, error: null });
    try {
      const result = await Api.send(`/api/channels/${channelId}/players/auto-link?board=${board}`, "POST");
      setAutoLink({ running: false, result, error: null });
      setLocalRefresh((v) => v + 1);
    } catch (error) {
      setAutoLink({ running: false, result: null, error: error.message || "Auto-link failed." });
    }
  };

  const deletePlayer = async (row, event) => {
    event.stopPropagation();
    if (!confirm(`Delete ${row.nick}'s rating, history and match participation for this board? This can't be undone.`)) return;
    setDeleting(row.user_id);
    setDeleteError("");
    try {
      await Api.send(`/api/channels/${channelId}/players/${row.user_id}?board=${board}`, "DELETE");
      setLocalRefresh((v) => v + 1);
    } catch (error) {
      setDeleteError(error.message || "Failed to delete player.");
    } finally {
      setDeleting(null);
    }
  };

  const rows = (state.data?.rows || []).filter((row) => {
    if (searchQuery && !String(row.nick || row.member_names || "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (missingGameId && board !== "predictions" && board !== "teamcombos" && row.game_id) return false;
    if (unlinkedOnly && board !== "predictions" && board !== "teamcombos" && row.is_linked) return false;
    return true;
  });

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h1>Leaderboard</h1>
          <p>Live from the database</p>
        </div>
        {board !== "predictions" && board !== "teamcombos" ? (
          <button className="button secondary" disabled={autoLink.running} onClick={runAutoLink}>
            {autoLink.running ? "Linking..." : "Auto-link by nickname"}
          </button>
        ) : null}
      </div>
      <div className="tabs">
        {["team", "1v1", "predictions", "teamcombos"].map((b) => (
          <button key={b} className={`tab ${board === b ? "active" : ""}`} onClick={() => setBoard(b)}>{b === "teamcombos" ? "team combos" : b}</button>
        ))}
      </div>
      <div className="lb-toolbar">
        <input
          className="input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={board === "teamcombos" ? "Search player name" : "Search nickname"}
        />
        {board === "teamcombos" ? (
          <select className="input" value={sort} onChange={(e) => setSort(e.target.value)} style={{ flex: "none", width: "auto" }}>
            <option value="matches">Sort: highest matches together</option>
            <option value="win_rate">Sort: highest win rate</option>
            <option value="loss_rate">Sort: highest loss rate</option>
          </select>
        ) : null}
        {board !== "predictions" && board !== "teamcombos" ? (
          <label className="lb-filter-toggle">
            <input type="checkbox" checked={missingGameId} onChange={(e) => setMissingGameId(e.target.checked)} />
            Missing Game ID only
          </label>
        ) : null}
        {board !== "predictions" && board !== "teamcombos" ? (
          <label className="lb-filter-toggle">
            <input type="checkbox" checked={unlinkedOnly} onChange={(e) => setUnlinkedOnly(e.target.checked)} />
            Unlinked Discord id only
          </label>
        ) : null}
      </div>
      {autoLink.error ? <div className="empty-panel">{autoLink.error}</div> : null}
      {deleteError ? <div className="empty-panel">{deleteError}</div> : null}
      {autoLink.result ? (
        <div className="field-description">
          Linked <strong>{autoLink.result.linked.length}</strong> by matching nickname
          {autoLink.result.linked.length ? `: ${autoLink.result.linked.map((l) => l.nick).join(", ")}` : ""}.
          {autoLink.result.ambiguous.length ? ` Skipped ${autoLink.result.ambiguous.length} ambiguous (multiple members share that nickname): ${autoLink.result.ambiguous.join(", ")}.` : ""}
          {autoLink.result.no_match.length ? ` No matching member found for ${autoLink.result.no_match.length}: ${autoLink.result.no_match.join(", ")}.` : ""}
        </div>
      ) : null}
      {state.loading ? <div className="empty-panel">Loading...</div> : null}
      {state.error ? <div className="empty-panel">{state.error}</div> : null}
      {state.data && rows.length && board === "teamcombos" ? (
        <table className="lb-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Team</th>
              <th>Matches</th>
              <th>W/L/D</th>
              <th>Win rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rank} className={`rank-tier-${row.rank <= 3 ? row.rank : 0}`}>
                <td>{row.rank}</td>
                <td>{row.member_names}</td>
                <td>{row.matches}</td>
                <td>{row.wins}/{row.losses}/{row.draws}</td>
                <td>{row.win_rate != null ? <WinRateBar value={row.win_rate} /> : "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {state.data && rows.length && board !== "teamcombos" ? (
        <table className="lb-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Nickname</th>
              {board === "predictions" ? null : <th>Game ID</th>}
              {board === "predictions" ? <th>Points</th> : <th>Rating</th>}
              {board === "predictions" ? null : <th>W/L/D</th>}
              {board === "predictions" ? null : <th>Win rate</th>}
              {board === "predictions" ? null : <th>Discord</th>}
              {board === "predictions" ? null : <th></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const winRate = row.wins + row.losses > 0 ? row.wins / (row.wins + row.losses) : null;
              return (
                <tr
                  key={row.user_id}
                  className={`rank-tier-${row.rank <= 3 ? row.rank : 0}`}
                  onClick={() => board !== "predictions" && onSelectPlayer?.({ ...row, board })}
                >
                  <td>{row.rank}</td>
                  <td>{row.nick}</td>
                  {board === "predictions" ? null : <td>{row.game_id || "\u2014"}</td>}
                  {board === "predictions"
                    ? <td>{row.points >= 0 ? "+" : ""}{row.points}</td>
                    : <>
                        <td>{row.rank_str ? <span className="rank-badge">{row.rank_str}</span> : null}{row.rating ?? "?"}</td>
                        <td>{row.wins}/{row.losses}/{row.draws}</td>
                        <td>{winRate != null ? <WinRateBar value={winRate} /> : "\u2014"}</td>
                      </>
                  }
                  {board === "predictions" ? null : (
                    <td>{row.is_linked ? <span className="link-badge linked">linked</span> : <span className="link-badge unlinked">unlinked</span>}</td>
                  )}
                  {board === "predictions" ? null : (
                    <td>
                      <button
                        className="button danger tiny-danger"
                        disabled={deleting === row.user_id}
                        onClick={(e) => deletePlayer(row, e)}
                        title="Delete this player's rating/history for this board"
                      >
                        {deleting === row.user_id ? "..." : "Delete"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {board !== "predictions" && board !== "teamcombos" ? <div className="table-hint">Click a player row to view their full profile.</div> : null}
      {state.data ? <Pagination page={state.data.page || 1} pages={state.data.pages || 1} onChange={setPage} /> : null}
    </div>
  );
}

const MATCH_STATE_LABELS = {
  init: "Initializing",
  check_in: "Check-in",
  draft: "Draft",
  waiting_report: "Waiting on report",
};

function ActiveMatchesPanel({ channelId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [reporting, setReporting] = useState(null); // matchId currently being reported or canceled
  const [reportError, setReportError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    Api.get(`/api/channels/${channelId}/active-matches`)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [channelId, refreshToken]);

  const report = async (matchId, winner) => {
    setReporting(matchId);
    setReportError("");
    try {
      await Api.send(`/api/channels/${channelId}/active-matches/${matchId}/report`, "POST", { winner });
      setRefreshToken((v) => v + 1);
    } catch (error) {
      setReportError(error.message || "Failed to report match.");
    } finally {
      setReporting(null);
    }
  };

  const cancelMatch = async (matchId) => {
    if (!confirm(`Cancel match ${matchId}? This can't be undone.`)) return;
    setReporting(matchId);
    setReportError("");
    try {
      await Api.send(`/api/channels/${channelId}/active-matches/${matchId}/cancel`, "POST", {});
      setRefreshToken((v) => v + 1);
    } catch (error) {
      setReportError(error.message || "Failed to cancel match.");
    } finally {
      setReporting(null);
    }
  };

  const matches = state.data?.matches || [];

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h1>Active Matches</h1>
          <p>
            All matches currently in progress. Reporting or canceling here posts the result in
            Discord too. Stuck matches (e.g. draft/check-in that never resolves) can be canceled
            directly.
          </p>
        </div>
      </div>
      {reportError ? <div className="empty-panel">{reportError}</div> : null}
      {state.loading ? <div className="empty-panel">Loading...</div> : null}
      {state.error ? <div className="empty-panel">{state.error}</div> : null}
      {state.data && !matches.length ? <div className="empty-panel">No active matches right now.</div> : null}
      {matches.map((m) => (
        <div key={m.id} className="active-match-card">
          <div className="active-match-head">
            <strong>{m.queue_name}</strong>
            <span className="field-suffix">Match id: {m.id}</span>
            <span className="field-suffix">{MATCH_STATE_LABELS[m.state] || m.state}</span>
            {m.maps?.length ? <span className="field-suffix">{m.maps.join(", ")}</span> : null}
          </div>
          {m.teams.some((t) => t.players.length) ? (
            <div className="active-match-teams">
              {m.teams.map((team, idx) => (
                <div key={idx} className="active-match-team">
                  <div className="active-match-team-head">
                    <span>{team.emoji} <strong>{team.name}</strong></span>
                    {team.avg_rating != null ? <span className="field-suffix">avg {team.avg_rating}</span> : null}
                  </div>
                  <div className="field-description">{team.players.map((p) => p.nick).join(", ") || "empty"}</div>
                  {m.state === "waiting_report" ? (
                    <button
                      className="button secondary"
                      disabled={reporting === m.id}
                      onClick={() => report(m.id, idx)}
                    >
                      {reporting === m.id ? "Reporting..." : `Report ${team.name} won`}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="field-description">
              Players: {m.players.map((p) => p.nick).join(", ") || "none"}
            </div>
          )}
          <div className="active-match-teams">
            {m.state === "waiting_report" ? (
              <button className="button secondary" disabled={reporting === m.id} onClick={() => report(m.id, null)}>
                {reporting === m.id ? "Reporting..." : "Report draw"}
              </button>
            ) : null}
            <button className="button danger" disabled={reporting === m.id} onClick={() => cancelMatch(m.id)}>
              {reporting === m.id ? "Working..." : "Cancel match"}
            </button>
          </div>
        </div>
      ))}

      <h2 className="section-subhead">Recently completed</h2>
      {state.data && !(state.data.recent || []).length ? <div className="empty-panel">No matches reported yet.</div> : null}
      <div className="match-card-list">
        {(state.data?.recent || []).map((m) => <MatchCard key={`${m.source}-${m.match_id}`} match={m} />)}
      </div>
    </div>
  );
}

function StatTable({ title, rows, nameLabel, onRowClick }) {
  if (!rows?.length) {
    return (
      <section className="stat-table-section">
        {title ? <h2>{title}</h2> : null}
        <div className="empty-panel">No data yet.</div>
      </section>
    );
  }
  return (
    <section className="stat-table-section">
      {title ? <h2>{title} <span className="field-suffix">{rows.length}</span></h2> : null}
      <table className="stat-table">
        <thead>
          <tr>
            <th>{nameLabel}</th>
            <th>Matches</th>
            <th>W/L/D</th>
            <th>Win rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.key || row.profile_id || idx} className={onRowClick ? "clickable-row" : ""} onClick={() => onRowClick?.(row)}>
              <td>{analyticsLabel(row)}</td>
              <td>{formatCount(row.matches)}</td>
              <td>{formatCount(row.wins)}/{formatCount(row.losses)}/{formatCount(row.draws)}</td>
              <td>{row.win_rate != null ? <WinRateBar value={row.win_rate} /> : "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ProfileTabs({ matches, civs, maps, teammates, opponents }) {
  const [tab, setTab] = useState("matches");
  const tabs = [
    ["matches", `Recent matches (${matches?.length || 0})`],
    ["civs", `Civilizations (${civs?.length || 0})`],
    ["maps", `Maps (${maps?.length || 0})`],
    ["teammates", `Teammates (${teammates?.length || 0})`],
    ["opponents", `Opponents (${opponents?.length || 0})`],
  ];
  return (
    <section>
      <div className="tabs">
        {tabs.map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? "active" : ""}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === "matches" ? (
        matches?.length ? (
          <div className="match-card-list">
            {matches.map((m) => <MatchCard key={`${m.source}-${m.match_id}`} match={m} onClick={() => navigate(`/match/${m.source}/${m.match_id}`)} />)}
          </div>
        ) : <div className="empty-panel">No recent matches found.</div>
      ) : null}
      {tab === "civs" ? <StatTable title="" rows={civs} nameLabel="Civilization" /> : null}
      {tab === "maps" ? <StatTable title="" rows={maps} nameLabel="Map" /> : null}
      {tab === "teammates" ? <StatTable title="" rows={teammates} nameLabel="Teammate" /> : null}
      {tab === "opponents" ? <StatTable title="" rows={opponents} nameLabel="Opponent" /> : null}
    </section>
  );
}

function RecentForm({ matches }) {
  const recent = (matches || []).filter((m) => m.won !== null && m.won !== undefined).slice(0, 10);
  if (!recent.length) return null;
  return (
    <div className="recent-form">
      <span className="recent-form-label">Recent form</span>
      <div className="recent-form-badges">
        {recent.map((m, idx) => (
          <span
            key={`${m.source}-${m.match_id}-${idx}`}
            className={`recent-form-badge ${m.won ? "win" : "loss"}`}
            title={`${m.won ? "Win" : "Loss"} \u00B7 ${m.map_name || ""} \u00B7 ${formatAgo(m.at) || ""}`}
          >
            {m.won ? "W" : "L"}
          </span>
        ))}
      </div>
    </div>
  );
}

function EloHistory({ entries }) {
  if (!entries?.length) return null;
  return (
    <section className="profile-section">
      <div className="profile-section-head">
        <div>
          <h2>All Elo changes</h2>
          <p>{entries.length} recorded rating updates, including matches and decay.</p>
        </div>
      </div>
      <div className="elo-history">
        <div className="elo-history-head" role="row">
          <span>Change</span>
          <span>Before / After</span>
          <span>Time period</span>
          <span>Reason</span>
        </div>
        <div className="elo-history-list">
          {entries.map((entry) => {
            const delta = Number(entry.rating_change || 0);
            const before = entry.rating_before != null ? formatElo(entry.rating_before) : "—";
            const after = entry.rating_after != null ? formatElo(entry.rating_after) : "—";
            const reason = entry.reason || "unknown";
            const sign = delta > 0 ? "+" : "";
            return (
              <div key={entry.id} className="elo-history-row">
                <strong className={delta > 0 ? "up" : delta < 0 ? "down" : "flat"}>{sign}{delta}</strong>
                <span>{before} → {after}</span>
                <span title={entry.at ? new Date(entry.at * 1000).toLocaleString() : ""}>{formatAgoPrecise(entry.at) || "—"}</span>
                <span>{reason}{entry.match_id != null ? `(${entry.match_id})` : ""}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---- Game analysis (bot/replays/profile_stats.py): scores from analysed recorded games, trends, breakdowns ----

const GA_METRICS = [
  { key: "overall", label: "Overall score", kind: "score" },
  { key: "execution", label: "Execution", kind: "score" },
  { key: "pressure", label: "Pressure", kind: "score" },
  { key: "survival", label: "Holding position", kind: "score" },
  { key: "team", label: "Team play", kind: "score" },
  { key: "elo_delta", label: "Score vs Elo", kind: "delta" },
  { key: "apm", label: "APM", kind: "count" },
  { key: "castle_s", label: "Castle Age time", kind: "time", lowerIsBetter: true },
  { key: "imperial_s", label: "Imperial Age time", kind: "time", lowerIsBetter: true },
];
const GA_ROLLING = 10;

function gaTime(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return "—";
  const s = Math.round(Number(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function gaValue(value, kind) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  if (kind === "time") return gaTime(n);
  if (kind === "count") return String(Math.round(n));
  if (kind === "delta") return `${n > 0 ? "+" : ""}${n.toFixed(1)}`;
  return n.toFixed(1);
}

function gaDate(unix) {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function GaTrendChart({ series }) {
  const [metricKey, setMetricKey] = useState("overall");
  const [hover, setHover] = useState(null);
  const metric = GA_METRICS.find((m) => m.key === metricKey) || GA_METRICS[0];
  const points = (series || [])
    .map((g, i) => ({ ...g, idx: i, value: g[metric.key] == null ? null : Number(g[metric.key]) }))
    .filter((g) => g.value !== null && Number.isFinite(g.value));
  const W = 720, H = 240, padL = 44, padR = 16, padT = 14, padB = 28;
  if (points.length < 2) {
    return <div className="empty-panel">Not enough analysed games with {metric.label.toLowerCase()} yet.</div>;
  }
  const values = points.map((p) => p.value);
  let lo = Math.min(...values), hi = Math.max(...values);
  if (metric.kind === "score") { lo = Math.min(lo, 1); hi = Math.max(hi, 10); }
  if (hi === lo) { hi += 1; lo -= 1; }
  const x = (i) => padL + (i / Math.max(1, points.length - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const rolling = points.map((_, i) => {
    const window = points.slice(Math.max(0, i - GA_ROLLING + 1), i + 1);
    return window.reduce((sum, p) => sum + p.value, 0) / window.length;
  });
  const linePath = rolling.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => lo + t * (hi - lo));
  const zeroLine = metric.kind === "delta" && lo < 0 && hi > 0 ? y(0) : null;
  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((px - padL) / (W - padL - padR)) * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };
  const h = hover !== null ? points[hover] : null;
  return (
    <div className="ga-chart">
      <div className="ga-chart-head">
        <label className="ga-select">
          <span>Metric</span>
          <select value={metricKey} onChange={(e) => { setMetricKey(e.target.value); setHover(null); }}>
            {GA_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>
        <div className="ga-legend">
          <span><i className="ga-key-dot filled" /> Won</span>
          <span><i className="ga-key-dot" /> Lost</span>
          <span><i className="ga-key-line" /> {GA_ROLLING}-game average</span>
          {metric.lowerIsBetter ? <span className="ga-legend-note">lower is better</span> : null}
        </div>
      </div>
      <div className="ga-chart-frame">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${metric.label} per analysed game, oldest to newest`}
          onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className="ga-gridline" />
              <text x={padL - 8} y={y(t) + 4} className="ga-axis" textAnchor="end">{gaValue(t, metric.kind)}</text>
            </g>
          ))}
          {zeroLine !== null ? <line x1={padL} x2={W - padR} y1={zeroLine} y2={zeroLine} className="ga-zero" /> : null}
          <text x={padL} y={H - 8} className="ga-axis">{gaDate(points[0].played_at)}</text>
          <text x={W - padR} y={H - 8} className="ga-axis" textAnchor="end">{gaDate(points[points.length - 1].played_at)}</text>
          {points.map((p, i) => (
            <circle key={p.game_key} cx={x(i)} cy={y(p.value)} r={hover === i ? 5.5 : 4}
              className={`ga-dot ${p.won ? "won" : "lost"}`} />
          ))}
          <path d={linePath} className="ga-line" />
          {h ? <line x1={x(hover)} x2={x(hover)} y1={padT} y2={H - padB} className="ga-crosshair" /> : null}
        </svg>
        {h ? (
          <div className="ga-tooltip" style={{ left: `${(x(hover) / W) * 100}%` }}>
            <strong>{gaValue(h.value, metric.kind)}</strong> {metric.label.toLowerCase()}
            <span>{gaDate(h.played_at)} · {h.won ? "Won" : "Lost"}</span>
            <span>{h.civ || "?"}{h.seat ? ` · ${h.seat.toLowerCase()}` : ""} · {h.map || "?"}</span>
            <span>{GA_ROLLING}-game average: {gaValue(rolling[hover], metric.kind)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function GaGroupTable({ title, rows, empty }) {
  return (
    <div className="ga-table-block">
      <h3>{title}</h3>
      {rows?.length ? (
        <table className="stat-table">
          <thead><tr><th>Name</th><th>Games</th><th>Win %</th><th>Avg score</th><th>Vs Elo</th></tr></thead>
          <tbody>
            {rows.slice(0, 10).map((r) => (
              <tr key={r.name}>
                <td>{decodeEscapedText(r.name)}</td>
                <td>{r.games}</td>
                <td>{r.win_rate}%</td>
                <td>{gaValue(r.avg_score, "score")}</td>
                <td>{gaValue(r.avg_elo_delta, "delta")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="empty-panel">{empty}</div>}
    </div>
  );
}

function GaGameList({ title, games }) {
  if (!games?.length) return null;
  return (
    <div className="ga-table-block">
      <h3>{title}</h3>
      <table className="stat-table">
        <thead><tr><th>Date</th><th>Result</th><th>Civ</th><th>Map</th><th>Score</th><th>Vs Elo</th><th>Strategy</th><th /></tr></thead>
        <tbody>
          {games.map((g) => (
            <tr key={g.game_key}>
              <td>{gaDate(g.played_at)}</td>
              <td className={g.won ? "ga-won" : "ga-lost"}>{g.won ? "Won" : "Lost"}</td>
              <td>{g.civ || "—"}{g.seat ? <small> · {g.seat.toLowerCase()}</small> : null}</td>
              <td>{g.map || "—"}</td>
              <td>{gaValue(g.overall, "score")}{g.rank ? <small> (#{g.rank})</small> : null}</td>
              <td>{gaValue(g.elo_delta, "delta")}</td>
              <td className="ga-strategy">{g.strategy || "—"}</td>
              <td>{g.post_url ? <a href={g.post_url} target="_blank" rel="noreferrer">Breakdown</a> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GameAnalysisSection({ analysis }) {
  if (!analysis?.summary) {
    return <div className="empty-panel">No analysed games yet. Games are analysed from recorded games after matches and with /replay upload.</div>;
  }
  const s = analysis.summary;
  const recent = [...(analysis.series || [])].reverse().slice(0, 20);
  const topStyle = s.playstyles?.[0];
  return (
    <div className="ga-section">
      <div className="analytics-grid profile-metric-grid">
        <AnalyticsCard label="Games analysed" value={formatCount(s.games)} note={`${s.wins}W / ${s.losses}L · ${s.win_rate}% won`} />
        <AnalyticsCard label="Average overall score" value={gaValue(s.avg_overall, "score")} note="1-10, relative to each lobby" />
        <AnalyticsCard label="Execution / Pressure" value={`${gaValue(s.avg_exec, "score")} / ${gaValue(s.avg_pressure, "score")}`} note="Average category scores" />
        <AnalyticsCard label="Holding / Team play" value={`${gaValue(s.avg_survival, "score")} / ${gaValue(s.avg_team, "score")}`} note="Average category scores" />
        <AnalyticsCard label="Against their Elo" value={gaValue(s.avg_elo_delta, "delta")} note={`${s.above_elo} games above · ${s.below_elo} below their Elo`} />
        <AnalyticsCard label="Average APM" value={gaValue(s.avg_apm, "count")} note="Actions per minute" />
        <AnalyticsCard label="Age-up times" value={`${gaTime(s.avg_feudal_s)} · ${gaTime(s.avg_castle_s)} · ${gaTime(s.avg_imperial_s)}`} note="Average Feudal · Castle · Imperial" />
        <AnalyticsCard label="Playstyle" value={topStyle ? topStyle.name : "—"} note={topStyle ? `${topStyle.games} of ${s.games} games` : "Macro / micro label per game"} />
      </div>

      <div className="ga-block">
        <h3>How they are improving</h3>
        {analysis.trend?.length ? (
          <table className="stat-table ga-trend">
            <thead><tr><th>Measure</th><th>Earlier games</th><th>Recent games</th><th>Change</th></tr></thead>
            <tbody>
              {analysis.trend.map((t) => {
                const kind = GA_METRICS.find((m) => m.key === t.key)?.kind || (/_s$/.test(t.key) ? "time" : "score");
                const status = t.improving === null ? "same" : t.improving ? "better" : "worse";
                const change = kind === "time" ? `${t.change > 0 ? "+" : t.change < 0 ? "−" : ""}${gaTime(Math.abs(t.change))}` : gaValue(t.change, "delta");
                return (
                  <tr key={t.key}>
                    <td>{t.label}{t.lower_is_better ? <small> (lower is better)</small> : null}</td>
                    <td>{gaValue(t.earlier, kind)}</td>
                    <td>{gaValue(t.recent, kind)}</td>
                    <td className={`ga-change ${status}`}>
                      <span aria-hidden="true">{status === "better" ? "▲" : status === "worse" ? "▼" : "•"}</span> {change} {status === "same" ? "" : status}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <div className="empty-panel">Trends show once 6 or more games are analysed.</div>}
      </div>

      <div className="ga-block">
        <h3>Over time</h3>
        <GaTrendChart series={analysis.series} />
      </div>

      <div className="ga-grid">
        <GaGroupTable title="Civilizations" rows={analysis.civs} empty="No civ played twice yet." />
        <GaGroupTable title="Maps" rows={analysis.maps} empty="No map played twice yet." />
        <GaGroupTable title="Map types" rows={analysis.map_types} empty="No map type played twice yet." />
        <GaGroupTable title="Positions" rows={analysis.seats} empty="No position data yet." />
        <GaGroupTable title="Openings" rows={analysis.openings} empty="No opening played twice yet." />
      </div>

      <GaGameList title="Best games" games={analysis.best_games} />
      <GaGameList title="Recent analysed games" games={recent} />
    </div>
  );
}

function ProfileInsightTabs({ lifetimeEntries, ladderCards, replayCards, gameAnalysis }) {
  const [tab, setTab] = useState(gameAnalysis?.summary ? "analysis" : "overview");
  const reseedEntries = (lifetimeEntries || []).filter((entry) => /seed|reset|snap|manual/i.test(String(entry.reason || "")));
  const overviewCards = ladderCards.length ? ladderCards : [];
  const hasReplay = replayCards.length > 0;
  return (
    <section className="profile-section">
      <div className="tabs profile-insight-tabs">
        {[
          ["analysis", `Game analysis (${gameAnalysis?.summary?.games || 0})`],
          ["overview", `Overview (${overviewCards.length})`],
          ["replay", `Replay insights (${replayCards.length})`],
          ["lifetime", `Lifetime Elo (${lifetimeEntries?.length || 0})`],
        ].map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? "active" : ""}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {tab === "analysis" ? <GameAnalysisSection analysis={gameAnalysis} /> : null}

      {tab === "overview" ? (
        overviewCards.length ? (
          <div className="analytics-grid profile-metric-grid">{overviewCards}</div>
        ) : <div className="empty-panel">No ladder data yet.</div>
      ) : null}

      {tab === "replay" ? (
        hasReplay ? (
          <div className="analytics-grid profile-metric-grid profile-metric-grid-wide">{replayCards}</div>
        ) : <div className="empty-panel">No replay insights yet.</div>
      ) : null}

      {tab === "lifetime" ? (
        <>
          <div className="analytics-grid profile-metric-grid">
            <AnalyticsCard label="Lifetime changes" value={formatCount(lifetimeEntries?.length)} note="All recorded Elo updates" />
            <AnalyticsCard label="Reseed / reset events" value={formatCount(reseedEntries.length)} note="Manual seeding, resets, and snap events" />
          </div>
          <EloHistory entries={lifetimeEntries} />
        </>
      ) : null}
    </section>
  );
}

function useSeoMetadata(meta) {
  useEffect(() => {
    if (typeof document === "undefined" || !meta) return undefined;
    const previousTitle = document.title;
    const touched = [];

    const setNode = (selector, tagName, attrs, content) => {
      let node = document.head.querySelector(selector);
      const created = !node;
      if (!node) {
        node = document.createElement(tagName);
        document.head.appendChild(node);
      }
      const previous = {};
      for (const [key] of Object.entries(attrs)) {
        previous[key] = node.getAttribute(key);
      }
      touched.push({ node, created, previous, tagName, content: tagName === "script" ? node.textContent : null });
      for (const [key, value] of Object.entries(attrs)) {
        if (value === null || value === undefined || value === "") node.removeAttribute(key);
        else node.setAttribute(key, value);
      }
      if (tagName === "script") node.textContent = content || "";
    };

    document.title = meta.title || previousTitle;

    if (meta.description) setNode('meta[name="description"]', "meta", { name: "description", content: meta.description });
    if (meta.keywords) setNode('meta[name="keywords"]', "meta", { name: "keywords", content: meta.keywords });
    if (meta.robots) setNode('meta[name="robots"]', "meta", { name: "robots", content: meta.robots });
    if (meta.themeColor) setNode('meta[name="theme-color"]', "meta", { name: "theme-color", content: meta.themeColor });
    if (meta.applicationName) setNode('meta[name="application-name"]', "meta", { name: "application-name", content: meta.applicationName });
    if (meta.author) setNode('meta[name="author"]', "meta", { name: "author", content: meta.author });
    if (meta.aiSummary) setNode('meta[name="ai-summary"]', "meta", { name: "ai-summary", content: meta.aiSummary });
    if (meta.aiTopics) setNode('meta[name="ai-topics"]', "meta", { name: "ai-topics", content: meta.aiTopics });

    if (meta.canonical) setNode('link[rel="canonical"]', "link", { rel: "canonical", href: meta.canonical });
    if (meta.image) {
      setNode('meta[property="og:image"]', "meta", { property: "og:image", content: meta.image });
      setNode('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: meta.image });
    }
    if (meta.title) {
      setNode('meta[property="og:title"]', "meta", { property: "og:title", content: meta.title });
      setNode('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: meta.title });
    }
    if (meta.description) {
      setNode('meta[property="og:description"]', "meta", { property: "og:description", content: meta.description });
      setNode('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: meta.description });
    }
    if (meta.canonical) setNode('meta[property="og:url"]', "meta", { property: "og:url", content: meta.canonical });
    if (meta.type) setNode('meta[property="og:type"]', "meta", { property: "og:type", content: meta.type });
    if (meta.jsonLd) setNode('script[data-seo="player-jsonld"]', "script", { type: "application/ld+json", "data-seo": "player-jsonld" }, JSON.stringify(meta.jsonLd).replace(/</g, "\\u003c"));

    return () => {
      document.title = previousTitle;
      for (const entry of touched.reverse()) {
        if (entry.created) {
          entry.node.remove();
          continue;
        }
        if (entry.tagName === "script") {
          entry.node.textContent = entry.content || "";
          continue;
        }
        for (const [key, value] of Object.entries(entry.previous)) {
          if (value === null || value === undefined) entry.node.removeAttribute(key);
          else entry.node.setAttribute(key, value);
        }
      }
    };
  }, [
    meta?.title,
    meta?.description,
    meta?.keywords,
    meta?.robots,
    meta?.canonical,
    meta?.image,
    meta?.type,
    meta?.themeColor,
    meta?.applicationName,
    meta?.author,
    meta?.aiSummary,
    meta?.aiTopics,
    meta?.jsonLd ? JSON.stringify(meta.jsonLd) : "",
  ]);
}

function PlayerProfileSummary({ p, board = "team" }) {
  const boardLabel = board === "1v1" ? "1v1" : "team";
  const playerName = decodeEscapedText(p.nick) || "Unknown player";
  const companionProfileUrl = p.analytics?.profile_id ? `https://www.aoe2companion.com/players/${p.analytics.profile_id}` : null;
  const wins = Number(p.wins || 0);
  const losses = Number(p.losses || 0);
  const draws = Number(p.draws || 0);
  const winRate = wins + losses > 0 ? wins / (wins + losses) : null;
  const serverAverageElo = p.rating != null && p.highest_server_team_rating != null
    ? Math.round((Number(p.rating) + Number(p.highest_server_team_rating)) / 2)
    : null;
  const analytics = p.analytics;
  const ladderCards = [];
  const replayCards = [];

  if (analytics) {
    ladderCards.push(<AnalyticsCard key="current-1v1" label="Current 1v1 Elo" value={formatCount(analytics.current_1v1_rating) || "\u2014"} note={analytics.current_1v1_name || "AoE2Companion current"} />);
    ladderCards.push(<AnalyticsCard key="highest-1v1" label="Highest 1v1 Elo" value={formatCount(analytics.highest_1v1_rating) || "\u2014"} note={analytics.highest_1v1_name || "AoE2Companion peak"} />);
    ladderCards.push(<AnalyticsCard key="current-team" label="Current team Elo" value={formatCount(analytics.current_team_rating) || "\u2014"} note={analytics.current_team_name || "AoE2Companion current"} />);
    ladderCards.push(<AnalyticsCard key="highest-team" label="Highest team Elo" value={formatCount(analytics.highest_team_rating) || "\u2014"} note={analytics.highest_team_name || "AoE2Companion peak"} />);
  }

  if (p.highest_server_team_rating != null) {
    ladderCards.push(<AnalyticsCard key="server-team-peak" label="Highest server team Elo" value={formatCount(p.highest_server_team_rating)} note="Highest server rating ever reached in team games" />);
  }
  if (p.server_team_matches != null) {
    ladderCards.push(<AnalyticsCard key="server-team-matches" label="Server team matches" value={formatCount(p.server_team_matches)} note="All internal non-1v1 team games" />);
  }
  if (serverAverageElo != null) {
    ladderCards.push(<AnalyticsCard key="server-team-average" label="Server average Elo" value={formatCount(serverAverageElo)} note="(current server + highest server) / 2" />);
  }

  if (analytics) {
    ladderCards.push(<AnalyticsCard key="best-civ" label="Best civ" value={analyticsLabel(analytics.best_civ)} note={`${formatCount(analytics.best_civ?.matches)} matches \u00B7 ${formatWinRate(analytics.best_civ?.win_rate)}`} />);
    ladderCards.push(<AnalyticsCard key="best-map" label="Best map" value={analyticsLabel(analytics.best_map)} note={`${formatCount(analytics.best_map?.matches)} matches \u00B7 ${formatWinRate(analytics.best_map?.win_rate)}`} />);
    ladderCards.push(<AnalyticsCard key="best-teammate" label="Best teammate" value={analyticsLabel(analytics.best_teammate)} note={`${formatCount(analytics.best_teammate?.matches)} matches \u00B7 ${formatWinRate(analytics.best_teammate?.win_rate)}`} />);
    ladderCards.push(<AnalyticsCard key="best-opponent" label="Best opponent" value={analyticsLabel(analytics.best_opponent)} note={`${formatCount(analytics.best_opponent?.matches)} matches \u00B7 ${formatWinRate(analytics.best_opponent?.win_rate)}`} />);
    ladderCards.push(<AnalyticsCard key="matches" label="Matches analyzed" value={formatCount(analytics.overall?.matches) || "0"} note="AoE2Companion sync" />);
    ladderCards.push(<AnalyticsCard key="synced" label="Last synced" value={formatSyncedAt(analytics.last_synced_at) || "\u2014"} note="AoE2Companion analytics" />);

    if (analytics.avg_eapm != null) {
      replayCards.push(<AnalyticsCard key="avg-eapm" label="Avg EAPM" value={formatMetricValue(analytics.avg_eapm, 1) || "\u2014"} note="Blended across synced replays" />);
    }
    if (analytics.peak_eapm != null) {
      replayCards.push(<AnalyticsCard key="peak-eapm" label="Peak EAPM" value={formatMetricValue(analytics.peak_eapm, 1) || "\u2014"} note={analytics.peak_eapm_minute != null ? `Highest burst at minute ${analytics.peak_eapm_minute}` : "Best minute spike"} />);
    }
    if (analytics.avg_eapm_early != null || analytics.avg_eapm_mid != null || analytics.avg_eapm_late != null) {
      replayCards.push(
        <AnalyticsCard
          key="eapm-split"
          label="EAPM split"
          value={[analytics.avg_eapm_early, analytics.avg_eapm_mid, analytics.avg_eapm_late]
            .map((value) => (value != null ? formatMetricValue(value, 1) : "—"))
            .join(" / ")}
          note="Early / mid / late game"
        />,
      );
    }
    if (analytics.eapm_trend != null) {
      replayCards.push(<AnalyticsCard key="eapm-trend" label="EAPM trend" value={formatSignedNumber(analytics.eapm_trend, 1) || "\u2014"} note="Positive means the player sped up later in the game" />);
    }
    if (analytics.economy_score != null) {
      replayCards.push(<AnalyticsCard key="eco-score" label="Economy score" value={formatCount(analytics.economy_score) || "\u2014"} note="In-game eco impact" />);
    }
    if (analytics.eco_score_per_min != null) {
      replayCards.push(<AnalyticsCard key="eco-rate" label="Eco score / min" value={formatMetricValue(analytics.eco_score_per_min, 1) || "\u2014"} note="Higher means cleaner macro" />);
    }
    if (analytics.gather_rate_per_min != null) {
      replayCards.push(<AnalyticsCard key="gather-rate" label="Gather rate / min" value={formatMetricValue(analytics.gather_rate_per_min, 1) || "\u2014"} note="Combined resource intake per minute" />);
    }
    if (analytics.tc_idle_estimate_s != null) {
      replayCards.push(<AnalyticsCard key="tc-idle" label="TC idle estimate" value={formatDuration(analytics.tc_idle_estimate_s) || "\u2014"} note={analytics.tc_idle_ratio != null ? `Idle share ${formatRatio(analytics.tc_idle_ratio)}` : "Estimated from villager timing"} />);
    }
    if (analytics.villager_efficiency != null) {
      replayCards.push(<AnalyticsCard key="villager-efficiency" label="Villager efficiency" value={formatRatio(analytics.villager_efficiency) || "\u2014"} note="Actual villagers against expected timing" />);
    }
    if (analytics.resource_bank_avg != null || analytics.resource_bank_peak != null || analytics.resource_bank_min != null) {
      replayCards.push(<AnalyticsCard key="resource-bank" label="Resource bank" value={[analytics.resource_bank_avg, analytics.resource_bank_peak, analytics.resource_bank_min].map((value) => (value != null ? formatCount(value) : "—")).join(" / ")} note="Avg / peak / min stored resources" />);
    }
    if (analytics.food_collected != null || analytics.wood_collected != null || analytics.stone_collected != null || analytics.gold_collected != null) {
      replayCards.push(<AnalyticsCard key="resource-mix" label="Resource mix" value={[analytics.food_collected, analytics.wood_collected, analytics.stone_collected, analytics.gold_collected].map((value) => (value != null ? formatCount(value) : "—")).join(" / ")} note="Food / wood / stone / gold collected" />);
    }
    if (analytics.total_resources_collected != null) {
      replayCards.push(<AnalyticsCard key="resources-total" label="Total resources" value={formatCount(analytics.total_resources_collected) || "\u2014"} note="Sum of all collected resources" />);
    }
    if (analytics.trade_gold != null || analytics.relic_gold != null) {
      replayCards.push(<AnalyticsCard key="bonus-income" label="Bonus income" value={[analytics.trade_gold, analytics.relic_gold].map((value) => (value != null ? formatCount(value) : "—")).join(" / ")} note="Trade gold / relic gold" />);
    }
    if (analytics.opening_strategy) {
      replayCards.push(<AnalyticsCard key="opening" label="Opening strategy" value={decodeEscapedText(analytics.opening_strategy)} note={formatStrategyTags(analytics.strategy_tags) || "Replay-derived strategy tags"} />);
    }
  }

  const railCards = [
    analytics?.current_team_rating != null ? {
      label: "Current team Elo",
      value: formatElo(analytics.current_team_rating),
      note: analytics.current_team_name || "AoE2Companion current",
    } : null,
    analytics?.highest_team_rating != null ? {
      label: "Highest team Elo",
      value: formatElo(analytics.highest_team_rating),
      note: analytics.highest_team_name || "AoE2Companion peak",
    } : null,
    p.highest_server_team_rating != null ? {
      label: "Highest server Elo",
      value: formatElo(p.highest_server_team_rating),
      note: "Best server rating in team games",
    } : null,
    serverAverageElo != null ? {
      label: "Server average Elo",
      value: formatElo(serverAverageElo),
      note: "Current server Elo and peak server Elo averaged together",
    } : null,
  ].filter(Boolean);

  return (
    <div className="profile-dashboard">
      <div className="profile-hero">
        <div className="profile-avatar-column">
          {p.discord_avatar_url ? <img className="profile-avatar" src={p.discord_avatar_url} alt={playerName} /> : <div className="profile-avatar profile-avatar-placeholder">{playerName.slice(0, 1).toUpperCase()}</div>}
          {p.rank_str ? <span className="rank-badge rank-badge-lg">{p.rank_str}</span> : null}
        </div>
        <div className="profile-hero-info">
          <div className="profile-hero-name">
            <h1>{playerName}</h1>
            <span className="profile-board-pill">{boardLabel}</span>
            {p.is_linked ? <span className="link-badge linked">linked</span> : <span className="link-badge unlinked">unlinked</span>}
          </div>
          <p className="profile-hero-copy">A full-screen profile snapshot of this player's ladder identity, replay habits, and server-side team performance across AoE2 Companion and the AOE BD server.</p>
          <div className="profile-hero-links">
            {companionProfileUrl ? (
              <a className="profile-source-link" href={companionProfileUrl} target="_blank" rel="noreferrer">
                Open AoE2Companion profile
              </a>
            ) : null}
            {p.game_id ? <span className="profile-source-chip">Game ID {p.game_id}</span> : null}
            {p.discord_display_name ? <span className="profile-source-chip">Discord {decodeEscapedText(p.discord_display_name)}</span> : null}
          </div>
          <div className="profile-hero-stats">
            <div><strong>{p.rating ?? "?"}</strong><span>rating{p.deviation != null ? ` \u00B1 ${p.deviation}` : ""}</span></div>
            <div><strong>{p.wins}/{p.losses}/{p.draws}</strong><span>W/L/D</span></div>
            <div><strong>{winRate != null ? formatWinRate(winRate) : "\u2014"}</strong><span>win rate</span></div>
            <div><strong>{p.streak ?? 0}</strong><span>streak</span></div>
            {p.game_id ? <div><strong>{p.game_id}</strong><span>Game ID</span></div> : null}
          </div>
          <RecentForm matches={p.recent_matches} />
        </div>
        {railCards.length ? (
          <div className="profile-hero-rail">
            {railCards.map((card) => (
              <div key={card.label} className="profile-rail-card">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.note}</small>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <ProfileInsightTabs lifetimeEntries={p.rating_history} ladderCards={ladderCards} replayCards={replayCards} gameAnalysis={p.game_analysis} />

      <ProfileTabs matches={p.recent_matches} civs={p.civs} maps={p.maps} teammates={p.teammates} opponents={p.opponents} />
    </div>
  );
}

function PlayerProfilePage({ channelId, userId, board, members, guildName, onBack, onSaved }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkMemberId, setLinkMemberId] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [showManage, setShowManage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/channels/${channelId}/players/${userId}?board=${board}`)
      .then((data) => {
        if (cancelled) return;
        const parsed = PlayerSchema.safeParse(data);
        if (!parsed.success) {
          throw new Error(`Player validation failed: ${parsed.error.issues.map((i) => `${i.path.join(".") || "<root>"} ${i.message}`).join("; ")}`);
        }
        setState({ loading: false, data: parsed.data, error: null });
        setDraft(parsed.data);
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ loading: false, data: null, error: error.message });
      });
    return () => { cancelled = true; };
  }, [channelId, userId, board]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        game_id: draft.game_id ?? "",
        highest_server_team_rating_override: draft.highest_server_team_rating_override,
        rating: draft.rating,
        deviation: draft.deviation,
        wins: draft.wins,
        losses: draft.losses,
        draws: draft.draws,
        streak: draft.streak,
        is_hidden: draft.is_hidden ? 1 : 0,
      };
      const updated = await Api.send(`/api/channels/${channelId}/players/${userId}?board=${board}`, "POST", payload);
      const parsed = PlayerSchema.safeParse(updated);
      if (!parsed.success) {
        throw new Error(`Saved player validation failed: ${parsed.error.issues.map((i) => `${i.path.join(".") || "<root>"} ${i.message}`).join("; ")}`);
      }
      setState({ loading: false, data: parsed.data, error: null });
      setDraft(parsed.data);
      onSaved?.(parsed.data);
    } catch (error) {
      setState((prev) => ({ ...prev, error: error.message || "Failed to save player." }));
    } finally {
      setSaving(false);
    }
  };

  const link = async () => {
    if (!linkMemberId) return;
    setLinking(true);
    setLinkError("");
    try {
      const updated = await Api.send(`/api/channels/${channelId}/players/${userId}/link?board=${board}`, "POST", { member_id: linkMemberId });
      const parsed = PlayerSchema.safeParse(updated);
      if (!parsed.success) {
        throw new Error(`Linked player validation failed: ${parsed.error.issues.map((i) => `${i.path.join(".") || "<root>"} ${i.message}`).join("; ")}`);
      }
      setState({ loading: false, data: parsed.data, error: null });
      setDraft(parsed.data);
      onSaved?.(parsed.data);
    } catch (error) {
      setLinkError(error.message || "Failed to link player.");
    } finally {
      setLinking(false);
    }
  };

  if (state.loading) return <div className="panel empty-panel">Loading player...</div>;
  if (state.error) return <div className="panel empty-panel">{state.error}</div>;
  if (!state.data) return null;

  const p = state.data;

  return (
    <div className="profile-page">
      <button className="button secondary back-button" onClick={onBack}>← Back to leaderboard</button>

      <PlayerProfileSummary p={p} board={board} />

      <section className="manage-section">
        <button className="button secondary" onClick={() => setShowManage((v) => !v)}>
          {showManage ? "Hide manage options" : "Manage player"}
        </button>
        {showManage ? (
          <div className="manage-panel">
            <div className="player-grid">
              <label>
                <span>Nick</span>
                <input className="input" value={draft.nick ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, nick: e.target.value }))} />
              </label>
              <label>
                <span>Game ID</span>
                <input className="input" value={draft.game_id ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, game_id: e.target.value }))} />
              </label>
              <label>
                <span>Highest server Elo</span>
                <input className="input" type="number" value={draft.highest_server_team_rating_override ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, highest_server_team_rating_override: e.target.value }))} />
              </label>
              <label>
                <span>Rating</span>
                <input className="input" type="number" value={draft.rating ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, rating: e.target.value }))} />
              </label>
              <label>
                <span>Deviation</span>
                <input className="input" type="number" value={draft.deviation ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, deviation: e.target.value }))} />
              </label>
              <label>
                <span>Wins</span>
                <input className="input" type="number" value={draft.wins ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, wins: e.target.value }))} />
              </label>
              <label>
                <span>Losses</span>
                <input className="input" type="number" value={draft.losses ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, losses: e.target.value }))} />
              </label>
              <label>
                <span>Draws</span>
                <input className="input" type="number" value={draft.draws ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, draws: e.target.value }))} />
              </label>
              <label>
                <span>Streak</span>
                <input className="input" type="number" value={draft.streak ?? ""} onChange={(e) => setDraft((prev) => ({ ...prev, streak: e.target.value }))} />
              </label>
              <label>
                <span>Hidden</span>
                <select className="input" value={draft.is_hidden ? "1" : "0"} onChange={(e) => setDraft((prev) => ({ ...prev, is_hidden: e.target.value === "1" }))}>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button className="button" disabled={saving || !draft} onClick={save}>{saving ? "Saving..." : "Save player"}</button>
            </div>
            <div className="link-player-row">
              <div className="field-label"><span>Link to Discord member</span></div>
              <div className="field-description">
                Move this player's rating, match history and nickname onto a real Discord server member (e.g. for
                rows imported/seeded under a placeholder id, so /rank and the leaderboard find them).
                Showing members of <strong>{guildName || "the selected server"}</strong> ({members?.length ?? 0} found) \u2014
                switch the channel in the sidebar first if that's the wrong server.
              </div>
              <div className="modal-actions">
                <select className="input" value={linkMemberId} onChange={(e) => setLinkMemberId(e.target.value)}>
                  <option value="">Select a member...</option>
                  {(members || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button className="button secondary" disabled={linking || !linkMemberId} onClick={link}>
                  {linking ? "Linking..." : "Link"}
                </button>
              </div>
              {linkError ? <div className="field-description" style={{ color: "var(--danger)" }}>{linkError}</div> : null}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ActionModal({ modal, onClose, guilds, channels, members, onEnableChannel, onCreateQueue, onCreateTournamentTeam }) {
  const [guildId, setGuildId] = useState(guilds[0]?.id || "");
  const [channelId, setChannelId] = useState("");
  const [announcementChannelId, setAnnouncementChannelId] = useState("");
  const [channelType, setChannelType] = useState("queue");
  const [name, setName] = useState("Lobby");
  const [size, setSize] = useState("8");
  const [teamName, setTeamName] = useState("");
  const [player1Id, setPlayer1Id] = useState(members[0]?.id || "");
  const [player2Id, setPlayer2Id] = useState(members[1]?.id || members[0]?.id || "");
  const guild = guilds.find((g) => String(g.id) === String(guildId)) || guilds[0];
  const available = guild?.text_channels || [];

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>{modal.type === "createQueue" ? "Create Queue" : modal.type === "createTournamentTeam" ? "Create Tournament Team" : "Enable Channel"}</h3>
        {modal.type === "createQueue" ? (
          <>
            <label>
              <span>Queue name</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              <span>Queue size</span>
              <input className="input" type="number" value={size} onChange={(e) => setSize(e.target.value)} />
            </label>
            <div className="modal-actions">
              <button className="button" onClick={async () => { await onCreateQueue({ name, size }); onClose(); }}>Create</button>
              <button className="button secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : modal.type === "createTournamentTeam" ? (
          <>
            <label>
              <span>Team name</span>
              <input className="input" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Idle Villager" />
            </label>
            <label>
              <span>Player 1</span>
              <select className="input" value={player1Id} onChange={(e) => setPlayer1Id(e.target.value)}>
                <option value="">Select a member</option>
                {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
              </select>
            </label>
            <label>
              <span>Player 2</span>
              <select className="input" value={player2Id} onChange={(e) => setPlayer2Id(e.target.value)}>
                <option value="">Select a member</option>
                {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
              </select>
            </label>
            <div className="modal-actions">
              <button className="button" onClick={async () => {
                if (teamName && player1Id && player2Id) {
                  await onCreateTournamentTeam({ teamName, player1Id, player2Id });
                  onClose();
                }
              }}>Create</button>
              <button className="button secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <label>
              <span>Channel type</span>
              <select className="input" value={channelType} onChange={(e) => setChannelType(e.target.value)}>
                <option value="queue">Queue channel</option>
                <option value="tournament">Tournament channel</option>
              </select>
            </label>
            <label>
              <span>Guild</span>
              <select className="input" value={guildId} onChange={(e) => setGuildId(e.target.value)}>
                {guilds.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
            <label>
              <span>{channelType === "tournament" ? "Tournament registration" : "Text channel"}</span>
              <select className="input" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                <option value="">Select a channel</option>
                {available.map((ch) => <option key={ch.id} value={ch.id}>#{ch.name}</option>)}
              </select>
            </label>
            {channelType === "tournament" ? (
              <label>
                <span>Tournament announcements</span>
                <select className="input" value={announcementChannelId} onChange={(e) => setAnnouncementChannelId(e.target.value)}>
                  <option value="">Select a channel</option>
                  {available.map((ch) => <option key={ch.id} value={ch.id}>#{ch.name}</option>)}
                </select>
              </label>
            ) : null}
            <div className="modal-actions">
              <button className="button" onClick={async () => {
                if (channelId && (channelType !== "tournament" || announcementChannelId)) {
                  await onEnableChannel({ channelId, type: channelType, announcementChannelId });
                  onClose();
                }
              }}>Enable</button>
              <button className="button secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("PUBobot2 UI crashed", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="screen-state error">
          <div className="login-card">
            <h1>Failed to load</h1>
            <p>{this.state.error?.message || String(this.state.error)}</p>
            <div className="auth-help">
              Refresh the page after the cache is cleared or open the browser in a private window.
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AoeRelicScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0.6, 7);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);
    const gold = new THREE.MeshStandardMaterial({ color: 0xe0a63a, roughness: 0.38, metalness: 0.72 });
    const jade = new THREE.MeshStandardMaterial({ color: 0x3fb873, roughness: 0.45, metalness: 0.2 });
    const stone = new THREE.MeshStandardMaterial({ color: 0x2a2f3b, roughness: 0.75, metalness: 0.1 });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.45, 0.08, 16, 96), gold);
    const shield = new THREE.Mesh(new THREE.IcosahedronGeometry(1.02, 1), stone);
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), jade);
    group.add(ring, shield, gem);
    for (let i = 0; i < 34; i += 1) {
      const star = new THREE.Mesh(new THREE.SphereGeometry(0.025 + Math.random() * 0.035, 8, 8), gold);
      const radius = 2.2 + Math.random() * 2.2;
      const angle = Math.random() * Math.PI * 2;
      star.position.set(Math.cos(angle) * radius, (Math.random() - 0.5) * 2.8, Math.sin(angle) * radius);
      group.add(star);
    }
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.PointLight(0xffd58a, 3.2, 16);
    key.position.set(3, 3, 4);
    scene.add(key);
    const rim = new THREE.PointLight(0x4d8fe0, 1.8, 14);
    rim.position.set(-3, -1, 4);
    scene.add(rim);

    const resize = () => {
      const rect = mount.getBoundingClientRect();
      const width = Math.max(rect.width, 280);
      const height = Math.max(rect.height, 280);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);
    let frame = 0;
    const clock = new THREE.Clock();
    const animate = () => {
      const t = clock.getElapsedTime();
      group.rotation.y = t * 0.35;
      group.rotation.x = Math.sin(t * 0.55) * 0.16;
      gem.position.y = Math.sin(t * 1.7) * 0.18;
      renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div className="landing-3d" ref={mountRef} aria-hidden="true" />;
}

function LandingActiveMatch({ match }) {
  return (
    <div className="landing-match-live">
      <div>
        <span className="landing-kicker">{match.state || "live"}</span>
        <h3>{match.queue_name || "Community match"}</h3>
      </div>
      <div className="landing-versus">
        {(match.teams || []).slice(0, 2).map((team, idx) => (
          <div key={idx} className="landing-team">
            <strong>{team.name || (idx === 0 ? "Alpha" : "Beta")}</strong>
            <span>{team.players?.map((p) => p.nick).join(", ") || "Drafting players"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingTopPlayer({ row }) {
  const profileId = String(row.game_id || "").split(",")[0].trim();
  const profileUrl = profileId ? `https://www.aoe2companion.com/players/${profileId}` : null;
  const games = Number(row.wins || 0) + Number(row.losses || 0) + Number(row.draws || 0);
  const displayName = decodeEscapedText(row.nick) || "Unknown player";
  const displayRank = decodeEscapedText(row.rank_str) || "AOE BD";
  const openProfile = () => navigate(withQuery(`/leaderboard/player/${row.user_id}`, { board: "team" }));
  return (
    <div
      className="landing-player-row landing-player-row-clickable"
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openProfile();
        }
      }}
    >
      {row.discord_avatar_url ? (
        <img className="landing-player-avatar" src={row.discord_avatar_url} alt="" />
      ) : (
        <div className="landing-player-avatar landing-player-avatar-placeholder">{(displayName || "?").slice(0, 1).toUpperCase()}</div>
      )}
      <span className="landing-rank">#{row.rank}</span>
      <div>
        <strong>{displayName}</strong>
        <small>{displayRank} · {formatCount(games)} games</small>
      </div>
      <b>{row.rating ?? "?"}</b>
      {profileUrl ? <a className="landing-mini-link" href={profileUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>AoE2C</a> : null}
    </div>
  );
}

function LandingTournamentCard({ tournament }) {
  const teams = tournament.teams || [];
  const startDate = tournament.start_date ? formatDateOnly(tournament.start_date) : null;
  return (
    <div className="landing-tournament-card">
      <div className="landing-tournament-head">
        <div>
          <span className="landing-kicker">{(tournament.status || "registration") === "registration" ? "Registration live" : "Tournament live"}</span>
          <h3>{tournament.name}</h3>
          <p>{formatCount(tournament.team_count)} registered teams{startDate ? ` · Starts ${startDate}` : ""}</p>
        </div>
        <TournamentStatusPill status={tournament.status} />
      </div>
      <div className="landing-tournament-team-grid">
        {teams.length ? teams.slice(0, 4).map((team) => (
          <TeamMiniCard
            key={team.id}
            team={team}
            onClick={() => navigate(`/tournaments/${encodeURIComponent(tournament.slug)}/teams/${team.id}`)}
          />
        )) : <div className="landing-empty">No teams registered yet.</div>}
      </div>
      {teams.length > 4 ? <div className="landing-more-note">+{teams.length - 4} more teams in the bracket</div> : null}
    </div>
  );
}

function PublicAccountAction({ auth }) {
  if (auth.loading) return null;
  if (!auth.user) {
    return <button className="button" onClick={() => navigate("/member/login")}>Member login</button>;
  }
  const metadata = auth.user.user_metadata || {};
  const name = metadata.custom_claims?.global_name || metadata.full_name || metadata.name || metadata.preferred_username || "Member";
  return (
    <button className="public-account" onClick={() => navigate("/member")}>
      <img src={metadata.avatar_url || metadata.picture || "/android-chrome-192x192.png"} alt="" />
      <span>{name}</span>
    </button>
  );
}

function LandingPage() {
  const auth = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const pageRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Api.get("/api/public/landing")
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!pageRef.current) return;
    gsap.fromTo(
      pageRef.current.querySelectorAll(".landing-reveal"),
      { y: 28, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.85, stagger: 0.09, ease: "power3.out" },
    );
  }, [state.loading]);

  const data = state.data || {};
  const community = data.community || {};
  const recent = data.recent_matches || [];
  const active = data.active_matches || [];
  const topPlayers = data.top_players || [];
  const registrationTournaments = data.registration_tournaments || [];
  const onlineCount = community.online_member_count ?? null;
  const memberCount = community.member_count ?? null;
  const onlineCountLabel = onlineCount == null ? "0" : formatCount(onlineCount);
  const memberCountLabel = memberCount == null ? "0" : formatCount(memberCount);
  return (
    <div className="landing-shell" ref={pageRef}>
      <header className="landing-nav landing-reveal">
        <div className="brand" onClick={() => navigate("/")}>
          <img className="brand-mark" src="/android-chrome-192x192.png" alt="" />
          <div>
            <div className="brand-title">AOE BD</div>
            <div className="brand-subtitle">Age Of Empires 2 DE Bangladesh Community</div>
          </div>
        </div>
        <nav>
          <a href="/leaderboard">Leaderboard</a>
          <a href="#matches">Matches</a>
          <a href="/tournaments">Tournaments</a>
          <a href={community.discord_url || "https://discord.gg/TTywMrCCnf"} target="_blank" rel="noreferrer">Discord</a>
          <PublicAccountAction auth={auth} />
          <button className="button secondary" onClick={() => navigate("/login")}>Admin login</button>
        </nav>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-hero-copy landing-reveal">
            <span className="landing-kicker">Wololo, Bangladesh is online</span>
            <h1>Age Of Empires 2 DE Bangladesh Community</h1>
            <p>
              The home base for Bangladeshi AoE2: Definitive Edition players, ranked pickup games,
              team balancing, tournaments, live match tracking, and a leaderboard powered by BDBot.
            </p>
            <div className="landing-actions">
              <a className="button" href={community.discord_url || "https://discord.gg/TTywMrCCnf"} target="_blank" rel="noreferrer">Join Discord</a>
              <a className="button secondary" href={community.facebook_url || "https://www.facebook.com/groups/367960850054876"} target="_blank" rel="noreferrer">Facebook group</a>
              <a className="button ghost" href={publicUrl("/leaderboard")}>View leaderboard</a>
            </div>
          </div>
          <div className="landing-hero-visual landing-reveal">
            <AoeRelicScene />
            <div className="landing-avatar-stack">
              {(topPlayers.slice(0, 4)).map((row, idx) => (
                <img
                  key={row.user_id}
                  className={`landing-avatar-thumb landing-avatar-thumb-${idx + 1}`}
                  src={row.discord_avatar_url || "/android-chrome-192x192.png"}
                  alt={row.nick || "AoE BD player"}
                  title={row.nick || "AoE BD player"}
                />
              ))}
            </div>
            <div className="landing-floating-card">
              <strong>{onlineCountLabel}</strong>
              <span>online right now</span>
              <small>{memberCountLabel} total Discord members</small>
            </div>
          </div>
        </section>

        <section className="landing-stat-grid landing-reveal">
          <div><strong>{formatCount(community.registered_player_count) || "0"}</strong><span>ranked players</span></div>
          <div><strong>{formatCount(community.active_match_count) || "0"}</strong><span>active matches</span></div>
          <div><strong>{formatCount(community.queue_count) || "0"}</strong><span>pickup queues</span></div>
          <div><strong>{onlineCountLabel}</strong><span>online Discord users</span></div>
          <div><strong>{memberCountLabel}</strong><span>total Discord members</span></div>
          <div><strong>24/7</strong><span>community matchmaking</span></div>
        </section>

        <section className="landing-grid">
          <div className="landing-panel landing-reveal">
            <div className="landing-section-head">
              <span className="landing-kicker">Tournament watch</span>
              <h2>Tournaments in progress</h2>
              <p>Live signups and brackets with teams already on the board.</p>
            </div>
            <div className="landing-stack">
              {registrationTournaments.length ? registrationTournaments.map((tournament) => (
                <LandingTournamentCard key={tournament.id} tournament={tournament} />
              )) : <div className="landing-empty">No active tournaments right now.</div>}
            </div>
          </div>

          <div id="leaderboard" className="landing-panel landing-reveal">
            <div className="landing-section-head">
              <span className="landing-kicker">Top 10</span>
              <h2>Bangladesh leaderboard</h2>
              <p>Team leaderboard with direct AoE2Companion profile links where linked.</p>
            </div>
            <div className="landing-player-list">
              {state.loading ? <div className="empty-note">Loading leaderboard...</div> : null}
              {topPlayers.map((row) => <LandingTopPlayer key={row.user_id} row={row} />)}
            </div>
          </div>

          <div id="matches" className="landing-panel landing-reveal">
            <div className="landing-section-head">
              <span className="landing-kicker">Live war room</span>
              <h2>Active matches</h2>
              <p>Matches currently running through BDBot queues.</p>
            </div>
            <div className="landing-stack">
              {active.length ? active.map((match) => <LandingActiveMatch key={match.id} match={match} />) : <div className="landing-empty">No active matches right now. Join Discord and start one.</div>}
            </div>
          </div>
        </section>

        <section className="landing-panel landing-reveal">
          <div className="landing-section-head">
            <span className="landing-kicker">Recent battles</span>
            <h2>Latest completed games</h2>
          </div>
          <div className="landing-recent-grid">
            {recent.length ? recent.slice(0, 3).map((match) => (
              <MatchCard key={`${match.source}-${match.match_id}`} match={match} onClick={() => navigate(`/match/${match.source}/${match.match_id}`)} />
            )) : <div className="landing-empty">Recent results will appear after matches are reported.</div>}
          </div>
        </section>

        <section className="landing-cta landing-reveal">
          <h2>Ready to queue with Bangladesh?</h2>
          <p>Bring your mangudai micro, your monk conversions, and your best "one more game" energy.</p>
          <div className="landing-actions">
            <a className="button" href="https://discord.gg/TTywMrCCnf" target="_blank" rel="noreferrer">Join Discord</a>
            <a className="button secondary" href="https://www.facebook.com/groups/367960850054876" target="_blank" rel="noreferrer">Visit Facebook Group</a>
          </div>
        </section>

        <footer className="landing-footer landing-reveal">
          <span>
            Created by <a href="https://www.tanvirxen.com/" target="_blank" rel="noreferrer">Tanvir Ishtiaq (TanvirXen)</a> for BDBot and this website.
          </span>
        </footer>
      </main>
    </div>
  );
}

// --- Public routes -------------------------------------------------------
// /leaderboard and /leaderboard/player/:id - unauthenticated, read-only mirrors of the
// protected leaderboard/profile views, backed by /api/public/* (see webui/webserver.py's
// WEBUI_PUBLIC_CHANNEL_ID). Rendered before any auth check so they work for signed-out visitors.

function PublicShell({ children }) {
  const auth = useAuth();
  return (
    <div className="public-shell">
      <header className="landing-nav public-nav">
        <div className="brand" onClick={() => navigate("/")}>
          <img className="brand-mark" src="/android-chrome-192x192.png" alt="" />
          <div>
            <div className="brand-title">AOE BD</div>
            <div className="brand-subtitle">Age Of Empires 2 DE Bangladesh Community</div>
          </div>
        </div>
        <nav>
          <a href="/leaderboard">Leaderboard</a>
          <a href="/#matches">Matches</a>
          <a href="/tournaments">Tournaments</a>
          <a href="https://discord.gg/TTywMrCCnf" target="_blank" rel="noreferrer">Discord</a>
          <PublicAccountAction auth={auth} />
          <a className="button secondary" href="/login">Admin login</a>
        </nav>
      </header>
      <main className="public-main">
        <div className="content public-content">{children}</div>
      </main>
    </div>
  );
}

function TournamentStatusPill({ status }) {
  return <span className={`link-badge tournament-status status-${status || "registration"}`}>{status || "registration"}</span>;
}

function PublicTournamentsPage() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    Api.get("/api/public/tournaments")
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, []);
  const tournaments = state.data?.tournaments || [];
  return (
    <PublicShell>
      <div className="panel tournament-page">
        <div className="panel-header">
          <div>
            <h1>Tournaments</h1>
            <p>AoE BD team events, registrations, groups, and knockout matches.</p>
          </div>
        </div>
        {state.loading ? <div className="empty-panel">Loading tournaments...</div> : null}
        {state.error ? <div className="empty-panel">{state.error}</div> : null}
        {!state.loading && !state.error && !tournaments.length ? <div className="empty-panel">No tournaments published yet.</div> : null}
        <div className="tournament-list">
          {tournaments.map((t) => (
            <button key={t.id} className="tournament-card" onClick={() => navigate(`/tournaments/${encodeURIComponent(t.slug)}`)}>
              <div>
                <h2>{t.name}</h2>
                <p>{formatCount(t.team_count)} teams{t.start_date ? ` · Starts ${formatDateOnly(t.start_date)}` : ""}</p>
              </div>
              <TournamentStatusPill status={t.status} />
            </button>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}

function TeamMiniCard({ team, onClick }) {
  if (!team) return <div className="team-mini-card empty">TBD</div>;
  const playerServerAverages = (team.players || [])
    .map((player) => {
      const current = player.server_current_elo ?? player.current_1v1_elo;
      const highest = player.server_highest_elo;
      const avg = player.server_avg_elo != null
        ? player.server_avg_elo
        : (current != null && highest != null ? Math.round((Number(current) + Number(highest)) / 2) : null);
      if (avg == null) return null;
      return `${player.name} avg ${avg}`;
    })
    .filter(Boolean);
  const CardTag = onClick ? "button" : "div";
  return (
    <CardTag type={onClick ? "button" : undefined} className={`team-mini-card${onClick ? " clickable" : ""}`} onClick={onClick}>
      <strong>{team.name}</strong>
      <span>{team.players?.map((p) => p.name).join(" + ") || "Players pending"}</span>
      {playerServerAverages.length ? <small>Server avg Elo {playerServerAverages.join(" · ")}</small> : null}
      <small>Registration combined server Elo {team.combined_elo ?? "?"}</small>
      <small>Current combined server Elo {team.current_combined_server_elo ?? "?"}</small>
    </CardTag>
  );
}

function TournamentMatchResultEditor({ match, onSave }) {
  const initialWinner = match.winner_team_id || match.team1?.id || "";
  const [winnerTeamId, setWinnerTeamId] = useState(String(initialWinner));
  const [team1Points, setTeam1Points] = useState(match.forfeit_team_id === match.team1?.id ? "F" : String(match.team1_points ?? 1));
  const [team2Points, setTeam2Points] = useState(match.forfeit_team_id === match.team2?.id ? "F" : String(match.team2_points ?? 1));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setWinnerTeamId(String(match.winner_team_id || match.team1?.id || ""));
    setTeam1Points(match.forfeit_team_id === match.team1?.id ? "F" : String(match.team1_points ?? 1));
    setTeam2Points(match.forfeit_team_id === match.team2?.id ? "F" : String(match.team2_points ?? 1));
  }, [match.id, match.winner_team_id, match.team1_points, match.team2_points, match.forfeit_team_id, match.team1?.id, match.team2?.id]);

  const stageLabel = match.stage === "group"
    ? `Group ${match.group} · Match ${match.round}`
    : `${match.stage === "quarterfinal" ? "Quarterfinal" : match.stage === "semifinal" ? "Semifinal" : "Final"}${match.round ? ` ${match.round}` : ""}`;

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await onSave(match.id, {
        winner_team_id: Number(winnerTeamId),
        team1_points: team1Points,
        team2_points: team2Points,
      });
      setMessage("Pointboard updated.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="tournament-result-card" onSubmit={submit}>
      <div className="matchup-head"><span>{stageLabel}</span><span>Match #{match.id}</span></div>
      <div className="tournament-score-inputs">
        <label>
          <span>{match.team1?.name || "Team A"}</span>
          <input type="text" inputMode="text" maxLength="1" pattern="[0-7fF]" title="Enter 0-7, or F for forfeit" required value={team1Points} onChange={(event) => {
            const value = event.target.value.toUpperCase();
            setTeam1Points(value);
            if (value === "F" && match.team2) setWinnerTeamId(String(match.team2.id));
          }} />
        </label>
        <span className="matchup-vs">VS</span>
        <label>
          <span>{match.team2?.name || "Team B"}</span>
          <input type="text" inputMode="text" maxLength="1" pattern="[0-7fF]" title="Enter 0-7, or F for forfeit" required value={team2Points} onChange={(event) => {
            const value = event.target.value.toUpperCase();
            setTeam2Points(value);
            if (value === "F" && match.team1) setWinnerTeamId(String(match.team1.id));
          }} />
        </label>
      </div>
      <label className="tournament-winner-select">
        <span>Winner</span>
        <select required value={winnerTeamId} onChange={(event) => setWinnerTeamId(event.target.value)}>
          <option value="" disabled>Select winner</option>
          {match.team1 ? <option value={match.team1.id}>{match.team1.name}</option> : null}
          {match.team2 ? <option value={match.team2.id}>{match.team2.name}</option> : null}
        </select>
      </label>
      <div className="tournament-result-actions">
        <button className="button" type="submit" disabled={saving || !match.team1 || !match.team2}>{saving ? "Saving..." : match.status === "reported" ? "Update result" : "Save result"}</button>
        {message ? <small>{message}</small> : null}
      </div>
    </form>
  );
}

function MatchupCard({ match }) {
  const winner = match.winner_team_id;
  const team1Name = match.team1?.name || match.team1Label || "To be decided";
  const team2Name = match.team2?.name || match.team2Label || "To be decided";
  const team1Won = winner != null && winner === match.team1?.id;
  const team2Won = winner != null && winner === match.team2?.id;
  const isForfeit = match.forfeit_team_id != null;
  const team1Forfeited = isForfeit && match.forfeit_team_id === match.team1?.id;
  const team2Forfeited = isForfeit && match.forfeit_team_id === match.team2?.id;
  const winnerName = team1Won ? team1Name : team2Won ? team2Name : null;
  const hasScore = match.team1_points != null && match.team2_points != null;
  const stageLabel = match.stage === "group"
    ? `Group ${match.group} · Match ${match.round}`
    : `${match.stage === "quarterfinal" ? "Quarterfinal" : match.stage === "semifinal" ? "Semifinal" : "Final"}${match.stage !== "final" && match.round ? ` ${match.round}` : ""}`;
  return (
    <div className={`matchup-card stage-${match.stage}`}>
      <div className="matchup-head">
        <span>{stageLabel}</span>
        {typeof match.id === "number" ? <span>Match #{match.id}</span> : <span>Upcoming</span>}
      </div>
      <div className="matchup-versus">
        <div className={`matchup-team ${team1Won ? "winner" : ""} ${team1Won && isForfeit ? "forfeit-winner" : ""} ${team1Forfeited ? "forfeited" : ""}`}>
          <small>{match.team1Caption || "Team 1"}</small>
          <strong>{team1Name}</strong>
          {hasScore ? <b className="matchup-score">{team1Forfeited ? "F" : match.team1_points}</b> : null}
          {team1Won ? <span>{isForfeit ? "Forfeit win" : "Winner"}</span> : null}
        </div>
        <span className="matchup-vs">VS</span>
        <div className={`matchup-team ${team2Won ? "winner" : ""} ${team2Won && isForfeit ? "forfeit-winner" : ""} ${team2Forfeited ? "forfeited" : ""}`}>
          <small>{match.team2Caption || "Team 2"}</small>
          <strong>{team2Name}</strong>
          {hasScore ? <b className="matchup-score">{team2Forfeited ? "F" : match.team2_points}</b> : null}
          {team2Won ? <span>{isForfeit ? "Forfeit win" : "Winner"}</span> : null}
        </div>
      </div>
      <div className={`matchup-result ${winnerName ? "reported" : "pending"} ${isForfeit ? "forfeit-result" : ""}`}>
        <span>{winnerName ? "Result" : "Status"}</span>
        <strong>{winnerName ? `${hasScore ? `${team1Forfeited ? "F" : match.team1_points}–${team2Forfeited ? "F" : match.team2_points} · ` : ""}${winnerName} won${isForfeit ? " by forfeit" : ""}` : "Result pending"}</strong>
      </div>
    </div>
  );
}

function PublicTournamentPage({ route }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [pointboardOpen, setPointboardOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/public/tournaments/${encodeURIComponent(route.slug)}`)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [route.slug]);
  const t = state.data;
  const groups = {};
  for (const team of t?.teams || []) {
    const key = team.group || "Registration";
    groups[key] = [...(groups[key] || []), team];
  }
  const hasPublishedDraw = Object.keys(groups).some((groupName) => groupName !== "Registration");
  if (t?.status === "registration" && hasPublishedDraw) {
    for (const groupName of Object.keys(groups)) {
      while (groups[groupName].length < 4) {
        groups[groupName].push({
          id: `pending-${groupName}-${groups[groupName].length + 1}`,
          name: "To Be Announced",
          group: groupName,
          seed: groupName === "2" ? 7 : 9999,
          current_combined_server_elo: groupName === "2" ? 1700 : null,
          placeholder: true,
        });
      }
    }
  }
  const groupEntries = Object.entries(groups).sort(([left], [right]) => left.localeCompare(right, undefined, { numeric: true }));
  const standingsByTeam = new Map((t?.standings || []).map((standing) => [String(standing.team_id), standing]));
  const compareStandingTeams = (left, right, useSeedFallback = true) => {
    const leftStanding = standingsByTeam.get(String(left.id)) || {};
    const rightStanding = standingsByTeam.get(String(right.id)) || {};
    const resultOrder = (Number(rightStanding.points || 0) - Number(leftStanding.points || 0))
      || (Number(rightStanding.wins || 0) - Number(leftStanding.wins || 0))
      || (Number(leftStanding.losses || 0) - Number(rightStanding.losses || 0));
    if (resultOrder) return resultOrder;
    if (useSeedFallback) {
      const seedOrder = Number(left.seed ?? 9999) - Number(right.seed ?? 9999);
      if (seedOrder) return seedOrder;
    }
    return (Number(right.current_combined_server_elo ?? right.combined_elo ?? -1)
      - Number(left.current_combined_server_elo ?? left.combined_elo ?? -1));
  };
  const overallTeams = [...(t?.teams || [])].sort((left, right) => compareStandingTeams(left, right, false)
    || String(left.group || "").localeCompare(String(right.group || ""), undefined, { numeric: true })
    || Number(left.seed ?? 9999) - Number(right.seed ?? 9999));
  const groupMatches = (t?.matches || [])
    .filter((m) => m.stage === "group")
    .sort((left, right) => String(left.group).localeCompare(String(right.group), undefined, { numeric: true }) || (left.round || 0) - (right.round || 0));
  const quarterfinals = (t?.matches || []).filter((m) => m.stage === "quarterfinal").sort((a, b) => (a.round || 0) - (b.round || 0));
  const semifinals = (t?.matches || []).filter((m) => m.stage === "semifinal").sort((a, b) => (a.round || 0) - (b.round || 0));
  const finals = (t?.matches || []).filter((m) => m.stage === "final");
  const startDate = t?.start_date ? formatDateOnly(t.start_date) : t?.settings?.start_date ? formatDateOnly(t.settings.start_date) : null;
  const resolvedGroupPlacement = (groupName, placementIndex, fallbackLabel) => {
    const groupTeams = groups[String(groupName)] || [];
    const override = t?.group_placement_overrides?.[String(groupName)];
    const overrideId = placementIndex === 0 ? override?.champion : override?.runner_up;
    const overrideTeam = overrideId == null ? null : groupTeams.find((team) => String(team.id) === String(overrideId));
    if (overrideTeam) return `${overrideTeam.name} (${fallbackLabel})`;
    const matches = groupMatches.filter((match) => String(match.group) === String(groupName));
    const expectedMatches = groupTeams.length > 1 ? (groupTeams.length * (groupTeams.length - 1)) / 2 : 0;
    const groupComplete = expectedMatches > 0 && matches.length === expectedMatches && matches.every((match) => match.status === "reported");
    if (!groupComplete) return fallbackLabel;
    const ranked = [...groupTeams].sort((left, right) => compareStandingTeams(left, right, false)
      || Number(right.combined_elo ?? -1) - Number(left.combined_elo ?? -1));
    const team = ranked[placementIndex];
    return team ? `${team.name} (${fallbackLabel})` : fallbackLabel;
  };
  const quarterfinalPlan = [
    [resolvedGroupPlacement("4", 0, "Group 4 champion"), resolvedGroupPlacement("3", 1, "Group 3 runner-up")],
    [resolvedGroupPlacement("2", 0, "Group 2 champion"), resolvedGroupPlacement("1", 1, "Group 1 runner-up")],
    [resolvedGroupPlacement("3", 0, "Group 3 champion"), resolvedGroupPlacement("4", 1, "Group 4 runner-up")],
    [resolvedGroupPlacement("1", 0, "Group 1 champion"), resolvedGroupPlacement("2", 1, "Group 2 runner-up")],
  ];
  const quarterfinalCards = quarterfinals.length ? quarterfinals : quarterfinalPlan.map(([team1Label, team2Label], index) => ({
    id: `quarterfinal-placeholder-${index + 1}`,
    stage: "quarterfinal",
    round: index + 1,
    status: "pending",
    team1Label,
    team2Label,
  }));
  const semifinalCards = semifinals.length ? semifinals : [
    { id: "semifinal-placeholder-1", stage: "semifinal", round: 1, status: "pending", team1Label: "Quarterfinal 1 winner", team2Label: "Quarterfinal 2 winner" },
    { id: "semifinal-placeholder-2", stage: "semifinal", round: 2, status: "pending", team1Label: "Quarterfinal 3 winner", team2Label: "Quarterfinal 4 winner" },
  ];
  const finalCards = finals.length ? finals : [{
    stage: "final",
    status: "pending",
    id: "final-placeholder-1",
    team1Label: "Semifinal 1 winner",
    team2Label: "Semifinal 2 winner",
  }];
  return (
    <PublicShell>
      <div className="tournament-page">
        <button className="button secondary back-button" onClick={() => navigate("/tournaments")}>Back to tournaments</button>
        {state.loading ? <div className="panel empty-panel">Loading tournament...</div> : null}
        {state.error ? <div className="panel empty-panel">{state.error}</div> : null}
        {t ? (
          <>
            <section className="tournament-hero panel">
              <div>
                <h1>{t.name}</h1>
                <p>{formatCount(t.team_count)} registered teams{startDate ? ` · Starts ${startDate}` : ""}</p>
              </div>
              <TournamentStatusPill status={t.status} />
            </section>
            <section className="tournament-section">
              <h2>Teams</h2>
              <div className="tournament-team-grid">
                {(t.teams || []).map((team) => (
                  <TeamMiniCard
                    key={team.id}
                    team={team}
                    onClick={() => navigate(`/tournaments/${encodeURIComponent(route.slug)}/teams/${team.id}`)}
                  />
                ))}
              </div>
            </section>
            <section className="tournament-section">
              <div className="tournament-section-title">
                <div>
                  <h2>Group Pointboards</h2>
                  <p>Group standings and the current Elo draw from the tournament sheet.</p>
                </div>
              </div>
              <div className="group-grid">
                {groupEntries.map(([groupName, teams]) => {
                  const orderedTeams = [...teams].sort((left, right) => compareStandingTeams(left, right));
                  const eloValues = orderedTeams
                    .map((team) => team.current_combined_server_elo ?? team.combined_elo)
                    .filter((value) => value != null);
                  const groupAverage = eloValues.length ? eloValues.reduce((sum, value) => sum + Number(value), 0) / eloValues.length : null;
                  const pendingCount = orderedTeams.filter((team) => team.placeholder).length;
                  return (
                    <div key={groupName} className="group-panel">
                      <div className="group-panel-head">
                        <h3>Group {groupName}</h3>
                        <span>{pendingCount ? `${orderedTeams.length - pendingCount} registered · ${pendingCount} pending` : `${orderedTeams.length} teams`}</span>
                      </div>
                      <div className="group-table-scroll">
                        <table className="stat-table tournament-group-table">
                          <thead><tr><th>Team</th><th>Current Elo</th><th>P</th><th>W</th><th>L</th><th>Pts</th></tr></thead>
                          <tbody>
                            {orderedTeams.map((team) => {
                              const standing = standingsByTeam.get(String(team.id));
                              const currentElo = team.current_combined_server_elo ?? team.combined_elo;
                              return (
                                <tr key={team.id} className={team.placeholder ? "pending-team-row" : ""}>
                                  <td>{team.name}</td>
                                  <td>{currentElo != null ? formatCount(currentElo) : "—"}</td>
                                  <td>{standing?.played ?? 0}</td>
                                  <td>{standing?.wins ?? 0}</td>
                                  <td>{standing?.losses ?? 0}</td>
                                  <td>{standing?.points ?? 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="group-average">
                        <span>Group average</span>
                        <strong>{groupAverage != null ? groupAverage.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
              {groupMatches.length ? (
                <div className="tournament-fixtures">
                  <h3>Group fixtures & results</h3>
                  <div className="matchup-grid">{groupMatches.map((m) => <MatchupCard key={m.id} match={m} />)}</div>
                </div>
              ) : null}
            </section>
            <section className="tournament-section">
              <h2>Knockout</h2>
              <div className="bracket-grid">
                <div>
                  <h3>Quarterfinal</h3>
                  <div className="matchup-grid">{quarterfinalCards.map((m) => <MatchupCard key={m.id} match={m} />)}</div>
                </div>
                <div>
                  <h3>Semifinal</h3>
                  <div className="matchup-grid">{semifinalCards.map((m) => <MatchupCard key={m.id} match={m} />)}</div>
                </div>
                <div>
                  <h3>Final</h3>
                  <div className="matchup-grid">{finalCards.map((m) => <MatchupCard key={m.id} match={m} />)}</div>
                </div>
              </div>
            </section>
            <section className="tournament-section">
              <button
                type="button"
                className="tournament-section-title collapsible-toggle"
                aria-expanded={pointboardOpen}
                onClick={() => setPointboardOpen((open) => !open)}
              >
                <div>
                  <h2>Overall Pointboard</h2>
                  <p>All registered teams ranked by points, wins, and losses.</p>
                </div>
                <span>{overallTeams.length} teams <span className={`collapsible-chevron ${pointboardOpen ? "open" : ""}`}>▾</span></span>
              </button>
              {pointboardOpen ? (
                <div className="overall-pointboard panel">
                  <div className="group-table-scroll">
                    <table className="stat-table tournament-overall-table">
                      <thead><tr><th>#</th><th>Team</th><th>Group</th><th>Current Elo</th><th>P</th><th>W</th><th>L</th><th>Pts</th></tr></thead>
                      <tbody>
                        {overallTeams.map((team, index) => {
                          const standing = standingsByTeam.get(String(team.id));
                          return (
                            <tr key={team.id}>
                              <td><span className="pointboard-rank">{index + 1}</span></td>
                              <td><strong>{team.name}</strong></td>
                              <td><span className="group-chip">Group {team.group || "—"}</span></td>
                              <td>{formatCount(team.current_combined_server_elo ?? team.combined_elo ?? 0)}</td>
                              <td>{standing?.played ?? 0}</td>
                              <td>{standing?.wins ?? 0}</td>
                              <td>{standing?.losses ?? 0}</td>
                              <td><strong className="pointboard-points">{standing?.points ?? 0}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PublicShell>
  );
}

function PublicTournamentTeamPage({ route }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/public/tournaments/${encodeURIComponent(route.slug)}/teams/${encodeURIComponent(route.teamId)}`)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [route.slug, route.teamId]);

  const data = state.data;
  const team = data?.team || null;
  const stats = data?.stats || {};
  const players = team?.players || [];
  const recentMatches = data?.recent_matches || [];
  const story = data?.story || "";
  const strengths = data?.strengths || [];
  const weaknesses = data?.weaknesses || [];
  const teamElo = team?.combined_elo ?? null;
  const pairCombo = data?.combo || null;

  const teamSeo = useMemo(() => {
    if (!team) return null;
    const title = `${team.name} | Tournament team profile | Age Of Empires 2 DE Bangladesh Community`;
    const playerNames = players.map((p) => p.name).filter(Boolean).join(" + ");
    const description = [
      playerNames,
      story,
      stats.matches != null ? `${stats.matches} replay matches analyzed` : null,
    ].filter(Boolean).join(" · ");
    return {
      title,
      description: description || `${team.name} on the AOE BD tournament page.`,
      keywords: `Age of Empires 2 team, AOE BD, ${team.name}, tournament scouting report`,
      canonical: publicUrl(`/tournaments/${encodeURIComponent(route.slug)}/teams/${encodeURIComponent(route.teamId)}`),
      image: publicUrl("/android-chrome-512x512.png"),
      applicationName: "AOE BD",
      author: "AOE BD Community",
      themeColor: "#e0a63a",
      aiSummary: story || `${team.name} team profile on AOE BD.`,
      aiTopics: "Age of Empires 2 team profile, tournament duo, replay stats, AOE BD",
      type: "article",
    };
  }, [team, story, stats.matches, route.slug, route.teamId, players]);

  useSeoMetadata(teamSeo);

  const metricCards = [
    teamElo != null ? <AnalyticsCard key="team-elo" label="Combined team Elo" value={formatCount(teamElo)} note="Tournament registration value" /> : null,
    stats.matches != null ? <AnalyticsCard key="matches" label="Replay matches analyzed" value={formatCount(stats.matches)} note="Latest team sample" /> : null,
    stats.wins != null ? <AnalyticsCard key="record" label="Sample record" value={`${stats.wins}/${stats.losses}/${stats.draws}`} note="W / L / D from replay sample" /> : null,
    stats.avg_eapm != null ? <AnalyticsCard key="avg-eapm" label="Average EAPM" value={formatMetricValue(stats.avg_eapm, 1)} note="Team average across the sample" /> : null,
    stats.peak_eapm != null ? <AnalyticsCard key="peak-eapm" label="Peak EAPM" value={formatMetricValue(stats.peak_eapm, 1)} note="Highest burst seen in a synced match" /> : null,
    stats.avg_eapm_early != null || stats.avg_eapm_mid != null || stats.avg_eapm_late != null ? <AnalyticsCard key="eapm-split" label="EAPM split" value={[stats.avg_eapm_early, stats.avg_eapm_mid, stats.avg_eapm_late].map((value) => (value != null ? formatMetricValue(value, 1) : "—")).join(" / ")} note="Early / mid / late" /> : null,
    stats.eapm_trend != null ? <AnalyticsCard key="trend" label="EAPM trend" value={formatSignedNumber(stats.eapm_trend, 2)} note="Positive means they ramp up later" /> : null,
    stats.avg_economy_score != null ? <AnalyticsCard key="eco-score" label="Avg economy score" value={formatCount(stats.avg_economy_score)} note="Economy output in the sample" /> : null,
    stats.avg_gather_rate_per_min != null ? <AnalyticsCard key="gather-rate" label="Gather rate / min" value={formatMetricValue(stats.avg_gather_rate_per_min, 1)} note="Resource intake efficiency" /> : null,
    stats.avg_eco_score_per_min != null ? <AnalyticsCard key="eco-rate" label="Eco score / min" value={formatMetricValue(stats.avg_eco_score_per_min, 1)} note="Cleaner macro usually shows here" /> : null,
    stats.avg_tc_idle_estimate_s != null ? <AnalyticsCard key="tc-idle" label="TC idle estimate" value={formatDuration(stats.avg_tc_idle_estimate_s)} note={stats.avg_tc_idle_ratio != null ? `Idle share ${formatRatio(stats.avg_tc_idle_ratio)}` : "Town Center uptime"} /> : null,
    stats.avg_villager_efficiency != null ? <AnalyticsCard key="vil-eff" label="Villager efficiency" value={formatRatio(stats.avg_villager_efficiency)} note="How closely they keep villagers working" /> : null,
    stats.avg_resource_bank != null ? <AnalyticsCard key="bank" label="Avg resource bank" value={formatCount(stats.avg_resource_bank)} note="High values can mean floating resources" /> : null,
    stats.favorite_opening ? <AnalyticsCard key="opening" label="Favorite opening" value={stats.favorite_opening} note="Most repeated replay opening" /> : null,
    stats.favorite_civ ? <AnalyticsCard key="civ" label="Favorite civ" value={stats.favorite_civ} note="Most used civilization in the sample" /> : null,
    stats.favorite_map ? <AnalyticsCard key="map" label="Favorite map" value={stats.favorite_map} note="Where they seem most comfortable" /> : null,
  ].filter(Boolean);

  return (
    <PublicShell>
      <div className="profile-page team-profile-page">
        <button className="button secondary back-button" onClick={() => navigate(`/tournaments/${encodeURIComponent(route.slug)}`)}>← Back to tournament</button>
        {state.loading ? <div className="panel empty-panel">Loading team profile...</div> : null}
        {state.error ? <div className="panel empty-panel">{state.error}</div> : null}
        {team ? (
          <div className="profile-dashboard team-profile-dashboard">
            <section className="profile-hero team-hero">
              <div className="profile-hero-info">
                <div className="profile-hero-name">
                  <h1>{team.name}</h1>
                  <span className="profile-board-pill">Tournament duo</span>
                </div>
                <p className="profile-hero-copy">{story || "A replay-backed scouting snapshot for this registered duo."}</p>
                <div className="profile-hero-links">
                  {team.group ? <span className="profile-source-chip">Group {team.group}</span> : null}
                  {team.seed != null ? <span className="profile-source-chip">Seed {team.seed}</span> : null}
                  {pairCombo?.matches != null ? <span className="profile-source-chip">Duo history {pairCombo.matches} matches</span> : null}
                </div>
                <div className="profile-hero-stats">
                  <div><strong>{teamElo ?? "—"}</strong><span>combined Elo</span></div>
                  <div><strong>{stats.matches ?? 0}</strong><span>replay matches</span></div>
                  <div><strong>{stats.avg_eapm != null ? formatMetricValue(stats.avg_eapm, 1) : "—"}</strong><span>avg EAPM</span></div>
                  <div><strong>{stats.peak_eapm != null ? formatMetricValue(stats.peak_eapm, 1) : "—"}</strong><span>peak EAPM</span></div>
                </div>
              </div>
              <div className="profile-hero-rail">
                {players.map((player) => (
                  <div key={player.discord_user_id || player.profile_id || player.name} className="profile-rail-card">
                    <span>{player.name}</span>
                    <strong>{player.tournament_elo != null ? formatCount(player.tournament_elo) : "—"}</strong>
                    <small>
                      {player.server_avg_elo != null ? `Server avg Elo ${formatCount(player.server_avg_elo)}` : "No server Elo snapshot"}
                      {player.recent_1v1_games != null ? ` · ${formatCount(player.recent_1v1_games)} recent games` : ""}
                    </small>
                  </div>
                ))}
              </div>
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Replay-backed profile</h2>
                  <p>Based on the latest 50 synced matches we could find for this duo.</p>
                </div>
              </div>
              <div className="analytics-grid profile-metric-grid profile-metric-grid-wide">
                {metricCards}
              </div>
            </section>

            <section className="profile-section team-insight-grid">
              <div className="team-insight-panel">
                <div className="profile-section-head">
                  <div>
                    <h2>What they do well</h2>
                    <p>Strengths pulled from the replay sample.</p>
                  </div>
                </div>
                <ul className="insight-list">
                  {strengths.map((item, idx) => <li key={`strength-${idx}`}>{item}</li>)}
                </ul>
              </div>
              <div className="team-insight-panel">
                <div className="profile-section-head">
                  <div>
                    <h2>What to watch</h2>
                    <p>Gaps and habits that could be punished.</p>
                  </div>
                </div>
                <ul className="insight-list">
                  {weaknesses.map((item, idx) => <li key={`weakness-${idx}`}>{item}</li>)}
                </ul>
              </div>
            </section>

            {pairCombo ? (
              <section className="profile-section">
                <div className="profile-section-head">
                  <div>
                    <h2>Duo history</h2>
                    <p>How this exact pairing has performed together before.</p>
                  </div>
                </div>
                <div className="analytics-grid profile-metric-grid">
                  <AnalyticsCard label="Combo matches" value={formatCount(pairCombo.matches)} note="Same duo on the same side" />
                  <AnalyticsCard label="Combo wins" value={formatCount(pairCombo.wins)} note="Wins together" />
                  <AnalyticsCard label="Combo win rate" value={pairCombo.win_rate != null ? formatWinRate(pairCombo.win_rate) : "—"} note="Historical synergy" />
                </div>
              </section>
            ) : null}

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Recent matches</h2>
                  <p>Latest replay files that informed this page.</p>
                </div>
              </div>
              <div className="team-match-list">
                {recentMatches.length ? recentMatches.map((match) => (
                  <div key={match.match_id} className="team-match-card">
                    <strong>{match.map_name || "Unknown map"}</strong>
                    <span>{formatAgo(match.played_at)} · {formatDuration(match.duration_s) || "Unknown duration"}</span>
                    <small>
                      {match.players?.map((player) => `${player.player_name || "Player"} ${player.avg_eapm != null ? `(${formatMetricValue(player.avg_eapm, 1)} EAPM)` : ""}`).join(" · ")}
                    </small>
                  </div>
                )) : <div className="empty-panel">No replay matches found yet for this duo.</div>}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </PublicShell>
  );
}

function PublicMatchPage({ route }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/public/matches/${encodeURIComponent(route.source)}/${encodeURIComponent(route.matchId)}`)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [route.source, route.matchId]);

  const match = state.data;
  const teams = useMemo(() => {
    const raw = match?.teams || {};
    return Object.entries(raw).map(([teamId, team]) => ({
      id: teamId,
      label: team?.name || team?.team_name || `Team ${teamId}`,
      avg_eapm: team?.avg_eapm ?? null,
      peak_eapm: team?.peak_eapm ?? null,
      players: team?.players || [],
    }));
  }, [match]);

  const players = match?.players || [];
  const story = match?.story || "";
  const winnerTeam = match?.winner != null ? String(match.winner) : null;
  const sourceLabel = match?.source === "aoe2c" ? "Replay match" : "Server match";
  const durationLabel = formatDuration(match?.duration_seconds);
  const playedLabel = formatAgo(match?.at);
  const matchTitle = match?.map_name ? `${match.map_name} match` : "Match detail";

  const matchSeo = useMemo(() => {
    if (!match) return null;
    const teamNames = (match.team_names || []).filter(Boolean).join(" vs ");
    const title = `${match.map_name || "Match"} | Age Of Empires 2 DE Bangladesh Community`;
    const description = [teamNames, durationLabel, story].filter(Boolean).join(" · ");
    return {
      title,
      description: description || `${match.map_name || "Match"} on the AOE BD public match page.`,
      keywords: `Age of Empires 2 match, AOE BD, AoE2 replay, ${match.map_name || "AoE2 match"}`,
      canonical: publicUrl(`/match/${encodeURIComponent(match.source)}/${encodeURIComponent(match.match_id)}`),
      image: publicUrl("/android-chrome-512x512.png"),
      applicationName: "AOE BD",
      author: "AOE BD Community",
      themeColor: "#e0a63a",
      aiSummary: story || `${match.map_name || "Match"} on AOE BD.`,
      aiTopics: "Age of Empires 2 match detail, replay analysis, community story, AOE BD",
      type: "article",
    };
  }, [match, story, durationLabel]);

  useSeoMetadata(matchSeo);

  const topPlayers = players
    .slice()
    .sort((a, b) => Number(b.avg_eapm || 0) - Number(a.avg_eapm || 0))
    .slice(0, 3);
  const totalEapm = players.filter((p) => p.avg_eapm != null).reduce((sum, p) => sum + Number(p.avg_eapm), 0);
  const avgEapm = players.filter((p) => p.avg_eapm != null).length ? Math.round(totalEapm / players.filter((p) => p.avg_eapm != null).length) : null;
  const openingCounts = players.reduce((acc, p) => {
    const key = p.opening_strategy || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const openingList = Object.entries(openingCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <PublicShell>
      <div className="profile-page match-page">
        <button className="button secondary back-button" onClick={() => navigate("/leaderboard")}>← Back to leaderboard</button>
        {state.loading ? <div className="panel empty-panel">Loading match...</div> : null}
        {state.error ? <div className="panel empty-panel">{state.error}</div> : null}
        {match ? (
          <div className="profile-dashboard">
            <section className="profile-hero match-hero">
              <div className="match-hero-badge">
                <span className="profile-board-pill">{sourceLabel}</span>
                <span className="link-badge linked">#{match.match_id}</span>
              </div>
              <div className="profile-hero-info">
                <div className="profile-hero-name">
                  <h1>{matchTitle}</h1>
                  <span className="link-badge unlinked">{match.source}</span>
                </div>
                <p className="profile-hero-copy">
                  {match.team_names?.filter(Boolean).join(" vs ") || "Teams from the match"}{playedLabel ? ` · ${playedLabel}` : ""}{durationLabel ? ` · ${durationLabel}` : ""}
                </p>
                <div className="profile-hero-stats">
                  <div><strong>{match.winner != null ? `Team ${match.winner}` : "—"}</strong><span>winner</span></div>
                  <div><strong>{players.length}</strong><span>players</span></div>
                  <div><strong>{avgEapm ?? "—"}</strong><span>avg EAPM</span></div>
                  <div><strong>{durationLabel || "—"}</strong><span>duration</span></div>
                </div>
                <div className="profile-hero-links">
                  {match.queue_name ? <span className="profile-source-chip">{match.queue_name}</span> : null}
                  {match.ranked ? <span className="profile-source-chip">Ranked</span> : <span className="profile-source-chip">Unranked</span>}
                  {match.map_name ? <span className="profile-source-chip">{match.map_name}</span> : null}
                </div>
              </div>
              <div className="profile-hero-rail">
                <div className="profile-rail-card">
                  <span>Match story</span>
                  <strong>{story ? "AI recap ready" : "No recap yet"}</strong>
                  <small>{story ? "Generated from the replay and player context." : "The recap could not be generated for this match."}</small>
                </div>
                <div className="profile-rail-card">
                  <span>Story focus</span>
                  <strong>{topPlayers[0]?.name || "—"}</strong>
                  <small>{topPlayers[0]?.avg_eapm != null ? `Peak activity: ${formatMetricValue(topPlayers[0].avg_eapm, 1)}` : "Highest average EAPM in the game."}</small>
                </div>
              </div>
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Story</h2>
                  <p>AI recap generated from the match context.</p>
                </div>
              </div>
              <div className="story-panel">
                <p>{story || "No story was generated for this match yet."}</p>
              </div>
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Match summary</h2>
                  <p>Small signals that shaped the game.</p>
                </div>
              </div>
              <div className="analytics-grid profile-metric-grid">
                <AnalyticsCard label="Winner" value={match.winner != null ? `Team ${match.winner}` : "—"} note="Match result" />
                <AnalyticsCard label="Teams" value={(match.team_names || []).filter(Boolean).join(" vs ") || "—"} note="Side names" />
                <AnalyticsCard label="Average EAPM" value={avgEapm != null ? formatMetricValue(avgEapm, 1) : "—"} note="Average across players" />
                <AnalyticsCard label="Players tracked" value={formatCount(players.length) || "0"} note="Players in this match" />
              </div>
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Teams</h2>
                  <p>Side-by-side team performance and composition.</p>
                </div>
              </div>
              <div className="match-team-grid">
                {teams.map((team) => (
                  <div key={team.id} className={`match-team-panel${winnerTeam === team.id ? " winner" : ""}`}>
                    <div className="match-team-head">
                      <strong>{team.label}</strong>
                      <span>{team.avg_eapm != null ? `Avg EAPM ${formatMetricValue(team.avg_eapm, 1)}` : "No EAPM data"}</span>
                    </div>
                    <div className="match-team-list">
                      {team.players.map((player) => (
                        <div key={`${team.id}-${player.user_id || player.player_number || player.name}`} className="match-player-row">
                          <div>
                            <strong>{player.name || "Unknown player"}</strong>
                            <small>{player.civ_name || player.civ || "Unknown civ"}</small>
                          </div>
                          <div className="match-player-metrics">
                            {player.avg_eapm != null ? <span>Avg {formatMetricValue(player.avg_eapm, 1)}</span> : null}
                            {player.peak_eapm != null ? <span>Peak {formatMetricValue(player.peak_eapm, 1)}</span> : null}
                            {player.tc_idle_estimate_s != null ? <span>Idle {formatDuration(player.tc_idle_estimate_s)}</span> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <div>
                  <h2>Player breakdown</h2>
                  <p>Sorting by activity helps surface the people who changed the tempo.</p>
                </div>
              </div>
              <div className="analytics-grid match-player-grid">
                {players.map((player) => (
                  <div key={`${player.team_id}-${player.player_number || player.name}`} className="analytics-card match-player-card">
                    <div className="analytics-card-label">{player.name || "Unknown"}</div>
                    <div className="analytics-card-value">{player.civ_name || player.civ || "Unknown civ"}</div>
                    <div className="analytics-card-note">
                      {player.avg_eapm != null ? `Avg EAPM ${formatMetricValue(player.avg_eapm, 1)}` : "No EAPM"}
                      {player.economy_score != null ? ` · Eco ${formatCount(player.economy_score)}` : ""}
                      {player.opening_strategy ? ` · ${decodeEscapedText(player.opening_strategy)}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {openingList.length ? (
              <section className="profile-section">
                <div className="profile-section-head">
                  <div>
                    <h2>Opening mix</h2>
                    <p>How the match started.</p>
                  </div>
                </div>
                <div className="analytics-grid profile-metric-grid">
                  {openingList.map(([label, count]) => (
                    <AnalyticsCard key={label} label={label} value={formatCount(count)} note="Players using this opening" />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </PublicShell>
  );
}

function PublicLeaderboardPage({ route }) {
  const board = route.query.get("board") || "team";
  const searchQuery = route.query.get("q") || "";
  const page = Number(route.query.get("page") || 1);
  const sort = route.query.get("sort") || getDefaultLeaderboardSort(board);
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    const sortParam = board === "teamcombos" ? `&sort=${sort}` : "";
    Api.get(`/api/public/leaderboard?board=${board}&page=${page}${sortParam}`)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [board, page, sort]);

  const setBoard = (b) => navigate(withQuery("/leaderboard", { board: b, sort: getDefaultLeaderboardSort(b) }), { replace: true });
  const setSearchQuery = (q) => navigate(withQuery("/leaderboard", { board, q, sort }), { replace: true });
  const setSort = (s) => navigate(withQuery("/leaderboard", { board, q: searchQuery, sort: s }), { replace: true });
  const setPage = (p) => navigate(withQuery("/leaderboard", { board, q: searchQuery, sort, page: p }), { replace: true });

  const sortOptions = useMemo(() => getLeaderboardSortOptions(board), [board]);
  const filteredRows = useMemo(() => {
    const rows = state.data?.rows || [];
    return rows.filter((row) => {
      if (!searchQuery) return true;
      const haystack = String(row.nick || row.member_names || "").toLowerCase();
      return haystack.includes(searchQuery.toLowerCase());
    });
  }, [searchQuery, state.data?.rows]);
  const rows = useMemo(() => sortLeaderboardRows(filteredRows, board, sort), [filteredRows, board, sort]);

  const BOARD_TABS = [
    { id: "team", label: "team" },
    { id: "1v1", label: "1v1" },
    { id: "predictionboard", label: "predictions" },
    { id: "teamcombos", label: "team combos" },
  ];

  return (
    <PublicShell>
      <div className="panel">
        <div className="panel-header">
          <div>
            <h1>Leaderboard</h1>
            <p>Live from the database</p>
          </div>
        </div>
        <div className="tabs">
          {BOARD_TABS.map((b) => (
            <button key={b.id} className={`tab ${board === b.id ? "active" : ""}`} onClick={() => setBoard(b.id)}>{b.label}</button>
          ))}
        </div>
        <div className="lb-toolbar">
          <input className="input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={board === "teamcombos" ? "Search player name" : "Search nickname"} />
          {sortOptions.length ? (
            <select className="input" value={sort} onChange={(e) => setSort(e.target.value)} style={{ flex: "none", width: "auto" }}>
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : null}
        </div>
        {state.loading ? <div className="empty-panel">Loading...</div> : null}
        {state.error ? <div className="empty-panel">{state.error}</div> : null}
        {state.data && !rows.length ? <div className="empty-panel">No data found.</div> : null}
        {state.data && rows.length && board === "predictionboard" ? (
          <table className="lb-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nickname</th>
                <th>Record</th>
                <th>Streak</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user_id} className={`rank-tier-${row.rank <= 3 ? row.rank : 0}`}>
                  <td>{row.rank}</td>
                  <td>{row.nick}</td>
                  <td>{row.win_rate}% ({row.wins}/{row.wins + row.losses})</td>
                  <td>{row.streak > 0 ? `+${row.streak}` : row.streak}</td>
                  <td>{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {state.data && rows.length && board === "teamcombos" ? (
          <table className="lb-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Team</th>
                <th>Matches</th>
                <th>W/L/D</th>
                <th>Win rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rank} className={`rank-tier-${row.rank <= 3 ? row.rank : 0}`}>
                  <td>{row.rank}</td>
                  <td>{row.member_names}</td>
                  <td>{row.matches}</td>
                  <td>{row.wins}/{row.losses}/{row.draws}</td>
                  <td>{row.win_rate != null ? <WinRateBar value={row.win_rate} /> : "\u2014"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {state.data && rows.length && board !== "predictionboard" && board !== "teamcombos" ? (
          <table className="lb-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nickname</th>
                <th>Rating</th>
                {board === "team" ? <th>Max server Elo</th> : null}
                {board === "team" ? <th>Avg server Elo</th> : null}
                <th>W/L/D</th>
                <th>Win rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const winRate = row.wins + row.losses > 0 ? row.wins / (row.wins + row.losses) : null;
                const maxServerElo = board === "team" ? row.highest_server_team_rating : null;
                const avgServerElo = board === "team" && row.rating != null && maxServerElo != null
                  ? (Number(row.rating) + Number(maxServerElo)) / 2
                  : null;
                return (
                  <tr
                    key={row.user_id}
                    className={`rank-tier-${row.rank <= 3 ? row.rank : 0}`}
                    onClick={() => navigate(withQuery(`/leaderboard/player/${row.user_id}`, { board }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(withQuery(`/leaderboard/player/${row.user_id}`, { board }));
                      }
                    }}
                    role="link"
                    tabIndex={0}
                    title={`Open ${row.nick}'s profile`}
                  >
                    <td>{row.rank}</td>
                    <td>{decodeEscapedText(row.nick) || row.nick}</td>
                    <td>{row.rank_str ? <span className="rank-badge">{decodeEscapedText(row.rank_str) || row.rank_str}</span> : null}{formatElo(row.rating)}</td>
                    {board === "team" ? <td>{formatElo(maxServerElo)}</td> : null}
                    {board === "team" ? <td>{formatElo(avgServerElo)}</td> : null}
                    <td>{row.wins}/{row.losses}/{row.draws}</td>
                    <td>{winRate != null ? <WinRateBar value={winRate} /> : "\u2014"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
        {state.data ? <Pagination page={state.data.page || 1} pages={state.data.pages || 1} onChange={setPage} /> : null}
      </div>
    </PublicShell>
  );
}

function PublicPlayerProfilePage({ route }) {
  const board = route.query.get("board") || "team";
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/public/players/${route.userId}?board=${board}`)
      .then((data) => {
        if (cancelled) return;
        const parsed = PlayerSchema.safeParse(data);
        if (!parsed.success) {
          throw new Error(`Player validation failed: ${parsed.error.issues.map((i) => `${i.path.join(".") || "<root>"} ${i.message}`).join("; ")}`);
        }
        setState({ loading: false, data: parsed.data, error: null });
      })
      .catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [route.userId, board]);

  const playerSeo = useMemo(() => {
    const player = state.data;
    if (!player) return null;
    const displayBoard = board === "1v1" ? "1v1" : "team";
    const currentRating = player.rating != null ? `${player.rating}` : "unknown rating";
    const highestTeam = player.analytics?.highest_team_rating != null ? `${player.analytics.highest_team_rating}` : null;
    const highest1v1 = player.analytics?.highest_1v1_rating != null ? `${player.analytics.highest_1v1_rating}` : null;
    const serverPeak = player.highest_server_team_rating != null ? `${player.highest_server_team_rating}` : null;
    const descriptionBits = [
      `${player.nick || "This player"} on the AOE BD ${displayBoard} leaderboard`,
      `current rating ${currentRating}`,
      highest1v1 ? `highest AoE2Companion 1v1 Elo ${highest1v1}` : null,
      highestTeam ? `highest AoE2Companion team Elo ${highestTeam}` : null,
      serverPeak ? `highest server team Elo ${serverPeak}` : null,
    ].filter(Boolean);
    const canonical = publicUrl(`/leaderboard/player/${route.userId}?board=${encodeURIComponent(board)}`);
    const title = `${player.nick || "Player"} - ${displayBoard === "1v1" ? "1v1" : "Team"} AoE2 profile | Age Of Empires 2 DE Bangladesh Community`;
    return {
      title,
      description: `${descriptionBits.join(" \u00B7 ")}.`,
      keywords: `Age of Empires 2 profile, AoE2 Bangladesh, AoE2 DE Bangladesh, ${player.nick || "player"}, ${displayBoard} leaderboard, AoE2Companion`,
      robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      type: "profile",
      canonical,
      image: player.discord_avatar_url || publicUrl("/android-chrome-512x512.png"),
      themeColor: "#e0a63a",
      applicationName: "AOE BD",
      author: "AOE BD Community",
      aiSummary: `AoE BD public ${displayBoard} profile for ${player.nick || "an AoE2 player"} with live rating, ladder peak, AoE2Companion analytics, and server record.`,
      aiTopics: `Age of Empires 2 Definitive Edition profile, Bangladesh AoE2 player, ${displayBoard} Elo, AoE2Companion, BDBot leaderboard`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        name: title,
        description: `${player.nick || "Player"}'s public AoE2 profile on AOE BD.`,
        url: canonical,
        mainEntity: {
          "@type": "Person",
          name: player.nick || "Unknown player",
          image: player.discord_avatar_url || publicUrl("/android-chrome-512x512.png"),
          description: `${player.nick || "Player"}'s public Age Of Empires 2 profile on AOE BD.`,
        },
      },
    };
  }, [board, route.userId, state.data]);

  useSeoMetadata(playerSeo);

  return (
    <PublicShell>
      <div className="profile-page">
        <button className="button secondary back-button" onClick={() => navigate(withQuery("/leaderboard", { board }))}>← Back to leaderboard</button>
        {state.loading ? <div className="panel empty-panel">Loading player...</div> : null}
        {state.error ? <div className="panel empty-panel">{state.error}</div> : null}
        {state.data ? <PlayerProfileSummary p={state.data} board={board} /> : null}
      </div>
    </PublicShell>
  );
}

function JoinLobbyPage({ route }) {
  const url = `aoe2de://0/${route.lobbyid}`;
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return (
    <PublicShell>
      <div className="panel empty-panel">
        <p>Opening Age of Empires II: DE...</p>
        <p>
          <a href={url}>Click here</a> if it didn't open automatically.
        </p>
      </div>
    </PublicShell>
  );
}

function MemberLoginScreen({ auth }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(() => {
    const stored = window.sessionStorage.getItem("pubobot-auth-error");
    if (stored) window.sessionStorage.removeItem("pubobot-auth-error");
    return stored || "";
  });
  const login = async () => {
    setBusy(true);
    setMessage("");
    try {
      await auth.signInWithDiscord({ redirectTo: `${window.location.origin}/auth/callback`, next: "/member" });
    } catch (error) {
      setMessage(error.message);
      setBusy(false);
    }
  };
  return (
    <div className="screen-state login member-login-screen">
      <div className="login-card member-login-card">
        <img className="member-login-mark" src="/android-chrome-192x192.png" alt="AOE BD" />
        <span className="landing-kicker">AOE BD member portal</span>
        <h1>Continue with Discord</h1>
        <p>Make tournament predictions, vote for lobby maps, check your points, and explore player profiles.</p>
        {auth.error ? <div className="auth-alert">{auth.error}</div> : null}
        {message ? <div className="auth-alert">{message}</div> : null}
        <button className="button discord-login-button" disabled={busy || !auth.ready} onClick={login}>{busy ? "Opening Discord..." : "Login with Discord"}</button>
        <a className="button ghost" href="/">Back to public site</a>
        <small>Only current AOE BD Discord members can enter.</small>
      </div>
    </div>
  );
}

function MemberShell({ auth, member, route, children }) {
  const item = (name, label, path) => <button className={`member-nav-item ${route.name === name ? "active" : ""}`} onClick={() => navigate(path)}>{label}</button>;
  return (
    <div className="member-shell">
      <header className="member-header">
        <div className="brand" onClick={() => navigate("/member")}>
          <img className="brand-mark" src="/android-chrome-192x192.png" alt="" />
          <div><div className="brand-title">AOE BD</div><div className="brand-subtitle">Member portal</div></div>
        </div>
        <nav className="member-nav">
          {item("member-home", "Home", "/member")}
          <button className={`member-nav-item ${route.name === "member-tournament" || route.name === "member-tournaments" ? "active" : ""}`} onClick={() => navigate("/member/tournaments")}>Tournaments</button>
          {item("member-predictions", "Predictions", "/member/predictions")}
          {item("member-map-vote", "Map Vote", "/member/map-vote")}
          {item("member-players", "Players", "/member/players")}
          {item("member-profile", "My Profile", "/member/profile")}
        </nav>
        <div className="member-account">
          {member?.avatar ? <img src={member.avatar} alt="" /> : null}
          <span>{member?.username || "Member"}</span>
          <button className="button secondary" onClick={auth.logout}>Logout</button>
        </div>
      </header>
      <main className="member-main">{children}</main>
    </div>
  );
}

function MemberHome({ data }) {
  const activeTournament = (data.tournaments || []).find((item) => item.status !== "completed") || data.tournaments?.[0];
  const queue = data.queues?.[0];
  return (
    <div className="member-stack">
      <section className="member-hero panel">
        <div><span className="landing-kicker">Welcome back</span><h1>{data.user.username}</h1><p>Your predictions, tournament data, player profile, and community map ballot in one place.</p></div>
        {data.user.avatar ? <img src={data.user.avatar} alt="" /> : null}
      </section>
      <div className="member-dashboard-grid">
        <button className="panel member-action-card" onClick={() => activeTournament && navigate(`/member/tournaments/${activeTournament.id}`)}><span>🏆</span><strong>{activeTournament?.name || "Tournaments"}</strong><small>{activeTournament ? `${activeTournament.status} · ${activeTournament.team_count || 0} teams` : "No active tournament"}</small></button>
        <button className="panel member-action-card" onClick={() => activeTournament && navigate(`/member/predictions/${activeTournament.id}`)}><span>🎯</span><strong>Tournament predictions</strong><small>Submit scores and stage outcomes</small></button>
        <button className="panel member-action-card" onClick={() => navigate(`/member/map-vote${queue ? `/${queue.id}` : ""}`)}><span>🗳️</span><strong>Monthly map vote</strong><small>{queue?.vote?.state || "No lobby queue"}</small></button>
        <button className="panel member-action-card" onClick={() => navigate("/member/profile")}><span>⚔️</span><strong>My player profile</strong><small>Ratings, matches, maps, and civilizations</small></button>
      </div>
      <section className="panel member-card"><div className="member-section-head"><div><h2>Published tournaments</h2><p>Groups, fixtures, standings, and knockout brackets.</p></div></div><div className="tournament-list">{(data.tournaments || []).map((t) => <button key={t.id} className="tournament-card" onClick={() => navigate(`/member/tournaments/${t.id}`)}><div><h2>{t.name}</h2><p>{t.team_count || 0} teams</p></div><TournamentStatusPill status={t.status} /></button>)}</div></section>
    </div>
  );
}

function MemberOutcomePrediction({ tournament, onSaved }) {
  const initialStage = tournament.status === "semifinal" ? "Semi" : tournament.status === "final" ? "Final" : "Group";
  const [stage, setStage] = useState(initialStage);
  const [teamId, setTeamId] = useState("");
  const [result, setResult] = useState("Champion");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const normalized = stage === "Semi" ? "semifinal" : stage.toLowerCase();
  const eligibleIds = new Set((tournament.matches || []).filter((m) => m.stage === normalized).flatMap((m) => [String(m.team1?.id || ""), String(m.team2?.id || "")]));
  const teams = normalized === "group" ? (tournament.teams || []) : (tournament.teams || []).filter((team) => eligibleIds.has(String(team.id)));
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setMessage("");
    try {
      const data = await Api.send(`/api/member/tournaments/${tournament.id}/outcome-prediction`, "PUT", { stage, team_id: Number(teamId), result });
      onSaved(data); setMessage("Prediction saved.");
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };
  return (
    <form className="panel member-card member-form" onSubmit={submit}>
      <div className="member-section-head"><div><h2>Stage outcome prediction</h2><p>Correct Champion or Runner Up selections earn 500 points.</p></div></div>
      <div className="member-form-grid">
        <label><span>Stage</span><select value={stage} onChange={(e) => { setStage(e.target.value); setTeamId(""); }}><option>Group</option><option>Semi</option><option>Final</option></select></label>
        <label><span>Team</span><select required value={teamId} onChange={(e) => setTeamId(e.target.value)}><option value="">Select team</option>{teams.map((team) => <option key={team.id} value={team.id}>{team.name}{team.group ? ` · Group ${team.group}` : ""}</option>)}</select></label>
        <label><span>Result</span><select value={result} onChange={(e) => setResult(e.target.value)}><option>Champion</option><option>Runner Up</option></select></label>
        <button className="button" disabled={busy}>{busy ? "Saving..." : "Save prediction"}</button>
      </div>
      {message ? <div className="auth-note">{message}</div> : null}
    </form>
  );
}

function MemberMatchPrediction({ tournamentId, match, prediction, state, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const locked = Boolean(prediction);
  const closed = !(state ? state.open : true);
  const closesAt = state?.closes_at;
  const pick = async (teamId) => {
    setBusy(true); setMessage("");
    try {
      const data = await Api.send(`/api/member/tournaments/${tournamentId}/matches/${match.id}/prediction`, "PUT", { team_id: teamId });
      onSaved(data);
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };
  return (
    <div className={`member-match-predict ${locked ? "locked" : ""}`}>
      <div className="matchup-head"><span>{match.stage === "group" ? `Group ${match.group}` : match.stage} · Match {match.id}</span><span>{locked ? "Locked" : closed ? "Closed" : "Open"}</span></div>
      <div className="member-winlose-row">
        <button className={`button ${prediction && String(prediction.team_id) === String(match.team1?.id) ? "" : "secondary"}`} disabled={locked || closed || busy} onClick={() => pick(match.team1.id)}>{match.team1?.name}</button>
        <button className={`button ${prediction && String(prediction.team_id) === String(match.team2?.id) ? "" : "secondary"}`} disabled={locked || closed || busy} onClick={() => pick(match.team2.id)}>{match.team2?.name}</button>
      </div>
      {prediction?.settled_at ? <p className="prediction-points">{prediction.outcome_correct ? "Correct" : "Incorrect"} · {prediction.points_awarded || 0} points</p> : null}
      {!locked && closesAt ? <small>Closes {new Date(closesAt * 1000).toLocaleTimeString()}</small> : null}
      {message ? <small>{message}</small> : null}
    </div>
  );
}

function MemberPredictionHistory({ tournament }) {
  return (
    <section className="panel member-card">
      <div className="member-section-head"><div><h2>My predictions</h2><p>Web and Discord submissions share this history.</p></div></div>
      <div className="member-result-list">
        {(tournament.my_outcome_predictions || []).map((row) => <div key={`${row.stage}-${row.prediction_scope}-${row.predicted_result}`}><strong>{row.team_name} · {row.predicted_result === "runner_up" ? "Runner Up" : "Champion"}</strong><span>{row.stage} · {row.settled_at ? `${row.correct ? "Correct" : "Incorrect"} · ${row.points_awarded || 0} pts` : "Pending"}</span></div>)}
        {(tournament.my_match_predictions || []).map((row) => <div key={`match-${row.match_id}`}><strong>{row.selected_team} to beat {row.opponent_team}</strong><span>{row.settled_at ? `${row.outcome_correct ? "Correct" : "Incorrect"} · ${row.points_awarded || 0} pts` : "Pending"}</span></div>)}
        {!(tournament.my_outcome_predictions || []).length && !(tournament.my_match_predictions || []).length ? <div className="empty-note">No predictions yet.</div> : null}
      </div>
    </section>
  );
}

function MemberTournamentWorkspace({ tournamentId, predictionsOnly = false }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get(`/api/member/tournaments/${encodeURIComponent(tournamentId)}`).then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); }).catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [tournamentId]);
  const t = state.data;
  if (state.loading) return <div className="panel empty-panel">Loading tournament...</div>;
  if (state.error) return <div className="auth-alert">{state.error}</div>;
  if (!t) return null;
  const matches = (t.matches || []).filter((match) => match.team1 && match.team2);
  const predictionState = t.match_prediction_state || {};
  const matchPredictions = new Map((t.my_match_predictions || []).map((row) => [String(row.match_id), row]));
  const predictableMatches = matches.filter((m) => m.status !== "reported" || matchPredictions.has(String(m.id)));
  return (
    <div className="member-stack">
      <section className="member-hero panel"><div><span className="landing-kicker">{predictionsOnly ? "Prediction center" : "Tournament"}</span><h1>{t.name}</h1><p>{t.team_count || t.teams?.length || 0} teams · {t.status}</p></div><TournamentStatusPill status={t.status} /></section>
      {!predictionsOnly ? <>
        <section className="panel member-card"><div className="member-section-head"><div><h2>Teams and groups</h2><p>Open a full scouting profile for any team.</p></div><a className="button secondary" href={`/tournaments/${encodeURIComponent(t.slug)}`}>Full pointboard & bracket</a></div><div className="tournament-team-grid">{(t.teams || []).map((team) => <TeamMiniCard key={team.id} team={team} onClick={() => navigate(`/tournaments/${encodeURIComponent(t.slug)}/teams/${team.id}`)} />)}</div></section>
        <section className="panel member-card"><div className="member-section-head"><div><h2>Fixtures and results</h2><p>All published group and knockout matches.</p></div></div><div className="matchup-grid">{matches.map((match) => <MatchupCard key={match.id} match={match} />)}</div></section>
      </> : null}
      <MemberOutcomePrediction tournament={t} onSaved={(data) => setState({ loading: false, data, error: null })} />
      <section className="panel member-card"><div className="member-section-head"><div><h2>Match win/lose predictions</h2><p>Correct picks earn 50 points. Open for any upcoming match until an admin starts the closing countdown.</p></div></div><div className="member-prediction-grid">{predictableMatches.map((match) => <MemberMatchPrediction key={match.id} tournamentId={t.id} match={match} prediction={matchPredictions.get(String(match.id))} state={predictionState[String(match.id)]} onSaved={(data) => setState({ loading: false, data, error: null })} />)}</div>{!predictableMatches.length ? <div className="empty-note">No matches available for prediction yet.</div> : null}</section>
      <MemberPredictionHistory tournament={t} />
      <section className="panel member-card"><div className="member-section-head"><div><h2>Tournament prediction board</h2><p>Outcome and match-prediction points combined.</p></div></div><div className="member-result-list">{(t.prediction_board || []).map((row) => <div key={row.user_id}><strong>#{row.rank} · {row.username}</strong><span>{row.points} points · {row.correct} correct</span></div>)}{!(t.prediction_board || []).length ? <div className="empty-note">No settled predictions yet.</div> : null}</div></section>
    </div>
  );
}

function MemberTournamentList({ tournaments, predictions = false }) {
  return <div className="member-stack"><div className="member-section-head"><div><h1>{predictions ? "Prediction center" : "Tournaments"}</h1><p>Select a tournament to continue.</p></div></div><div className="tournament-list">{(tournaments || []).map((t) => <button key={t.id} className="tournament-card" onClick={() => navigate(`/member/${predictions ? "predictions" : "tournaments"}/${t.id}`)}><div><h2>{t.name}</h2><p>{t.team_count || 0} teams</p></div><TournamentStatusPill status={t.status} /></button>)}</div></div>;
}

function MemberMapVote({ queues, route }) {
  const selectedQueue = queues.find((q) => String(q.id) === String(route.queueId)) || queues[0];
  const [period, setPeriod] = useState(selectedQueue?.vote || null);
  const [preferred, setPreferred] = useState(new Set(period?.ballot?.preferred || []));
  const [banned, setBanned] = useState(new Set(period?.ballot?.banned || []));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!selectedQueue) return;
    Api.get(`/api/member/map-votes/queues/${selectedQueue.id}`).then((data) => { setPeriod(data); setPreferred(new Set(data.ballot?.preferred || [])); setBanned(new Set(data.ballot?.banned || [])); }).catch((error) => setMessage(error.message));
  }, [selectedQueue?.id]);
  const toggle = (kind, name) => {
    const current = kind === "preferred" ? preferred : banned;
    const other = kind === "preferred" ? banned : preferred;
    const limit = kind === "preferred" ? 10 : 2;
    const next = new Set(current);
    if (next.has(name)) next.delete(name);
    else if (!other.has(name) && next.size < limit) next.add(name);
    kind === "preferred" ? setPreferred(next) : setBanned(next);
  };
  const submit = async () => {
    setBusy(true); setMessage("");
    try {
      const data = await Api.send(`/api/member/map-votes/${period.id}/ballot`, "PUT", { preferred: [...preferred], banned: [...banned] });
      setPeriod(data); setMessage("Your ballot is saved. You can edit it until voting closes.");
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };
  if (!selectedQueue) return <div className="panel empty-panel">No map-enabled lobby queue is available.</div>;
  return (
    <div className="member-stack">
      <section className="member-hero panel"><div><span className="landing-kicker">Monthly community ballot</span><h1>{selectedQueue.name} map vote</h1><p>Select exactly 10 preferred maps and ban exactly 2. Voting opens for the final ten days of each month.</p></div><span className={`member-state state-${period?.state}`}>{period?.state || "loading"}</span></section>
      {queues.length > 1 ? <div className="member-tabs">{queues.map((queue) => <button key={queue.id} className={queue.id === selectedQueue.id ? "active" : ""} onClick={() => navigate(`/member/map-vote/${queue.id}`)}>{queue.name}</button>)}</div> : null}
      {period ? <section className="panel member-card"><div className="member-vote-counts"><strong>{preferred.size}/10 preferred</strong><strong>{banned.size}/2 bans</strong><span>{period.turnout || 0} ballots</span></div><p>Opens {new Date(period.opens_at * 1000).toLocaleString()} · closes {new Date(period.closes_at * 1000).toLocaleString()}</p><div className="map-ballot-grid">{(period.candidates || []).map((name) => <div className={`map-ballot-item ${preferred.has(name) ? "preferred" : ""} ${banned.has(name) ? "banned" : ""}`} key={name}><strong>{name}</strong><div><button type="button" disabled={period.state !== "open"} onClick={() => toggle("preferred", name)}>✓ Prefer</button><button type="button" disabled={period.state !== "open"} onClick={() => toggle("ban", name)}>✕ Ban</button></div></div>)}</div><div className="member-actions"><button className="button" disabled={busy || period.state !== "open" || preferred.size !== 10 || banned.size !== 2} onClick={submit}>{busy ? "Saving..." : period.ballot?.submitted ? "Update ballot" : "Submit ballot"}</button></div>{message ? <div className="auth-note">{message}</div> : null}</section> : null}
      {period?.results ? <section className="panel member-card"><div className="member-section-head"><div><h2>Results</h2><p>Results become public after voting closes.</p></div></div><div className="member-result-list">{period.results.map((row) => <div key={row.map}><strong>{row.map}</strong><span>{row.preferred} votes · {row.bans} bans</span></div>)}</div></section> : null}
    </div>
  );
}

function MemberProfile({ user }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => { Api.get(`/api/public/players/${user.discord_user_id}?board=team`).then((data) => setState({ loading: false, data, error: null })).catch((error) => setState({ loading: false, data: null, error: error.message })); }, [user.discord_user_id]);
  const p = state.data;
  if (state.loading) return <div className="panel empty-panel">Loading your profile...</div>;
  if (state.error) return <div className="panel empty-panel"><h2>Your Discord account is not linked to a leaderboard player yet.</h2><p>{state.error}</p></div>;
  return <div className="member-stack"><section className="member-hero panel"><div><span className="landing-kicker">My profile</span><h1>{p.nick}</h1><p>{p.rank_str || "AOE BD player"}</p></div>{p.discord_avatar_url ? <img src={p.discord_avatar_url} alt="" /> : null}</section><div className="member-dashboard-grid"><div className="panel member-metric"><span>Current rating</span><strong>{formatCount(p.rating)}</strong></div><div className="panel member-metric"><span>Highest server Elo</span><strong>{formatCount(p.highest_server_team_rating)}</strong></div><div className="panel member-metric"><span>Wins</span><strong>{formatCount(p.wins)}</strong></div><div className="panel member-metric"><span>Matches</span><strong>{formatCount((p.wins || 0) + (p.losses || 0) + (p.draws || 0))}</strong></div></div><a className="button" href={`/leaderboard/player/${user.discord_user_id}?board=team`}>Open full player profile</a></div>;
}

function MemberPlayers() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => { Api.get("/api/public/leaderboard?board=team").then((data) => setState({ loading: false, data, error: null })).catch((error) => setState({ loading: false, data: null, error: error.message })); }, []);
  return <div className="member-stack"><div className="member-section-head"><div><h1>Players</h1><p>Browse AOE BD ratings and open detailed profiles.</p></div></div>{state.loading ? <div className="panel empty-panel">Loading players...</div> : null}{state.error ? <div className="auth-alert">{state.error}</div> : null}<div className="panel member-result-list">{(state.data?.rows || []).map((row) => <button key={row.user_id} onClick={() => navigate(`/leaderboard/player/${row.user_id}?board=team`)}><strong>#{row.rank} · {row.nick}</strong><span>{formatCount(row.rating)} Elo · {row.wins || 0}W/{row.losses || 0}L</span></button>)}</div></div>;
}

function MemberApp({ route }) {
  const auth = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    if (!auth.user) { setState({ loading: false, data: null, error: null }); return; }
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Api.get("/api/member/bootstrap").then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); }).catch((error) => { if (!cancelled) setState({ loading: false, data: null, error: error.message }); });
    return () => { cancelled = true; };
  }, [auth.user?.id]);
  if (auth.loading || state.loading) return <div className="screen-state login"><div className="login-card"><h1>Loading member portal...</h1></div></div>;
  if (!auth.user) return <MemberLoginScreen auth={auth} />;
  if (state.error) return <div className="screen-state login"><div className="login-card"><h1>Member access unavailable</h1><p>{state.error}</p><button className="button secondary" onClick={auth.logout}>Logout</button></div></div>;
  if (!state.data) return <div className="screen-state login"><div className="login-card"><h1>Loading member portal...</h1></div></div>;
  const data = state.data;
  const tournamentId = route.tournamentId || (data.tournaments || []).find((t) => t.status !== "completed")?.id || data.tournaments?.[0]?.id;
  let content;
  if (route.name === "member-tournaments") content = <MemberTournamentList tournaments={data.tournaments} />;
  else if (route.name === "member-tournament") content = tournamentId ? <MemberTournamentWorkspace tournamentId={tournamentId} /> : <MemberTournamentList tournaments={data.tournaments} />;
  else if (route.name === "member-predictions") content = tournamentId ? <MemberTournamentWorkspace tournamentId={tournamentId} predictionsOnly /> : <MemberTournamentList tournaments={data.tournaments} predictions />;
  else if (route.name === "member-map-vote") content = <MemberMapVote queues={data.queues || []} route={route} />;
  else if (route.name === "member-profile") content = <MemberProfile user={data.user} />;
  else if (route.name === "member-players") content = <MemberPlayers />;
  else content = <MemberHome data={data} />;
  return <MemberShell auth={auth} member={data.user} route={route}>{content}</MemberShell>;
}

function Root() {
  const route = useRoute();
  if (route.name === "landing") return <LandingPage />;
  if (route.name === "login") return <App />;
  if (route.name === "public-leaderboard") return <PublicLeaderboardPage route={route} />;
  if (route.name === "public-player") return <PublicPlayerProfilePage route={route} />;
  if (route.name === "public-match") return <PublicMatchPage route={route} />;
  if (route.name === "public-tournaments") return <PublicTournamentsPage />;
  if (route.name === "public-tournament") return <PublicTournamentPage route={route} />;
  if (route.name === "public-tournament-team") return <PublicTournamentTeamPage route={route} />;
  if (route.name === "join-lobby") return <JoinLobbyPage route={route} />;
  if (route.name === "auth-callback" || route.name.startsWith("member-")) return <MemberApp route={route} />;
  return <App />;
}

createRoot(document.getElementById("root")).render(
  <AppErrorBoundary>
    <Root />
  </AppErrorBoundary>,
);
